/**
 * Чат команды — Telegram-style supergroup с фиксированными топиками.
 *
 * Функционал:
 *   - Топики (sidebar) с unread badge, polling 10s
 *   - Сообщения (polling 5s) + optimistic-send
 *   - Reply на сообщение (preview бар над input + reply preview в bubble)
 *   - Edit/delete своих (hover toolbar; soft-delete на бэке)
 *   - Прикрепления: кнопка, drag-n-drop, paste из буфера, inline image preview
 *
 * Стили — chat.css (классы .chat-*).
 */
import {
  lazy, Suspense,
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
  type DragEvent, type FormEvent, type KeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";

import { Icon } from "@/components/prototype/Icon";
import { Avatar } from "@/components/prototype/Avatar";
import { Virtuoso, type VirtuosoHandle } from "react-virtuoso";
import {
  openContextMenu as vanillaOpenContextMenu,
  closeContextMenu as vanillaCloseContextMenu,
} from "@/components/chatVanillaMenu";
import { Spinner } from "@/components/Spinner";
import { KbSplash } from "@/components/KbSplash";
import { TopicMediaModal } from "@/components/TopicMediaModal";
import { PhotoLightbox, type LightboxImage } from "@/components/PhotoLightbox";
import { ProfileLightbox } from "@/components/ProfileLightbox";
import { ProfileSelfModal, applyDeptColors, applyRoleColors, loadDeptColorsLS, loadRoleColorsLS } from "@/components/ProfileSelfModal";
import { PinnedSidePanel } from "@/components/PinnedSidePanel";
import { ChatPinnedBanner } from "@/components/ChatPinnedBanner";
import { ForwardPickerModal, type ForwardPreviewInfo } from "@/components/ForwardPickerModal";
import { RichComposer, type ComposerHandle } from "@/components/RichComposer";
import { ChatSelectionBar } from "@/components/ChatSelectionBar";
import { useDrafts } from "@/hooks/useDrafts";
import {
  clearPendingSend,
  createPendingSendRecovery,
  readPendingSend,
  storePendingSend,
} from "@/chat/sendRecovery";
import { useScrollUpPaginator } from "@/hooks/useScrollUpPaginator";
import { useReactionBurst } from "@/hooks/useReactionBurst";
import { MobileProfileFab } from "@/components/MobileProfileFab";
import { VideoLightbox } from "@/components/VideoLightbox";
import { downloadChatAttachment } from "@/lib/downloadAttachment";
import { FilePreviewModal } from "@/components/FilePreviewModal";
import { ImageUploadModal } from "@/components/ImageUploadModal";
import { ChatEmojiPicker, prefetchEmojiPicker } from "@/components/ChatEmojiPicker";
import { VoicePlayerBar } from "@/components/VoicePlayerBar";
// REVERTED: lazy-load app-view'ов ломал загрузку сообщений/аватарок —
// вернул eager imports. Bundle чуть больше (~190KB), но всё работает.
import { DriveView } from "@/components/DriveView";
import { TasksView } from "@/components/TasksView";
import { DocsView } from "@/components/DocsView";
import { ReportsView } from "@/components/ReportsView";
import { FocalDealsView } from "@/components/FocalDealsView";
import { VisitsScheduleView } from "@/components/VisitsScheduleView";
import { VisitsFunnelView } from "@/components/VisitsFunnelView";
import { NewChatModal } from "@/components/NewChatModal";
import { TopicMembersModal } from "@/components/TopicMembersModal";
import { LottieFromUrl } from "@/components/LottieFromUrl";
// TopicIconPickerModal тянет CustomEmojiGrid → apple.json (~2МБ) — грузим лениво
// (модалка открывается редко), чтобы не раздувать стартовый бандл мессенджера.
const TopicIconPickerModal = lazy(() =>
  import("@/components/TopicIconPickerModal").then(m => ({ default: m.TopicIconPickerModal })));
import { MessengerRightPanel } from "@/components/MessengerRightPanel";
import { ProfileDrawer } from "@/components/ProfileDrawer";
import { MessengerContextMenu } from "@/components/MessengerContextMenu";
import { BotSlashMenu, filterCommands } from "@/components/BotSlashMenu";
import { SlashActionMenu, filterSlashActions, type SlashAction } from "@/components/SlashActionMenu";
import { NoteToProjectModal } from "@/components/NoteToProjectModal";
import { DriveAttachmentPicker } from "@/components/DriveAttachmentPicker";
import { EditorFilePicker } from "@/components/EditorFilePicker";
import { SaveToDriveModal } from "@/components/SaveToDriveModal";
import { TaskCreatorModal } from "@/components/TaskCreatorModal";
import { type DriveFile } from "@/api/drive";
import { BOT_SALARY_USER_ID } from "@/api/dashboard";
import { parseTopicIcon } from "@/data/animatedIcons";
import { FoldersBar } from "@/components/FoldersBar";
import { parseFolderIcon } from "@/data/folderIcons";
import { FolderRail } from "@/components/FolderRail";
import { Emoji, splitTextWithEmojis, prefetchEmojiSvgs } from "@/components/Emoji";
import { ChatMsg } from "@/components/ChatMsg";
import { optimizeBeforeUpload } from "@/lib/imageOptimize";
import {
  fetchChatTopics, fetchChatMessages, sendChatMessage, forwardChatMessages, markChatRead, userAvatarUrl, topicAvatarUrl, fetchAllTyping, ensureFavoritesTopic,
  fetchTopicStats,
  // fetchChatDrafts + saveChatDraft + deleteChatDraft переехали в useDrafts hook
  editChatMessage, deleteChatMessage, restoreChatMessage, uploadChatFile, uploadChatFileWithProgress, cloneChatAttachment, chatAttachmentUrl, chatVideoThumbnailUrl, chatAttachmentPreviewUrl,
  pinChatMessage, unpinChatMessage, fetchChatPinned, toggleChatReaction,
  fetchChatTyping, postChatTyping, fetchActiveUsers,
  fetchTopicMembers,
  searchChatMessages, createChatTopic, editChatTopic, deleteChatTopic, reorderChatTopics, reorderChatPins,
  leaveTopic, convertTopicKind, getOrCreateDm,
  pinTopicForMe, unpinTopicForMe, pinTopicForAll, unpinTopicForAll, markTopicUnread, findDm,
  fetchChatFolders, addTopicToFolder, removeTopicFromFolder, createChatFolder,
  REACTION_EMOJI,
  createScheduledMessage, fetchScheduledInTopic, cancelScheduledMessage,
  patchScheduledMessage, sendScheduledNow, type ScheduledMessage,
  type ChatMessage, type ChatTopic, type ChatAttachment, type ChatReplyPreview,
  type UserShort, type ChatSearchHit,
} from "@/api/dashboard";
import { ScheduleMessagePickerModal } from "@/components/ScheduleMessagePickerModal";
import { useAuth } from "@/auth/AuthContext";
import { useTopicOrgs } from "@/lib/topicOrgs";
import { APPS_CARDS_FROM, appCardOf } from "@/lib/appCards";
import { startChatStream, stopChatStream, setPinJumpActive, setActiveTopicForStream } from "@/lib/chatStream";
import twemoji from "twemoji";
import { fetchUserPublic } from "@/api/dashboard";
import { fmtTyping, parseBackendIso, isOnline } from "@/lib/presence";
import { useTaxonomy } from "@/lib/useTaxonomy";
import { useTheme } from "@/lib/useTheme";

const TOPIC_ICON: Record<string, string> = {
  general:     "users",
  reports:     "report",
  calc:        "money",
  schemes:     "target",
  invoices:    "save",
  tech:        "settings",
  content:     "tag",
  misc:        "drag",
  regulations: "lock",
  topic:       "managers",  // Выезды (slug "topic" в DB)
  visits:      "managers",  // на случай переименования
};

const topicIconName = (slug: string): string => TOPIC_ICON[slug] ?? "users";

// Кэшируем Intl-форматтеры на уровне модуля: toLocaleTimeString/DateString
// создают НОВЫЙ форматтер на каждый вызов, а fmtSidebarDate зовётся для каждой
// строки сайдбара на каждый ре-рендер ChatPage (в т.ч. на каждый keystroke).
const _fmtTimeHM = new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" });
const _fmtWeekday = new Intl.DateTimeFormat("ru-RU", { weekday: "short" });
const _fmtDayMonth = new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit" });
const _fmtDayMonthYear = new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit" });

// Спейсеры ленты Virtuoso вынесены на module-level. Инлайн
// components={{ Footer: () => …, Header: () => … }} создавал НОВЫЙ объект + две
// новые функции-компоненты на КАЖДЫЙ рендер ChatPage → Virtuoso каждый раз
// пересверял/ремаунтил их. Стабильная ссылка убирает лишнюю сверку.
const VirtuosoFeedFooter = () => <div style={{ height: 16 }} />;
const VirtuosoFeedHeader = () => <div style={{ height: 8 }} />;
const VIRTUOSO_FEED_COMPONENTS = { Footer: VirtuosoFeedFooter, Header: VirtuosoFeedHeader };
/** Страница истории при прыжке к далёкому сообщению (обычная прокрутка вверх — 200):
 *  12 запросов и 12 пересборок ленты на 2500 сообщений превращаются в 3. */
const JUMP_HISTORY_PAGE = 1000;

/** Краткий формат даты для sidebar (TG-style):
 *  сегодня → "12:34", вчера → "вчера", в пределах недели → "Сб"/"Пт"/"Чт",
 *  старше → "22.04" или "22.04.25" если другой год. */
function fmtSidebarDate(iso: string): string {
  const d = new Date(iso.endsWith("Z") ? iso : iso + "Z");
  const now = new Date();
  const ms = now.getTime() - d.getTime();
  const sameYMD = d.toDateString() === now.toDateString();
  if (sameYMD) return _fmtTimeHM.format(d);
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "вчера";
  if (ms < 7 * 24 * 60 * 60 * 1000) {
    return _fmtWeekday.format(d);  // Сб, Пт
  }
  if (d.getFullYear() === now.getFullYear()) {
    return _fmtDayMonth.format(d);
  }
  return _fmtDayMonthYear.format(d);
}

/** Черновик пересылки (TG-style, 18.09.2026): чат выбран, над строкой ввода плашка
 *  с тем, что пересылаем; отправка — кнопкой «Отправить». */
type ForwardDraft = {
  targetTopicId: number;
  messageIds: number[];
  attachmentIds: number[];
  info: ForwardPreviewInfo;
  /** Исходные сообщения из ленты-источника — для оптимистичной вставки. */
  sources: ChatMessage[];
};

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

/** Парсит @mentions и URL'ы в тексте сообщения.
 * @mention → <span class="chat-mention">@Иван_Иванов</span>
 * URL     → <a class="chat-link" target="_blank">https://…</a>
 * Unicode emoji → <Emoji native="❤️"/> (Apple-стиль через emoji-mart, как в TG)
 */
/**
 * Обернуть выделенный текст в textarea заданным markdown-маркером.
 * Используется mobile format-toolbar'ом над композером.
 * Toggle: повторное нажатие на уже обёрнутом — снимает обёртку.
 */
function wrapSelectionInTextarea(
  ta: ComposerHandle | null,
  marker: string,
  editingId: number | null,
  setText: (v: string) => void,
  setEditingText: (v: string) => void,
): void {
  if (!ta) return;
  const start = ta.selectionStart ?? 0;
  const end = ta.selectionEnd ?? 0;
  if (start === end) { ta.focus(); return; }   // без выделения — ничего (как TG)
  const value = ta.value;
  const before = value.slice(0, start);
  const sel = value.slice(start, end);
  const after = value.slice(end);
  let newValue: string;
  let newStart: number;
  let newEnd: number;
  if (before.endsWith(marker) && after.startsWith(marker)) {
    // выделено содержимое уже обёрнутого куска — снимаем обёртку
    newValue = before.slice(0, -marker.length) + sel + after.slice(marker.length);
    newStart = start - marker.length;
    newEnd = newStart + sel.length;
  } else if (sel.startsWith(marker) && sel.endsWith(marker) && sel.length >= 2 * marker.length) {
    const inner = sel.slice(marker.length, -marker.length);
    newValue = before + inner + after;
    newStart = start;
    newEnd = start + inner.length;
  } else {
    newValue = before + marker + sel + marker + after;
    newStart = start + marker.length;
    newEnd = newStart + sel.length;
  }
  if (editingId !== null) setEditingText(newValue);
  else setText(newValue);
  requestAnimationFrame(() => {
    ta.focus();
    try { ta.setSelectionRange(newStart, newEnd); } catch { /* ignore */ }
  });
}


/** ||Спойлер||: текст под плашкой, открывается по клику/тапу (как в TG). */
function Spoiler({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      className={"chat-spoiler" + (open ? " is-open" : "")}
      role={open ? undefined : "button"}
      title={open ? undefined : "Показать скрытый текст"}
      onClick={(e) => {
        if (open) return;
        e.stopPropagation();
        e.preventDefault();
        setOpen(true);
      }}
    >{children}</span>
  );
}

/** Класс цвета для бейджа воронки Amo ([pipe:Имя]). */
function pipeCls(name: string): string {
  const low = name.toLowerCase();
  if (low.includes("хранилищ")) return "chat-pipe--store";
  if (low.includes("отдел продаж")) return "chat-pipe--sales";
  return "chat-pipe--other";
}

function renderTextWithMentions(
  s: string,
  opts?: {
    /** map: slug в lowercase ("дмитрий_осипов") → user_id */
    mentionMap?: Record<string, string>;
    /** клик по найденному в map @-теге → открыть лайтбокс профиля */
    onMentionClick?: (userId: string) => void;
    /** клик по #хэштегу → подставить тег в sidebar searchQ (поиск по сообщениям) */
    onHashtagClick?: (tag: string) => void;
    /** служебное: глубина вложенного форматирования (**жирный _курсив_**) */
    _depth?: number;
  },
): ReactNode[] {
  // Объединённый regex: bold (**...** или *...*), icon-token, mention, URL.
  // Bold-варианты ПЕРВЫМИ — иначе одиночная звёздочка может попасть внутрь
  // URL/mention. Non-greedy + запрет переноса строки (`[^*\n]+?`) — чтобы
  // звёздочки в разных абзацах не "склеивались" в один bold-блок.
  // Форматирование (30.09.2026): **жирный**, __подчёркнутый__, ~~зачёркнутый~~,
  // ||спойлер||, `код`, *жирный* (бот зарплаты), _курсив_ — двойные маркеры раньше
  // одинарных, чтобы «__» не разбирался как два «_».
  const re = /\*\*([^*\n]+?)\*\*|__([^_\n]+?)__|~~([^~\n]+?)~~|\|\|([^|\n]+?)\|\||`([^`\n]+?)`|\*([^*\n]+?)\*|_([^_\n]+?)_|\[[^\]\n]+\]\((?:tel:\+?[\d ()-]+|tg:[^\s)]+|mailto:[^\s)]+|https?:\/\/[^\s)]+)\)|\[pipe:[^\]\n]+\]|\[ic:[a-z_]+\]|[@#][\wа-яёА-ЯЁ_-]{2,80}|https?:\/\/[^\s<>"']+/giu;
  const parts: ReactNode[] = [];
  let last = 0;
  let i = 0;
  // Хелпер: добавить чистый текстовый кусок, разрезав его по эмодзи (Apple-стиль)
  const pushText = (chunk: string) => {
    if (!chunk) return;
    for (const node of splitTextWithEmojis(chunk)) parts.push(node);
  };
  for (const m of s.matchAll(re)) {
    const start = m.index ?? 0;
    if (start > last) pushText(s.slice(last, start));
    const token = m[0];
    // Вложенное форматирование: внутренность маркера разбираем рекурсивно (до 3 уровней)
    const depth = opts?._depth ?? 0;
    const inner = (t: string): ReactNode[] =>
      depth < 3 ? renderTextWithMentions(t, { ...opts, _depth: depth + 1 }) : splitTextWithEmojis(t);
    if (token.startsWith("**")) {
      parts.push(<strong key={`b-${i++}`} className="chat-bold">{inner(token.slice(2, -2))}</strong>);
    } else if (token.startsWith("__")) {
      parts.push(<span key={`un-${i++}`} className="chat-underline">{inner(token.slice(2, -2))}</span>);
    } else if (token.startsWith("~~")) {
      parts.push(<span key={`st-${i++}`} className="chat-strike">{inner(token.slice(2, -2))}</span>);
    } else if (token.startsWith("||")) {
      parts.push(<Spoiler key={`sp-${i++}`}>{inner(token.slice(2, -2))}</Spoiler>);
    } else if (token.startsWith("`")) {
      parts.push(<code key={`cd-${i++}`} className="chat-code">{token.slice(1, -1)}</code>);
    } else if (token.startsWith("*") && token.endsWith("*") && token.length > 2) {
      // *text* — жирный (так пишет бот зарплаты); курсив — _text_
      parts.push(<strong key={`b-${i++}`} className="chat-bold">{inner(token.slice(1, -1))}</strong>);
    } else if (token.startsWith("_") && token.endsWith("_") && token.length > 2) {
      // _курсив_ — только как отдельное слово/фраза, не внутри snake_case_имени
      const prev = start > 0 ? s[start - 1] : "";
      const next = s[start + token.length] ?? "";
      const body = token.slice(1, -1);
      if ((/[\wа-яёА-ЯЁ]/.test(prev) && /[\wа-яёА-ЯЁ]/.test(next)) || /^\s|\s$/.test(body)) pushText(token);
      else parts.push(<em key={`it-${i++}`} className="chat-italic">{inner(body)}</em>);
    } else if (token.startsWith("[") && token.includes("](")) {
      // Markdown-ссылка [текст](url): кликабельный телефон tel:, Telegram и пр.
      const mm = /^\[([^\]]+)\]\((.+)\)$/.exec(token);
      if (mm) {
        const label = mm[1];
        const href = mm[2];
        parts.push(
          <a
            key={`ml-${i++}`}
            className="chat-link"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >{splitTextWithEmojis(label)}</a>
        );
      } else {
        pushText(token);
      }
    } else if (token.startsWith("[pipe:")) {
      // Бейдж воронки Amo: цветная пилюля с подкладкой
      const pname = token.slice(6, -1);
      parts.push(
        <span key={`p-${i++}`} className={"chat-pipe " + pipeCls(pname)}>{pname}</span>
      );
    } else if (token.startsWith("[ic:")) {
      // Системные иконки (рендерятся в backend-сообщениях вместо эмодзи)
      const name = token.slice(4, -1);
      parts.push(
        <span key={`i-${i++}`} className="chat-icon-inline">
          <Icon name={name} size={16}/>
        </span>
      );
    } else if (token.startsWith("@")) {
      const slugLc = token.slice(1).toLowerCase();
      const userId = opts?.mentionMap?.[slugLc];
      if (userId && opts?.onMentionClick) {
        parts.push(
          <button
            key={`m-${i++}`}
            type="button"
            className="chat-mention chat-mention-clickable"
            onClick={(e) => { e.stopPropagation(); opts.onMentionClick!(userId); }}
          >{token}</button>
        );
      } else {
        parts.push(<span key={`m-${i++}`} className="chat-mention">{token}</span>);
      }
    } else if (token.startsWith("#")) {
      // Хэштеги: синий inline-текст (CSS .chat-hashtag, без плашки/фона).
      // Клик подставляет тег в sidebar searchQ → поиск всех сообщений с тегом.
      if (opts?.onHashtagClick) {
        parts.push(
          <button
            key={`h-${i++}`}
            type="button"
            className="chat-hashtag"
            onClick={(e) => { e.stopPropagation(); opts.onHashtagClick!(token); }}
          >{token}</button>
        );
      } else {
        parts.push(<span key={`h-${i++}`} className="chat-hashtag">{token}</span>);
      }
    } else {
      // Trailing-знаки препинания не должны попадать в URL
      const stripped = token.replace(/[.,;:!?)]+$/, "");
      const tail = token.slice(stripped.length);
      parts.push(
        <a
          key={`u-${i++}`}
          className="chat-link"
          href={stripped}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >{stripped}</a>
      );
      if (tail) parts.push(tail);
    }
    last = start + token.length;
  }
  if (last < s.length) pushText(s.slice(last));
  return parts;
}

/** "сегодня" / "вчера" / "5 мая" / "5 мая 2025". */
function fmtDateSep(d: Date): string {
  const today = new Date(); today.setHours(0,0,0,0);
  const dd = new Date(d); dd.setHours(0,0,0,0);
  const diff = (today.getTime() - dd.getTime()) / 86400000;
  if (diff === 0) return "сегодня";
  if (diff === 1) return "вчера";
  const months = ["января","февраля","марта","апреля","мая","июня","июля","августа","сентября","октября","ноября","декабря"];
  const ds = `${dd.getDate()} ${months[dd.getMonth()]}`;
  return dd.getFullYear() !== today.getFullYear() ? `${ds} ${dd.getFullYear()}` : ds;
}

function isImage(mime: string | null | undefined, filename?: string | null): boolean {
  if (mime && mime.startsWith("image/")) return true;
  // Fallback по расширению — для старых аттачей где mime не сохранился
  // или сохранился как application/octet-stream.
  const f = (filename || "").toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp|avif|heic|heif|svg)$/.test(f);
}
function isVideo(mime: string | null | undefined, filename?: string | null): boolean {
  const m = (mime || "").toLowerCase();
  if (m.startsWith("video/")) return true;
  // Если mime явно audio — НЕ видео, даже при .webm (наши голосовые)
  if (m.startsWith("audio/")) return false;
  const f = (filename || "").toLowerCase();
  return /\.(mp4|mov|m4v|avi|mkv|webm)$/.test(f);
}

// === Lift utility (module-level — доступна и в global long-press, и в bubble) ===
let _liftedClone: HTMLElement | null = null;
let _liftedOriginal: HTMLElement | null = null;
let _liftObserver: MutationObserver | null = null;

function _removeLift() {
  try { if (_liftObserver) _liftObserver.disconnect(); } catch {}
  _liftObserver = null;
  try { if (_liftedClone && _liftedClone.parentNode) _liftedClone.remove(); } catch {}
  _liftedClone = null;
  try { if (_liftedOriginal) _liftedOriginal.style.visibility = ""; } catch {}
  _liftedOriginal = null;
  try { if (_liftBackdrop && _liftBackdrop.parentNode) _liftBackdrop.remove(); } catch {}
  _liftBackdrop = null;
}

let _liftBackdrop: HTMLElement | null = null;

function _liftEl(el: HTMLElement) {
  _removeLift();
  // 1) Реальный backdrop: clickable div с blur, при клике закрывает меню
  const bd = document.createElement("div");
  bd.id = "kb-lift-backdrop";
  bd.style.cssText = "position:fixed;inset:0;z-index:1399;backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);background:rgba(0,0,0,0.001);cursor:pointer";
  bd.addEventListener("click", (ev) => {
    ev.stopPropagation();
    // Диспатч click на body — обработчики меню (document.addEventListener("click", close))
    // увидят клик ВНЕ меню и сами вызовут setState(null) → React корректно размонтирует.
    setTimeout(() => {
      const clickEv = new MouseEvent("click", { bubbles: true, cancelable: true });
      document.body.dispatchEvent(clickEv);
      _removeLift();
    }, 0);
  });
  document.body.appendChild(bd);
  _liftBackdrop = bd;

  // 2) Клонировать element. Если это chat-msg — оборачиваем в .chat-redesign,
  //    чтобы все CSS-селекторы с .chat-redesign префиксом срабатывали (::after хвост и пр.)
  const r = el.getBoundingClientRect();
  const cs = getComputedStyle(el);
  const inner = el.cloneNode(true) as HTMLElement;
  inner.removeAttribute("id");
  inner.querySelectorAll("[id]").forEach(n => n.removeAttribute("id"));
  inner.style.visibility = "";
  // ВСЕГДА оборачиваем в .chat-redesign (внутри body[data-app-mode=chat]) —
  // иначе клон теряет все CSS-правила, scoped на `.chat-redesign .X` (включая
  // box-sizing, padding, flex layout у chat-topic). Без обёртки avatar наезжает
  // на текст и контейнер растягивается.
  const clone = document.createElement("div");
  clone.className = "chat-redesign";
  const origRedesign = el.closest(".chat-redesign") as HTMLElement | null;
  if (origRedesign) {
    const accent = origRedesign.getAttribute("data-accent");
    if (accent) clone.setAttribute("data-accent", accent);
    const bg = origRedesign.getAttribute("data-bg");
    if (bg) clone.setAttribute("data-bg", bg);
  }
  clone.appendChild(inner);
  // 5px gap от левого края экрана для chat-msg клонов (визуальный отступ)
  const leftPos = el.classList.contains("chat-msg") ? 5 : Math.max(0, r.left);
  const widthPx = el.classList.contains("chat-msg")
    ? (window.innerWidth - 10)
    : Math.min(r.width, window.innerWidth - Math.max(0, r.left));
  clone.style.cssText = `position:fixed;top:${r.top}px;left:${leftPos}px;width:${widthPx}px;height:${r.height}px;margin:0;z-index:1450;pointer-events:none;background:transparent;transition:transform 0.18s cubic-bezier(0.34, 1.4, 0.64, 1);transform:scale(1)`;
  // Тень + bg только если это не chat-msg (для chat-msg сами bubble имеют свой фон+тень)
  if (!el.classList.contains("chat-msg")) {
    // Apply background to INNER element (.chat-topic etc.) — wrapper is transparent.
    // Inner naturally has its own border-radius from CSS class.
    inner.style.background = cs.backgroundColor && cs.backgroundColor !== "rgba(0, 0, 0, 0)" ? cs.backgroundColor : "#ffffff";
    inner.style.boxShadow = "0 12px 32px rgba(0,0,0,0.18)";
    inner.style.borderRadius = cs.borderRadius || "10px";
  }
  document.body.appendChild(clone);
  // Двухпроходный подъём: первый проход — оценка по 40vh, второй — после
  // монтирования реального меню (RAF + 2 retries) меряем .msgr-ctx-menu /
  // .chat-context-menu / .folder-ctx-menu rect и подгоняем gap ровно 20px.
  const GAP = 20;
  const minTop = 56;
  const computeTranslateY = (menuTopPx: number): number => {
    let ty = 0;
    if (r.top + r.height > menuTopPx - GAP) {
      ty = -(r.top + r.height - (menuTopPx - GAP));
      if (r.top + ty < minTop) ty = minTop - r.top;
    }
    return ty;
  };
  // Первоначальная оценка — 40vh (на случай если меню тонет долго)
  let translateY = computeTranslateY(window.innerHeight * 0.4);
  requestAnimationFrame(() => {
    clone.style.transform = `translateY(${translateY}px) scale(1.03)`;
  });
  // После того как меню реально замонтируется — re-measure
  const remeasure = (attempt: number) => {
    const menuEl = document.querySelector(".msgr-ctx-menu, .chat-context-menu, .folder-ctx-menu") as HTMLElement | null;
    if (menuEl) {
      const mRect = menuEl.getBoundingClientRect();
      const adjustedTy = computeTranslateY(mRect.top);
      if (Math.abs(adjustedTy - translateY) > 1) {
        translateY = adjustedTy;
        clone.style.transform = `translateY(${translateY}px) scale(1.03)`;
      }
    } else if (attempt < 4) {
      requestAnimationFrame(() => remeasure(attempt + 1));
    }
  };
  requestAnimationFrame(() => remeasure(0));
  el.style.visibility = "hidden";
  _liftedClone = clone;
  _liftedOriginal = el;

  // 3) MutationObserver: если меню исчезли из DOM → removeLift
  _liftObserver = new MutationObserver(() => {
    const hasMenu = document.querySelector(".msgr-ctx-menu, .chat-context-menu, .folder-ctx-menu");
    if (!hasMenu) _removeLift();
  });
  _liftObserver.observe(document.body, { childList: true, subtree: true });
}


const ROLE_LABELS: Record<string, string> = {
  owner: "Собственник",
  admin: "Администратор",
  head_op: "Руководитель ОП",
  op_manager: "Менеджер ОП",
  kc_manager: "Менеджер КЦ",
  assistant: "Ассистент",
  field_engineer: "Выездной инженер",
  engineer: "Инженер",
  foreman: "Бригадир",
  builder: "Строитель",
};

function topicPartnerPosition(
  partner: { position?: string | null; role?: string | null; is_superuser?: boolean } | null | undefined,
  labels?: Record<string, string>,
): { label: string; roleKey?: string } | null {
  if (!partner) return null;
  if (partner.is_superuser) return { label: "Собственник", roleKey: "owner" };
  const r = partner.role;
  if (r) {
    const lbl = (labels && labels[r]) || ROLE_LABELS[r];
    if (lbl) return { label: lbl, roleKey: r };
  }
  if (partner.position && partner.position.trim()) return { label: partner.position.trim() };
  return null;
}


const DEPARTMENT_TOPIC_LABELS: Record<string, string> = {
  sales: "Отдел продаж",
  production: "Производство",
  marketing: "Маркетинг",
  admin: "Администрация",
  finance: "Финансы",
  supply: "Отдел снабжения",
};


export function ChatPage() {
  useEffect(() => {
    // build-marker — показываем всегда чтобы юзер видел реальный билд.
    // Тап/клик — сворачивает в точку 12px; повторный — разворачивает.
    // Состояние сохраняется в localStorage.
    const id = "kb-build-marker";
    let el = document.getElementById(id);
    if (!el) { el = document.createElement("div"); el.id = id; document.body.appendChild(el); }
    const label = "build 19:10:32";
    const baseStyle = "position:fixed;top:env(safe-area-inset-top,4px);right:4px;background:red;color:white;font:bold 11px monospace;border-radius:4px;z-index:99999;cursor:pointer;user-select:none;transition:padding 0.15s,width 0.15s,height 0.15s;";
    let collapsed = false;
    try { collapsed = localStorage.getItem("kb-build-marker-collapsed") === "1"; } catch { /* ignore */ }
    const render = () => {
      if (collapsed) {
        el!.textContent = "";
        el!.style.cssText = baseStyle + "width:12px;height:12px;padding:0;";
      } else {
        el!.textContent = label;
        el!.style.cssText = baseStyle + "padding:3px 7px;";
      }
    };
    const onClick = (e: Event) => {
      e.stopPropagation();
      collapsed = !collapsed;
      try { localStorage.setItem("kb-build-marker-collapsed", collapsed ? "1" : "0"); } catch { /* ignore */ }
      render();
    };
    el.addEventListener("click", onClick);
    render();
    return () => {
      el?.removeEventListener("click", onClick);
      el?.remove();
    };
  }, []);

  const { user } = useAuth();
  const topicOrgs = useTopicOrgs();   // организация чата — пометка в списке и шапке (04.10.2026)
  const qc = useQueryClient();
  const { effective: themeEffective } = useTheme();

  // SSE real-time stream: подписка на /api/chat/stream. patch'ит react-query
  // кэш (chat/messages, chat/typing, chat/topics) push'ом, без 700ms polling.
  // Polling выше оставлен как fallback на 30с — догоняет если SSE порвался.
  useEffect(() => {
    if (!user?.id) return;
    startChatStream(qc, user.id);
    return () => { stopChatStream(); };
  }, [qc, user?.id]);

  // Drafts logic (hydration + topic-switch подстановка + debounce save)
  // извлечена в useDrafts hook (P2#10d). Hook возвращает clearDraftForTopic
  // для cleanup из sendMut.onSuccess.

  // Query-params (для глубоких ссылок из toast'ов уведомлений: ?topic=N&jump=M)
  const search = useSearch({ strict: false }) as { topic?: number | string; jump?: number | string; folder?: number | string };
  const [activeTopicId, setActiveTopicId] = useState<number | null>(null);
  // Зеркало activeTopicId в ref — jumpToMessage/doOutOfRange читают СВЕЖЕЕ
  // значение, а не stale-замыкание (иначе поиск в другой чат прыгает мимо).
  const activeTopicIdRef = useRef<number | null>(null);
  activeTopicIdRef.current = activeTopicId;

  // Сообщаем chatStream'у текущий активный topicId — нужно для гарда в
  // applyRead/scheduleReadInvalidate: события для НЕ активного топика
  // помечают cache stale, но не вызывают немедленный refetch (видим
  // эффект только при возврате в эту переписку).
  // ОБЯЗАТЕЛЬНО ПОСЛЕ useState activeTopicId — иначе TDZ в проде после
  // минификации (var lookup до объявления).
  useEffect(() => {
    setActiveTopicForStream(activeTopicId);
    return () => setActiveTopicForStream(null);
  }, [activeTopicId]);
  const isMessengerRoute = typeof window !== "undefined" && (window.location.pathname.includes("/messenger")
    || (window.location.pathname === "/" && /(^|\.)brixchat\.ru$|^chat\.brixcrm\.ru$/.test(window.location.hostname)));   // BrixChat — в корне
  const TG_AVATAR_GRADIENTS = [
    "linear-gradient(135deg, #4fae4e 0%, #7bc67e 100%)",
    "linear-gradient(135deg, #3390ec 0%, #61a8f0 100%)",
    "linear-gradient(135deg, #e99a12 0%, #f0b84d 100%)",
    "linear-gradient(135deg, #9c7ce5 0%, #b8a0ec 100%)",
    "linear-gradient(135deg, #00acc1 0%, #4dd0e1 100%)",
    "linear-gradient(135deg, #e85c5c 0%, #ee8585 100%)",
  ];
  const pickAvatarGradient = (key: string | number) => {
    const k = String(key);
    let h = 0;
    for (let i = 0; i < k.length; i++) h = (h * 31 + k.charCodeAt(i)) >>> 0;
    return TG_AVATAR_GRADIENTS[h % TG_AVATAR_GRADIENTS.length];
  };
  const [text, setText] = useState("");
  // Прогрев twemoji-SVG эмодзи, которые набирают в композере (16.09.2026):
  // большой одиночный эмодзи после отправки раньше ждал загрузку своей картинки.
  useEffect(() => {
    if (!text) return;
    const t = window.setTimeout(() => prefetchEmojiSvgs(text), 120);
    return () => window.clearTimeout(t);
  }, [text]);
  /** Черновик ответа. `attachment` — фронтовое поле для миниатюры в плашке
   *  над строкой ввода (фото / кадр видео / иконка файла); на бэкенд не
   *  уходит — сервер строит reply_to сам. */
  type ReplyDraft = ChatReplyPreview & { attachments?: Array<{ id: number; mime: string | null; filename: string; size_bytes?: number | null }> };
  const [replyTo, setReplyTo] = useState<ReplyDraft | null>(null);
  const [pendingFiles, setPendingFiles] = useState<ChatAttachment[]>([]);
  const [sendError, setSendError] = useState<string | null>(null);
  // Drafts state/effects управляется useDrafts hook'ом (P2#10d). Здесь только
  // controlled state composer'а (text/replyTo) — он живёт в parent.
  const { clearDraftForTopic, draftsByTopic } = useDrafts({
    activeTopicId,
    userId: user?.id,
    qc,
    text,
    replyTo,
    setText,
    setReplyTo,
  });
  // Загрузки в процессе — instant preview + progress (TG-стиль)
  type PendingUpload = {
    tempId: string;
    file: File;
    thumbUrl: string | null;     // objectURL для image/video, null для остального
    isImage: boolean;
    isVideo: boolean;
    loaded: number;
    total: number;
    abort: () => void;
  };
  const [uploadingFiles, setUploadingFiles] = useState<PendingUpload[]>([]);
  const [composerModalOpen, setComposerModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState("");
  const [isDropping, setIsDropping] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [readByModal, setReadByModal] = useState<Array<{ user_id: string; display_name: string; avatar_filename: string | null }> | null>(null);
  const [reactionPicker, setReactionPicker] = useState<{
    messageId: number; top: number; left: number;
  } | null>(null);
  const [composerPickerOpen, setComposerPickerOpen] = useState(false);
  // Selection mode (TG-style): юзер кликает «Выбрать» в контекст-меню → чат
  // переходит в multi-select режим с топ-баром FORWARD/DELETE/CANCEL.
  const [selectionMode, setSelectionMode] = useState(false);
  // selectedIds — целые сообщения (текст + все аттачи)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // selectedAttachmentIds — TG-style: индивидуальные фото/видео в multi-attach
  // сообщениях. Юзер кликает на конкретный attach в album'е → toggle.
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<Set<number>>(new Set());
  // Topic-picker для Forward — открывается из selection-bar при клике "Переслать"
  const [forwardPickerOpen, setForwardPickerOpen] = useState(false);
  // @-mention autocomplete: query — текст после @ до курсора, или null если не активен
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIdx, setMentionIdx] = useState(0);
  const mentionStartRef = useRef<number>(0);   // позиция символа @
  const mentionActiveRef = useRef<HTMLButtonElement | null>(null);  // активный пункт автокомплита для скролла во view
  // Voice messages: MediaRecorder (WebM/Opus on Chromium/FF, mp4/aac on Safari).
  // Tap mic → start; tap send → upload+send; tap trash → discard.
  const [recState, setRecState] = useState<"idle" | "recording" | "paused" | "uploading">("idle");
  const [recElapsed, setRecElapsed] = useState(0);   // секунды
  const recRef = useRef<{
    mr: MediaRecorder; chunks: Blob[]; stream: MediaStream;
    timer: ReturnType<typeof setInterval> | null; startedAt: number;
  } | null>(null);

  // Поиск по сообщениям
  const [searchQ, setSearchQ] = useState("");                        // input в сайдбаре (глобальный)
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  // Закрытие поиска — с анимацией: держим оверлей смонтированным на время
  // slide-down, только потом unmount (иначе он просто исчезает рывком).
  const [mobileSearchClosing, setMobileSearchClosing] = useState(false);
  const mobileSearchRef = useRef<HTMLDivElement | null>(null);
  const closeMobileSearch = useCallback(() => {
    setMobileSearchClosing(true);
    window.setTimeout(() => {
      setMobileSearchOpen(false);
      setMobileSearchClosing(false);
    }, 180);  // = длительность msrch-slide-down в messenger-mobile.css
  }, []);

  // Пока открыт мобильный поиск — глушим edge-swipe AppShell'а (он делает
  // history.back и увёл бы со страницы). Тот же приём, что kb-in-chat-messages.
  useEffect(() => {
    if (mobileSearchOpen) document.body.classList.add("kb-mobile-search-open");
    else document.body.classList.remove("kb-mobile-search-open");
    return () => { document.body.classList.remove("kb-mobile-search-open"); };
  }, [mobileSearchOpen]);

  // Свайп закрытия поиска — тем же жестом, что «назад» в мессенджере:
  // edge-swipe от левого края, оверлей едет за пальцем.
  // touchmove ОБЯЗАН быть non-passive + preventDefault (иначе iOS морозит
  // transform во время жеста), transform — через setProperty(...,"important")
  // (иначе CSS-animation/!important перебивает инлайн-стиль).
  useEffect(() => {
    if (!mobileSearchOpen || mobileSearchClosing) return;
    const el = mobileSearchRef.current;
    if (!el) return;
    let startX = 0, startY = 0, active = false, moved = false, curDx = 0;

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) { active = false; return; }
      const t = e.touches[0];
      if (t.clientX <= 28) { startX = t.clientX; startY = t.clientY; active = true; moved = false; curDx = 0; }
      else { active = false; }
    };
    const onMove = (e: TouchEvent) => {
      if (!active || !e.touches[0]) return;
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      if (!moved) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        if (Math.abs(dy) > Math.abs(dx)) { active = false; return; }  // вертикаль = скролл списка
        moved = true;
        el.style.setProperty("animation", "none", "important");  // enter-анимация бьёт inline transform
      }
      e.preventDefault();
      curDx = Math.max(0, dx);
      el.style.setProperty("transition", "none", "important");
      el.style.setProperty("transform", `translateX(${curDx}px)`, "important");
    };
    const finish = (commit: boolean) => {
      const w = el.clientWidth || window.innerWidth;
      el.style.setProperty("transition", "transform 0.2s cubic-bezier(0.32, 0.72, 0, 1)", "important");
      el.style.setProperty("transform", commit ? `translateX(${w}px)` : "translateX(0)", "important");
      window.setTimeout(() => {
        if (commit) {
          setMobileSearchOpen(false);  // уже уехал за экран — доп. анимация не нужна
        } else {
          el.style.removeProperty("transition");
          el.style.removeProperty("transform");
          el.style.removeProperty("animation");
        }
      }, commit ? 200 : 220);
    };
    const onEnd = () => {
      if (!active) return;
      active = false;
      if (!moved) return;
      const w = el.clientWidth || window.innerWidth;
      finish(curDx > Math.min(120, w * 0.32));  // порог как у свайпа чата
    };
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onEnd, { passive: true });
    window.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onEnd);
    };
  }, [mobileSearchOpen, mobileSearchClosing]);
  const [topicSearchQ, setTopicSearchQ] = useState("");              // input в шапке (текущий топик)
  const [pipeFilter, setPipeFilter] = useState<string | null>(null); // фильтр по воронке Amo ([pipe:])
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const [profileDrawerStack, setProfileDrawerStack] = useState<ChatTopic[]>([]);
  const [messengerCtxMenu, setMessengerCtxMenu] = useState<{ messageId: number; x: number; y: number; attachmentId?: number; quote?: string } | null>(null);
  // Latest non-empty selection — обновляется на selectionchange event.
  // + на pointerdown/touchstart в capture-фазе (до того как браузер очистит
  // выделение при клике мимо). Двойной подход — потому что:
  //   1. selectionchange даёт обновление сразу при выделении
  //   2. pointerdown gвязывает selection с моментом ПЕРЕД right-click/long-press
  //      → openContextMenu гарантированно увидит свежее значение в ref
  const lastSelectionTextRef = useRef<string>("");
  useEffect(() => {
    const capture = () => {
      try {
        const s = window.getSelection();
        if (!s || s.isCollapsed || s.rangeCount === 0) return;
        const t = s.toString().trim();
        if (t) lastSelectionTextRef.current = t;
      } catch { /* ignore */ }
    };
    // selectionchange: слышим любые изменения выделения
    document.addEventListener("selectionchange", capture);
    // pointerdown/mousedown/touchstart в CAPTURE-фазе — перед bubble/click
    // очищает selection. Ловим текущее выделение ДО этого.
    document.addEventListener("pointerdown", capture, true);
    document.addEventListener("mousedown", capture, true);
    document.addEventListener("touchstart", capture, true);
    return () => {
      document.removeEventListener("selectionchange", capture);
      document.removeEventListener("pointerdown", capture, true);
      document.removeEventListener("mousedown", capture, true);
      document.removeEventListener("touchstart", capture, true);
    };
  }, []);
  // Tick раз в минуту — обновляет "был в сети N минут назад" без новых данных
  const [minuteTick, setMinuteTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setMinuteTick(v => v + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);
  const profileDrawerTopic = profileDrawerStack.length > 0 ? profileDrawerStack[profileDrawerStack.length - 1] : null;
  const [selfProfileOpen, setSelfProfileOpen] = useState(false);
  const [pinnedListOpen, setPinnedListOpen] = useState(false);
  const [forwardPicker, setForwardPicker] = useState<{ messageIds: number[]; sourceTopicId?: number } | null>(null);
  // Пересылка как в TG (18.09.2026). Состояние объявлено здесь — раньше submit()
  // и sendMut; сама логика (runForward) ниже по файлу, после messagesRef, и
  // подключается через runForwardRef — так нет обращения к const до объявления.
  const [forwardDraft, setForwardDraft] = useState<ForwardDraft | null>(null);
  const pendingForwardRef = useRef<ForwardDraft | null>(null);
  const runForwardRef = useRef<(f: ForwardDraft) => void>(() => { /* назначается ниже */ });
  useEffect(() => {
    const onOpen = () => setSelfProfileOpen(true);
    document.addEventListener("kb-open-self-profile", onOpen);
    return () => document.removeEventListener("kb-open-self-profile", onOpen);
  }, []);

  const [mngAccent, setMngAccent] = useState<string>(() => {
    // BrixChat (brixchat.ru) — оформление brixcrm: акцент и фон закреплены, не выбираются (04.10.2026).
    if (/(^|\.)brixchat\.ru$|^chat\.brixcrm\.ru$/.test(window.location.hostname)) return "brix";
    try { return localStorage.getItem("mng-accent") || "green"; } catch { return "green"; }
  });
  const [mngBg, setMngBg] = useState<string>(() => {
    if (/(^|\.)brixchat\.ru$|^chat\.brixcrm\.ru$/.test(window.location.hostname)) return "brix";
    try { return localStorage.getItem("mng-bg") || "green"; } catch { return "green"; }
  });
  useEffect(() => {
    if (!isMessengerRoute) return;
    const el = document.querySelector(".chat-redesign") as HTMLElement | null;
    if (el) {
      el.setAttribute("data-accent", mngAccent);
      el.setAttribute("data-bg", mngBg);
    }
    // Дублируем на :root — portal-модалки (ImageUploadModal и т.п.) рендерятся
    // в document.body ВНЕ .chat-redesign, и без этого var(--tg-accent) у них
    // не резолвится → фоллбэк на зелёный независимо от выбранного цвета.
    document.documentElement.setAttribute("data-accent", mngAccent);
    document.documentElement.setAttribute("data-bg", mngBg);
    try {
      localStorage.setItem("mng-accent", mngAccent);
      localStorage.setItem("mng-bg", mngBg);
    } catch { /* ignore */ }
  }, [isMessengerRoute, mngAccent, mngBg]);
  const leaveTopicMut = useMutation({
    mutationFn: (id: number) => leaveTopic(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat/topics"] });
      setActiveTopicId(null);
    },
    onError: (e: Error) => alert(`Не удалось покинуть: ${e.message}`),
  });
  const convertTopicMut = useMutation({
    mutationFn: (vars: { id: number; kind: "public" | "private_group"; member_ids?: string[] }) =>
      convertTopicKind(vars.id, { kind: vars.kind, member_ids: vars.member_ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat/topics"] }),
    onError: (e: Error) => alert(`Не удалось изменить тип: ${e.message}`),
  });

  // Right-click contextmenu over a topic в sidebar — pin/unread/delete/folders
  // Close other ctx menus when one opens
  useEffect(() => {
    const onCloseCtx = (e: Event) => {
      const ce = e as CustomEvent<{ except?: string }>;
      if (ce.detail?.except !== "topic") setTopicCtxMenu(null);
      if (ce.detail?.except !== "bubble") setContextMenu(null);
    };
    document.addEventListener("kb-close-ctx", onCloseCtx as EventListener);
    return () => document.removeEventListener("kb-close-ctx", onCloseCtx as EventListener);
  }, []);
  const [topicCtxMenu, setTopicCtxMenu] = useState<{
    topicId: number; x: number; y: number;
  } | null>(null);
  // Activeated folder filter (null = «Все»)
  const [activeFolderId, setActiveFolderId] = useState<number | null>(null);
  // TG-style: при клике на kind='group' (Диск/Задачник) sidebar «уезжает»
  // в группу и показывает только её детей + back-arrow. Не персистится —
  // на reload возвращается на главный список.
  const [activeGroupId, setActiveGroupId] = useState<number | null>(null);
  // Сворачивание sidebar (только десктоп через CSS). Persisted в localStorage.
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem("mng-sidebar-collapsed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("mng-sidebar-collapsed", sidebarCollapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [sidebarCollapsed]);
  // Folder submenu inside topic ctx menu — separate state to nest
  const [folderSubmenuOpen, setFolderSubmenuOpen] = useState(false);
  const folderSubBtnRef = useRef<HTMLButtonElement | null>(null);
  const [folderSubPos, setFolderSubPos] = useState<{ top: number; left: number } | null>(null);

  const foldersQ = useQuery({
    queryKey: ["chat/folders"],
    queryFn: fetchChatFolders,
    staleTime: 60_000,
  });
  const addToFolderMut = useMutation({
    mutationFn: (vars: { folderId: number; topicId: number }) =>
      addTopicToFolder(vars.folderId, vars.topicId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat/folders"] }),
    onError: (e: Error) => alert(`Не удалось: ${e.message}`),
  });
  const removeFromFolderMut = useMutation({
    mutationFn: (vars: { folderId: number; topicId: number }) =>
      removeTopicFromFolder(vars.folderId, vars.topicId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat/folders"] }),
    onError: (e: Error) => alert(`Не удалось: ${e.message}`),
  });

  const pinTopicMut = useMutation({
    mutationFn: (vars: { id: number; pin: boolean }) =>
      vars.pin ? pinTopicForMe(vars.id) : unpinTopicForMe(vars.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat/topics"] }),
    onError: (e: Error) => alert(`Не удалось: ${e.message}`),
  });
  const pinTopicForAllMut = useMutation({
    mutationFn: (vars: { id: number; pin: boolean }) =>
      vars.pin ? pinTopicForAll(vars.id) : unpinTopicForAll(vars.id),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["chat/topics"] });
      alert(vars.pin ? "Чат закреплён у всех сотрудников" : "Чат откреплён у всех сотрудников");
    },
    onError: (e: Error) => alert(`Не удалось: ${e.message}`),
  });
  const markUnreadMut = useMutation({
    mutationFn: (id: number) => markTopicUnread(id),
    onSuccess: (_data, id) => {
      // Сбрасываем кеш «уже отметили как прочитанное» — иначе при повторном
      // открытии чата useEffect skip-ает mark-read, и unread не очищается.
      delete lastMarkedReadRef.current[id];
      qc.invalidateQueries({ queryKey: ["chat/topics"] });
    },
    onError: (e: Error) => alert(`Не удалось: ${e.message}`),
  });

  // Close topic ctx menu on outside click / Esc
  useEffect(() => {
    if (!topicCtxMenu) return;
    const close = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      // Не закрываем, если клик внутри ЛЮБОГО chat-context-menu (top-level или submenu).
      if (t && t.closest(".chat-context-menu")) return;
      setTopicCtxMenu(null);
      setFolderSubmenuOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        if (folderSubmenuOpen) setFolderSubmenuOpen(false);
        else setTopicCtxMenu(null);
      }
    };
    document.addEventListener("click", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [topicCtxMenu, folderSubmenuOpen]);
  const [topicSearchOpen, setTopicSearchOpen] = useState(false);
  // Меню действий с топиком (переименовать/удалить — только superuser)
  const [topicMenuOpen, setTopicMenuOpen] = useState(false);
  // Модалка медиа-просмотра топика
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  // Smooth topic switching: лента скрыта (opacity:0) с момента смены topic'а
  // до того как messages загрузились + Virtuoso смонтировался + scroll выполнен.
  // Юзер не видит дёрганий — только плавный fade-in уже готовой ленты на bottom.
  // Шторка на время прыжка к сообщению (jumpToMessage): прикрывает ленту,
  // пока идут подгрузка истории, телепорт и доводка; снимается, когда цель
  // встала по центру. Сторож 12 с — на случай, если прыжок оборвался.
  const [jumpCurtain, setJumpCurtain] = useState(false);
  const jumpCurtainWatchdogRef = useRef<number | null>(null);
  const curtainOn = useCallback(() => {
    setJumpCurtain(true);
    if (jumpCurtainWatchdogRef.current) window.clearTimeout(jumpCurtainWatchdogRef.current);
    jumpCurtainWatchdogRef.current = window.setTimeout(() => {
      jumpCurtainWatchdogRef.current = null;
      setJumpCurtain(false);
    }, 12000);
  }, []);
  const curtainOff = useCallback(() => {
    if (jumpCurtainWatchdogRef.current) {
      window.clearTimeout(jumpCurtainWatchdogRef.current);
      jumpCurtainWatchdogRef.current = null;
    }
    setJumpCurtain(false);
  }, []);
  // Lightbox для тапа по картинке в чат-бабле — содержит индекс кликнутого attachment_id
  const [chatLightboxAid, setChatLightboxAid] = useState<number | null>(null);
  // Guard: блокирует loadMoreOlder в первые 1.2с после смены топика.
  // Virtuoso при mount думает что юзер near-top → triggers startReached →
  // 3-5 раз fetch'ит "старые" сообщения → лента толкается вверх 3-5 раз.
  // Этот guard даёт Virtuoso стабилизировать scroll-position до paginator.
  const paginatorReadyAtRef = useRef<number>(0);
  const [pinnedIdx, setPinnedIdx] = useState(0);
  // Visible range из Virtuoso — для TG-стиля автообновления pinnedIdx по скроллу
  // Ref, не state (16.09.2026): диапазон Virtuoso сдвигается десятки раз за один
  // свайп, и каждый setState здесь был re-render всего ChatPage. Баннер закрепа
  // пересчитывается в recomputeShownPin и трогает state только при смене закрепа.
  const visibleStartIdxRef = useRef(0);
  // Лайтбокс быстрого превью профиля (открывается по клику на аватар/имя)
  const [profileLightboxId, setProfileLightboxId] = useState<string | null>(null);
  // Preview-модалка для файлов (PDF/DOCX/text/...)
  const [filePreview, setFilePreview] = useState<ChatAttachment | null>(null);
  const [jumpTargetId, setJumpTargetId] = useState<number | null>(null);
  const jumpHighlightTimerRef = useRef<number | null>(null);   // таймер гашения подсветки цели
  const [contextMenu, setContextMenu] = useState<{
    messageId: number;
    /** Если меню вызвано на конкретном attach (фото/видео в альбоме) —
     *  его id. Тогда в меню появляются «Сохранить как» / «Скопировать img». */
    attachmentId?: number;
    pillTop: number;
    pillLeft: number;
    menuTop: number;
    menuLeft: number;
    /** Сдвиг bubble вверх (только мобилка). 0 на десктопе. */
    liftOffset: number;
    /** Максимальная высота меню в px (для overflow-y: auto при tall content). */
    menuMaxHeight: number;
    /** Текст выделения захваченный в момент открытия меню (для «Цитировать»). */
    quote?: string;
  } | null>(null);
  // "Sticky" copy: сохраняется ещё ~300ms после setContextMenu(null), чтобы
  // меню могло проиграть exit-анимацию вместо мгновенного unmount. iOS PWA
  // создаёт GPU-слой для меню; React unmount → unmount слоя → 1-2с paint лаг.
  // Sticky-подход: DOM остаётся, видимостью управляет CSS [data-open].
  type CtxMenuData = NonNullable<typeof contextMenu>;
  const [stickyContext, setStickyContext] = useState<CtxMenuData | null>(null);
  // Время открытия меню (ms timestamp) — для подавления ложного click'а от
  // mouseup правой кнопки сразу после ПКМ. Иначе на ПК backdrop ловит click
  // от того же правого жеста, и меню моментально закрывается (юзер: «надо
  // зажимать кнопку, иначе сразу пропадает»).
  const ctxMenuOpenedAtRef = useRef<number>(0);
  /** Утилита для menu-item action: onPointerUp (срабатывает для touch+mouse+pen
   *  одной строкой, без гонки click vs touchend). На iOS PWA standalone click
   *  иногда не доходит из-за конфликта с long-press release — pointerup приходит
   *  раньше и стабильнее. См. feedback_touch_handler_race. */
  /** Утилита для menu-item action: фаерим через onClick + onPointerUp +
   *  onTouchEnd одновременно. iOS PWA в standalone-режиме иногда не транслирует
   *  touch → click, но один из этих ивентов точно сработает. Дедуп через
   *  ref-флаг — переключается на 600ms после первого фаера. */
  // Меню — ВНЕ React event system. Все pointerdown/click ловятся нативным
  // listener'ом ниже (через data-menu-action атрибуты). React 18 + createPortal
  // на iOS PWA задерживает доставку click-событий на 1-3с — нативный
  // addEventListener минует эту проблему.
  const menuActionsRef = useRef<Map<string, () => void>>(new Map());
  const menuActionFiredRef = useRef(false);

  // Helper для menu-item: возвращает props { "data-menu-action": id } и
  // регистрирует action в menuActionsRef. id генерируется автоматически.
  const menuActionAttr = (action: () => void, id: string) => {
    menuActionsRef.current.set(id, action);
    return { "data-menu-action": id };
  };
  void menuActionAttr;

  // Native delegated pointerdown listener — ловит тапы по любым menu items
  useEffect(() => {
    if (!stickyContext) return;
    const handler = (e: Event) => {
      const target = e.target as HTMLElement;
      const item = target.closest("[data-menu-action]") as HTMLElement | null;
      if (!item) return;
      const id = item.dataset.menuAction;
      if (!id) return;
      const action = menuActionsRef.current.get(id);
      if (!action) return;
      if (menuActionFiredRef.current) return;
      menuActionFiredRef.current = true;
      e.stopPropagation();
      e.preventDefault();
      // Меню закрываем мгновенно через DOM (не через React state — тот может задержаться)
      const overlay = document.querySelector(".chat-context-overlay") as HTMLElement | null;
      if (overlay) overlay.style.display = "none";
      setContextMenu(null);
      setTimeout(() => action(), 0);
      setTimeout(() => {
        menuActionFiredRef.current = false;
        if (overlay) overlay.style.display = "";
      }, 600);
    };
    document.addEventListener("pointerdown", handler, { capture: true });
    document.addEventListener("click", handler, { capture: true });
    return () => {
      document.removeEventListener("pointerdown", handler, { capture: true } as EventListenerOptions);
      document.removeEventListener("click", handler, { capture: true } as EventListenerOptions);
    };
  }, [stickyContext]);
  // На мобильном — single-column: либо список топиков, либо лента сообщений
  const [mobileView, setMobileView] = useState<"topics" | "messages">("topics");
  // Выход из чата через СВАЙП: сайдбар уже виден под чатом во время жеста,
  // поэтому при переключении на topics НЕ проигрываем kb-view-in-left (иначе
  // он «продёргивается в сторону» вторым прогоном). Флаг гасит анимацию на ~350мс.
  const [swipeExiting, setSwipeExiting] = useState(false);
  // Sticky-date scroll handler: показываем дату текущего видимого сверху сообщения.
  // НО прячем когда inline date-sep уже виден сверху (иначе две одинаковые pill'и).
  // После остановки скролла — прячем за 600ms.
  // Старый scroll handler удалён — sticky date теперь обновляется через
  // Virtuoso rangeChanged (cheap, без querySelectorAll forced layout).

  // Setting "показывать удалённые сообщения" — admin-only.
  // Слушаем CustomEvent от ProfileSelfModal'а чтобы не требовать reload.
  const [showDeleted, setShowDeleted] = useState<boolean>(() => {
    try { return localStorage.getItem("kb-show-deleted") !== "0"; }
    catch { return true; }
  });
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent).detail as { show?: boolean } | undefined;
      if (typeof detail?.show === "boolean") setShowDeleted(detail.show);
    };
    document.addEventListener("kb-show-deleted-changed", onChange);
    return () => document.removeEventListener("kb-show-deleted-changed", onChange);
  }, []);
  // Body-класс для скрытия AppShell topbar когда внутри конкретного топика на мобиле
  useEffect(() => {
    if (mobileView === "messages") document.body.classList.add("kb-in-chat-messages");
    else document.body.classList.remove("kb-in-chat-messages");
    return () => { document.body.classList.remove("kb-in-chat-messages"); };
  }, [mobileView]);
  const longPressTimer = useRef<number | null>(null);
  const longPressFiredRef = useRef(false);

  // Чуть больше аватарка на мобиле (36px вместо 28)
  const [isMobileVw, setIsMobileVw] = useState<boolean>(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = (e: MediaQueryListEvent) => setIsMobileVw(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const avatarSize = isMobileVw ? 36 : 28;

  // Прогрев PNG реакций с jsdelivr удалён (16.09.2026): реакции давно рисуются
  // twemoji-SVG с нашего origin (MessengerContextMenu → <Emoji eager/>), а
  // jsdelivr из РФ медленный — это были 8 лишних внешних запросов на старте.

  const lastMarkedReadRef = useRef<Record<number, number>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const feedRef = useRef<HTMLDivElement>(null);
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);
  // Taxonomy: динамические лейблы ролей + отделов из backend
  const taxonomyQ = useTaxonomy();
  // useMemo (не IIFE): стабильная ссылка нужна, чтобы sidebarRows-мемо не
  // пересчитывался на каждый рендер (иначе набор текста снова бы перерисовывал сайдбар).
  const taxRoleLabels = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = { ...ROLE_LABELS };
    for (const r of (taxonomyQ.data?.roles ?? [])) map[r.key] = r.label;
    return map;
  }, [taxonomyQ.data]);
  const taxDeptLabels = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = { ...DEPARTMENT_TOPIC_LABELS };
    for (const d of (taxonomyQ.data?.departments ?? [])) map[d.key] = d.label;
    return map;
  }, [taxonomyQ.data]);
  // При получении taxonomy с сервера — переинициализируем CSS-переменные цветов
  // отделов/ролей с серверным default_color как fallback. Это нужно чтобы на
  // мобильном устройстве (где нет per-device localStorage) применялись цвета
  // из админ-настроек, а не хардкод-default (где marketing=pink, и т.д.).
  useEffect(() => {
    if (!taxonomyQ.data) return;
    const deptFallback: Record<string, string> = {};
    for (const d of taxonomyQ.data.departments) {
      if (d.default_color) deptFallback[d.key] = d.default_color;
    }
    const roleFallback: Record<string, string> = {};
    for (const r of taxonomyQ.data.roles) {
      if (r.default_color) roleFallback[r.key] = r.default_color;
    }
    applyDeptColors(loadDeptColorsLS(), deptFallback);
    applyRoleColors(loadRoleColorsLS(), roleFallback);
  }, [taxonomyQ.data]);

  // Один раз на mount — загружаем серверные ui-prefs юзера (AppearanceTab
  // дебаунсенно туда пишет на каждое изменение). Если на сервере что-то есть
  // и оно свежее/отличается от localStorage — переписываем localStorage и
  // применяем CSS-vars. Это синхронизация между устройствами: владелец
  // настроил цвета на десктопе → телефон забирает их при открытии.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/profiles/me/ui-prefs", { credentials: "include" });
        if (!res.ok) return;
        const data = await res.json() as { prefs?: Record<string, unknown> };
        const p = data?.prefs || {};
        if (cancelled || !p || Object.keys(p).length === 0) return;
        const brixHost = /(^|\.)brixchat\.ru$|^chat\.brixcrm\.ru$/.test(window.location.hostname);   // BrixChat: акцент и фон свои, не из профиля
        if (typeof p.accent === "string" && !brixHost) {
          try { localStorage.setItem("mng-accent", p.accent); } catch { /* */ }
          document.documentElement.setAttribute("data-accent", p.accent);
        }
        if (typeof p.bg === "string" && !brixHost) {
          try { localStorage.setItem("mng-bg", p.bg); } catch { /* */ }
          document.documentElement.setAttribute("data-bg", p.bg);
        }
        if (p.deptColors && typeof p.deptColors === "object") {
          try { localStorage.setItem("mng-dept-colors", JSON.stringify(p.deptColors)); } catch { /* */ }
          applyDeptColors(p.deptColors as Record<string, string>);
        }
        if (p.roleColors && typeof p.roleColors === "object") {
          try { localStorage.setItem("mng-role-colors", JSON.stringify(p.roleColors)); } catch { /* */ }
          applyRoleColors(p.roleColors as Record<string, string>);
        }
      } catch { /* offline-tolerant */ }
    })();
    return () => { cancelled = true; };
  }, []);
  // === TG-style "Новые сообщения" divider ===
  // Snapshot ID первого непрочитанного на момент открытия topic. Сохраняется
  // ОДИН раз на topic-open: пока юзер сидит в чате, snapshot не пересчитывается
  // (даже если приходят новые сообщения — они уйдут "под" divider).
  // Snapshot last_read_id (не +1 — это значение использует юзер для поиска
  // первого непрочитанного среди реальных messages). null = всё прочитано.
  // undefined = snapshot ещё не сделан (auto-mark-read должен подождать).
  const [unreadSnapshotLastReadId, setUnreadSnapshotLastReadId] = useState<number | null | undefined>(undefined);
  // Какой topic уже снэпшотнули (чтобы snapshot не пересоздавался при поллинге topicsQ).
  const snapshotTopicRef = useRef<number | null>(null);
  // Какой topic уже проскроллили к divider (чтобы scrollToIndex не повторялся).
  const scrolledToUnreadRef = useRef<number | null>(null);
  // Max msg-id per topic — нужно для cleanup при смене topic (closure
  // messagesQ.data может уже содержать данные НОВОГО topic, надо взять prev).
  const lastMaxMsgIdRef = useRef<Record<number, number>>({});
  // Force-follow window — Virtuoso auto-scroll'ит вниз 1.5s после отправки
  // СВОЕГО сообщения (followOutput=true). Чужие через polling — не дёргают.
  const [forceFollow] = useState(false);
  // Jump-to-message remount key + initial idx — для случая когда target
  // message вне загруженной ленты (pinned-banner click на старое сообщение,
  // reply-jump из глубокой истории). Меняется на каждый out-of-range jump,
  // триггерит Virtuoso remount с initialTopMostItemIndex = target.
  // Без этого подхода runtime mutation firstItemIndex дёргает чат при SSE-апдейтах.
  const [jumpRemountKey, setJumpRemountKey] = useState<string | null>(null);
  const jumpRemountTargetIdxRef = useRef<number | null>(null);

  // === Resizable sidebar (TG-style: drag right edge to widen) ===
  const SIDEBAR_MIN = 260;
  const SIDEBAR_MAX = 540;
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    try {
      const v = parseInt(localStorage.getItem("mng-sidebar-width") || "320", 10);
      if (Number.isFinite(v) && v >= SIDEBAR_MIN && v <= SIDEBAR_MAX) return v;
    } catch { /* ignore */ }
    return 320;
  });
  useEffect(() => {
    try { localStorage.setItem("mng-sidebar-width", String(sidebarWidth)); } catch { /* ignore */ }
  }, [sidebarWidth]);
  const sidebarResizeStartXRef = useRef<number>(0);
  const sidebarResizeStartWidthRef = useRef<number>(0);
  function onSidebarResizeDown(e: React.PointerEvent<HTMLDivElement>) {
    e.preventDefault();
    sidebarResizeStartXRef.current = e.clientX;
    sidebarResizeStartWidthRef.current = sidebarWidth;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }
  function onSidebarResizeMove(e: React.PointerEvent<HTMLDivElement>) {
    if (sidebarResizeStartWidthRef.current === 0) return;
    const dx = e.clientX - sidebarResizeStartXRef.current;
    const next = Math.max(SIDEBAR_MIN, Math.min(SIDEBAR_MAX, sidebarResizeStartWidthRef.current + dx));
    setSidebarWidth(next);
  }
  function onSidebarResizeUp(e: React.PointerEvent<HTMLDivElement>) {
    sidebarResizeStartWidthRef.current = 0;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }


  // === Preserve scroll-from-bottom при изменении ширины chat-feed-wrap ===
  // Когда side-panel открывается/закрывается, grid меняет ширину чата →
  // сообщения переносятся → высоты меняются → scrollTop становится "не туда".
  // ResizeObserver ловит КАЖДОЕ изменение ширины и СИНХРОННО (до paint'а)
  // корректирует scrollTop, чтобы расстояние от низа осталось то же. Без
  // jitter'а — браузер делает один атомарный layout+paint без промежуточных.
  const lastFeedWidthRef = useRef(0);
  const lastScrollFromBottomRef = useRef(0);
  useEffect(() => {
    const scroller = feedRef.current;
    if (!scroller || typeof ResizeObserver === "undefined") return;
    // Запоминаем bottomGap при каждом scroll событии
    const onScroll = () => {
      lastScrollFromBottomRef.current = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // initial
    const NEAR_BOTTOM_PX = 24;
    const ro = new ResizeObserver(() => {
      const w = scroller.clientWidth;
      if (w === lastFeedWidthRef.current) return;
      lastFeedWidthRef.current = w;
      const isNearBottom = lastScrollFromBottomRef.current < NEAR_BOTTOM_PX;
      // Только ОДНА синхронная коррекция — без многокадровых фиксапов
      // (они вызывали видимое "продёргивание" при открытии/закрытии grid'а).
      // Если был у дна — snap к низу. Если выше — оставляем как есть, новый
      // layout сам разместит контент (Virtuoso удержит anchor item).
      if (isNearBottom) {
        scroller.scrollTop = scroller.scrollHeight;
      }
    });
    ro.observe(scroller);
    lastFeedWidthRef.current = scroller.clientWidth;
    return () => {
      ro.disconnect();
      scroller.removeEventListener("scroll", onScroll);
    };
  }, []);

  // Sticky date pill сверху feed'а — показывает дату текущего видимого сообщения
  // Pill даты управляется ИМПЕРАТИВНО (без React state) — каждое setState на iOS
  // вызывает rerender ChatPage и даёт jitter при скролле.
  const stickyPillRef = useRef<HTMLDivElement | null>(null);
  // Sticky-pill живёт в document.body через createPortal — это escapes
  // chat-shell grid-layout (sidebar 76+320=396px слева). С position:fixed +
  // динамическим top/left он центрируется по ширине chat-feed-wrap (то же
  // визуальное место что и inline date-sep'ы внутри scroller'а). top вычисляем
  // от feed.top+8, left = feed.left + feed.width/2, transform:translateX(-50%).
  // Также пробрасываем --feed-shift через :root для inline date-sep'ов чтобы
  // они тоже смещались в визуальный центр окна сообщений.
  // Этот подход решает root cause: старые keyframes chat-sticky-fade-in
  // хардкодили `transform: translate(calc(-50% + var(--sticky-x-offset, 0px)))`
  // что переопределяло inline-style.transform (CSS animation спецификация:
  // running animation побеждает inline style).
  useEffect(() => {
    const update = (ev?: unknown) => {
      const scroller = feedRef.current;
      const pill = stickyPillRef.current;
      if (!scroller) return;
      // Скролл самой ленты её рамку не двигает: getBoundingClientRect здесь —
      // forced layout на каждое scroll-событие (слушатель capture на window).
      // Пересчёт нужен только скроллу окна и resize (16.09.2026).
      if (ev instanceof Event && ev.type === "scroll" && ev.target === scroller) return;
      const rect = scroller.getBoundingClientRect();
      // КРИТИЧНО: использовать clientWidth (без scrollbar), а НЕ rect.width.
      // Inline date-sep'ы внутри scroller'а центрируются по контент-зоне
      // (justify-content:center внутри virt-content, ширина = clientWidth,
      // без scrollbar). Если pill центрировать по rect.width (с scrollbar),
      // получаем offset ≈ scrollbarWidth/2 (~8px на Windows). См. memory:
      // feedback_sticky_pill_portal.
      const clientW = scroller.clientWidth;
      if (pill) {
        pill.style.left = (rect.left + clientW / 2) + "px";
        pill.style.top  = (rect.top + 8) + "px";
      }
    };
    update();
    let ro: ResizeObserver | null = null;
    if (feedRef.current) {
      ro = new ResizeObserver(update);
      ro.observe(feedRef.current);
    }
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    // Retry если Virtuoso ещё не смонтировался
    const retry1 = window.setTimeout(update, 50);
    const retry2 = window.setTimeout(update, 200);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      window.clearTimeout(retry1);
      window.clearTimeout(retry2);
    };
  }, [activeTopicId]);

  // Reset jumpRemountKey при смене активного топика: предыдущий out-of-range
  // jump относился к старому топику, новый стартует с обычного конца ленты.
  useEffect(() => {
    setJumpRemountKey(null);
    jumpRemountTargetIdxRef.current = null;
    curtainOff();
  }, [activeTopicId, curtainOff]);
  const stickyLastLabelRef = useRef<string | null>(null);
  const stickyHideTimerRef = useRef<number | null>(null);
  const stickyRafRef = useRef<number | null>(null);
  // Кеш rendered items (offset/size) для быстрого вычисления "первого видимого"
  // без forced layout (querySelectorAll/elementFromPoint).
  const renderedItemsRef = useRef<Array<{ index: number; offset: number; size: number; data: ChatMessage }>>([]);
  // TG-стиль scroll-to-bottom кнопка — показывается когда отскроллил вверх
  const [showScrollDown, setShowScrollDown] = useState(false);
  // Baseline messages.length при открытии чата. Pulse если current > baseline
  // (т.е. новые сообщения пришли пока юзер в чате). autoMarkRead обнуляет
  // unread_count мгновенно — поэтому используем length, она всегда растёт.
  const baselineMsgLenRef = useRef<{ topicId: number; len: number } | null>(null);
  const [pulseTick, setPulseTick] = useState(0);
  // Sync atBottom ref — обновляется сразу через atBottomStateChange.
  // Используется в pulse-circle condition чтобы не показывать badge когда юзер
  // at-bottom (новое сообщение уже visible — нет смысла highlight'ить).
  const atBottomRef = useRef<boolean>(true);
  const textareaRef = useRef<ComposerHandle>(null);
  // Активно ли выделение в textarea (для показа format-toolbar)
  const [hasTextareaSelection, setHasTextareaSelection] = useState(false);

  // Lazy-ensure «Избранное» один раз при mount юзера
  useEffect(() => {
    if (!user?.id) return;
    void ensureFavoritesTopic()
      .then((fav) => {
        // Перезапрашивать список только если «Избранного» в нём ещё нет (первый
        // вход юзера) — иначе на каждом старте шёл второй /api/chat/topics.
        const list = qc.getQueryData<ChatTopic[]>(["chat/topics"]);
        if (!list || !list.some(t => t.id === fav.id)) qc.invalidateQueries({ queryKey: ["chat/topics"] });
      })
      .catch(() => { /* ignore — на следующий load попробуем снова */ });
  }, [user?.id, qc]);

  const topicsQ = useQuery({
    queryKey: ["chat/topics"],
    queryFn: fetchChatTopics,
    // SSE event 'message' invalidate'ит topicsQ. Polling — fallback на 30с.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 5_000,
  });

  // ── Тихий старт (16.09.2026) ────────────────────────────────────────────
  // На телефоне холодный вход упирался в канал: спрайт эмодзи 4,4 МБ и прогрев
  // всех иконок паков ехали параллельно с главным JS и первой лентой (замер 4G:
  // список 4,8 с → 3,4 с без спрайта; лента 3,4 с → 2,2 с без иконок). Всё
  // необязательное ждёт bootSettled: список чатов пришёл и в сети нет активных
  // запросов (кап 8 с — на случай вечного фонового refetch).
  const [bootSettled, setBootSettled] = useState(false);
  useEffect(() => {
    if (bootSettled || !topicsQ.data) return;
    const started = Date.now();
    let timer = 0;
    const tick = () => {
      if (qc.isFetching() === 0 || Date.now() - started > 8000) { setBootSettled(true); return; }
      timer = window.setTimeout(tick, 500);
    };
    timer = window.setTimeout(tick, 300);
    return () => window.clearTimeout(timer);
  }, [bootSettled, topicsQ.data, qc]);

  // Список активных юзеров для @-mention autocomplete.
  // staleTime 30s + refetchOnWindowFocus — чтобы только что заведённые юзеры
  // (как «Анна Белова») сразу появлялись в автокомплите, без хард-рефреша.
  const activeUsersQ = useQuery({
    queryKey: ["users/active"],
    queryFn: fetchActiveUsers,
    enabled: bootSettled,   // нужен только для @-автокомплита — после тихого старта
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
  // Members текущего топика (для фильтрации @-mentions в private_group/dm).
  // Берём kind напрямую из topicsQ — activeTopic объявлен ниже, в TDZ нельзя.
  const _activeTopicKind = (topicsQ.data ?? []).find(t => t.id === activeTopicId)?.kind;

  // Прогрев после тихого старта. Спрайт эмодзи-пикера — СРАЗУ при bootSettled
  // (список виден, сеть свободна): без него пикер открывается пустым и
  // заполняется вразнобой, пока лист не приехал (владелец: «не дождёшься»).
  // Pack-иконки чатов (в списке владельца 71 × ~6 КБ = 444 КБ) — через 4 с и
  // только при свободной сети, чтобы не отбирать канал у первого открытого
  // чата. Видимые иконки списка грузятся сами через <img>, прогрев — для кэша.
  useEffect(() => {
    if (!bootSettled) return;
    prefetchEmojiPicker();
    const started = Date.now();
    let timer = 0;
    const warm = () => {
      if (qc.isFetching() > 0 && Date.now() - started < 30000) { timer = window.setTimeout(warm, 1500); return; }
      const packs = new Set<number>();
      for (const t of (qc.getQueryData<ChatTopic[]>(["chat/topics"]) ?? [])) {
        const ic = t.icon || "";
        if (ic.startsWith("pack:")) {
          const n = parseInt(ic.slice(5), 10);
          if (Number.isFinite(n) && n >= 1 && n <= 254) packs.add(n);
        }
      }
      packs.forEach(n => { const img = new Image(); img.src = `/icons/icon-${n}.webp`; });
    };
    timer = window.setTimeout(warm, 4000);
    return () => window.clearTimeout(timer);
  }, [bootSettled, qc]);
  const isPrivateChat = !!_activeTopicKind && _activeTopicKind !== "public";
  const topicMembersQ = useQuery({
    queryKey: ["chat/topic/members", activeTopicId],
    queryFn: () => fetchTopicMembers(activeTopicId!),
    enabled: !!activeTopicId && isPrivateChat,
    staleTime: 60_000,
  });

  // Глобальный поиск по сообщениям (в сайдбаре)
  const searchGlobalQ = useQuery({
    queryKey: ["chat/search", searchQ],
    queryFn: () => searchChatMessages(searchQ),
    enabled: searchQ.trim().length >= 2,
    staleTime: 5_000,
  });
  // Поиск в текущем топике (в шапке)
  const searchTopicQ = useQuery({
    queryKey: ["chat/search", topicSearchQ, activeTopicId],
    queryFn: () => searchChatMessages(topicSearchQ, activeTopicId ?? undefined),
    enabled: topicSearchOpen && !!activeTopicId && topicSearchQ.trim().length >= 2,
    staleTime: 5_000,
  });

  // CRUD топиков. Создание — все юзеры. Редактирование/удаление — superuser.
  const createTopicMut = useMutation({
    mutationFn: (name: string) => createChatTopic({ name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat/topics"] }),
    onError: (e: Error) => alert(`Не удалось создать: ${e.message}`),
  });
  void createTopicMut;
  const editTopicMut = useMutation({
    mutationFn: (vars: { id: number; name?: string; icon?: string | null }) =>
      editChatTopic(vars.id, { name: vars.name, icon: vars.icon }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat/topics"] }),
    onError: (e: Error) => alert(`Не удалось: ${e.message}`),
  });
  // Drag-and-drop порядка топиков (только superuser)
  const [draggingTopicId, setDraggingTopicId] = useState<number | null>(null);
  const reorderMut = useMutation({
    mutationFn: (items: { id: number; sort_order: number }[]) => reorderChatTopics(items),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat/topics"] }),
    onError: (e: Error) => alert(`Не удалось сохранить порядок: ${e.message}`),
  });
  const reorderPinsMut = useMutation({
    mutationFn: (topicIds: number[]) => reorderChatPins(topicIds),
    onError: (e: Error) => alert(`Не удалось сохранить порядок закреплённых: ${e.message}`),
  });
  // Drag ТОЛЬКО закреплённых (не приложений): меняем pin_order per-user.
  function handlePinDrop(targetId: number) {
    if (draggingTopicId === null || draggingTopicId === targetId) return;
    const all = topicsQ.data ?? [];
    const isApp = (k?: string | null) =>
      k === "group" || k === "docs" || k === "reports" || k === "focal" || k === "visits_schedule" || k === "visits_funnel";
    const src = all.find(t => t.id === draggingTopicId);
    const tgt = all.find(t => t.id === targetId);
    if (!src?.is_pinned || !tgt?.is_pinned || isApp(src.kind) || isApp(tgt.kind)) return;
    const pinned = all.filter(t => t.is_pinned && !isApp(t.kind))
      .sort((a, b) => ((a.pin_order ?? 0) - (b.pin_order ?? 0)));
    const fromIdx = pinned.findIndex(t => t.id === draggingTopicId);
    const toIdx = pinned.findIndex(t => t.id === targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = pinned.splice(fromIdx, 1);
    pinned.splice(toIdx, 0, moved);
    const orderedIds = pinned.map(t => t.id);
    qc.setQueryData<ChatTopic[]>(["chat/topics"], (prev) =>
      (prev ?? []).map(t => {
        const idx = orderedIds.indexOf(t.id);
        return idx >= 0 ? { ...t, pin_order: idx } : t;
      }));
    reorderPinsMut.mutate(orderedIds);
    setDraggingTopicId(null);
  }

  function handleTopicDrop(targetId: number) {
    if (draggingTopicId === null || draggingTopicId === targetId) return;
    const list = [...(topicsQ.data ?? [])];
    const fromIdx = list.findIndex(t => t.id === draggingTopicId);
    const toIdx = list.findIndex(t => t.id === targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, moved);
    // Перенумеруем sort_order шагом 10 — оставляет место для будущих вставок
    const items = list.map((t, i) => ({ id: t.id, sort_order: (i + 1) * 10 }));
    // Optimistic update — мгновенно меняем локальный кэш
    qc.setQueryData(["chat/topics"], list.map((t, i) => ({ ...t, sort_order: (i + 1) * 10 })));
    reorderMut.mutate(items);
    setDraggingTopicId(null);
  }
  void handleTopicDrop;

  const deleteTopicMut = useMutation({
    mutationFn: (id: number) => deleteChatTopic(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat/topics"] });
      setActiveTopicId(null);
      setTopicMenuOpen(false);
    },
    onError: (e: Error) => alert(`Не удалось удалить: ${e.message}`),
  });

  function gotoSearchHit(hit: ChatSearchHit) {
    setActiveTopicId(hit.topic_id);
    setSearchQ("");
    setTopicSearchQ("");
    setTopicSearchOpen(false);
    setMobileView("messages");
    // Сбрасываем перед установкой — иначе если jumpTargetId уже = hit.message_id
    // (тот же сообщение / тот же топик), useEffect не сработает заново
    setJumpTargetId(null);
    setTimeout(() => {
      // Подсветку ставит само прибытие (arrive → triggerHighlight) вместе с
      // проявлением шторки; ранняя подсветка здесь мигала под шторкой.
      jumpToMessage(hit.message_id, hit.topic_id);
    }, 100);
  }

  // Click-outside для меню топика (шестерёнка)
  useEffect(() => {
    if (!topicMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.closest(".chat-topic-menu") || target.closest("[data-act='topic-menu-toggle']")) return;
      setTopicMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [topicMenuOpen]);

  // Click-outside для глобального поиска (sidebar) — клик вне поля и
  // вне результатов закрывает поиск (очищает query).
  useEffect(() => {
    if (!searchQ) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // .chat-topic исключён: клик по найденному чату НЕ должен чистить поиск
      // на mousedown — иначе список перерисуется в полный ДО того как click
      // сработает, и клик попадёт в другой чат (баг «чат не открывается»).
      // Топик сам чистит поиск в onClick (после выбора правильного чата).
      // Мобильный оверлей ОБЯЗАН быть в исключениях: иначе тап по найденному
      // сообщению чистил searchQ на mousedown, список схлопывался ДО click,
      // кнопка исчезала из DOM и тап уходил в никуда — симптом «поиск на
      // телефоне не работает». Тот же баг, что уже чинили для .chat-topic.
      if (target.closest(".chat-side-search") || target.closest(".chat-search-section") || target.closest(".chat-topic")
        || target.closest(".mobile-search-overlay") || target.closest(".chat-side-search-mobile")) return;
      setSearchQ("");
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [searchQ]);

  // Click-outside для поиска по топику (шапка чата)
  useEffect(() => {
    if (!topicSearchOpen) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.closest(".chat-topic-search-bar")
        || target.closest(".chat-search-results.is-inline")
        || target.closest("[data-act='topic-search-toggle']")) return;
      setTopicSearchOpen(false);
      setTopicSearchQ("");
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [topicSearchOpen]);
  const mentionMatches: UserShort[] = useMemo(() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.toLowerCase();
    const all = activeUsersQ.data ?? [];
    // Для private_group/dm — фильтруем только members этого чата
    let pool: UserShort[] = all;
    if (isPrivateChat && topicMembersQ.data) {
      const memberIds = new Set(topicMembersQ.data.map(m => m.user_id));
      pool = all.filter(u => memberIds.has(u.id));
    }
    if (!q) return pool.slice(0, 12);
    const matched = pool.filter(u => (u.display_name ?? "").toLowerCase().includes(q));
    matched.sort((a, b) => {
      const aStarts = (a.display_name ?? "").toLowerCase().startsWith(q) ? 0 : 1;
      const bStarts = (b.display_name ?? "").toLowerCase().startsWith(q) ? 0 : 1;
      return aStarts - bStarts;
    });
    return matched.slice(0, 12);
  }, [mentionQuery, activeUsersQ.data, isPrivateChat, topicMembersQ.data]);

  // Карта slug-в-нижнем-регистре ("дмитрий_осипов") → user_id для кликабельных @-тегов.
  const mentionMap: Record<string, string> = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of activeUsersQ.data ?? []) {
      if (!u.id || !u.display_name) continue;
      const slug = u.display_name.trim().toLowerCase().replace(/\s+/g, "_");
      if (slug) map[slug] = u.id;
    }
    return map;
  }, [activeUsersQ.data]);

  // При навигации стрелками — скроллим активный пункт во вью внутри попапа.
  useEffect(() => {
    mentionActiveRef.current?.scrollIntoView({ block: "nearest" });
  }, [mentionIdx, mentionQuery]);

  function detectMentionAtCursor(value: string, caret: number) {
    // Ищем последний @ перед курсором без пробелов после него
    const before = value.slice(0, caret);
    const at = before.lastIndexOf("@");
    if (at < 0) { setMentionQuery(null); return; }
    // @ должен быть либо в начале строки, либо после пробела/переноса
    if (at > 0 && !/[\s\n]/.test(before[at - 1])) { setMentionQuery(null); return; }
    const word = before.slice(at + 1);
    // Если в слове уже есть пробел — мы вышли из mention-зоны
    if (/[\s\n]/.test(word)) { setMentionQuery(null); return; }
    if (word.length > 40) { setMentionQuery(null); return; }
    mentionStartRef.current = at;
    // Ресетим idx только если query реально изменилась — иначе при keyUp от
    // стрелок ↑↓ мы бы сбрасывали выбранный пункт обратно в 0
    setMentionQuery(prev => {
      if (prev !== word) setMentionIdx(0);
      return word;
    });
  }

  function applyMention(user: UserShort) {
    const ta = textareaRef.current;
    if (!ta) return;
    const slug = (user.display_name ?? "").trim().replace(/\s+/g, "_");
    if (!slug) return;
    const start = mentionStartRef.current;
    // Не доверяем ta.selectionStart — при mousedown фокус может слететь и вернуть 0.
    // Считаем end из known-данных: позиция @ + 1 + длина набранного запроса.
    const end = start + 1 + (mentionQuery ?? "").length;
    const insert = `@${slug} `;
    const isEditing = editingId !== null;
    const src = isEditing ? editingText : text;
    const next = src.slice(0, start) + insert + src.slice(end);
    if (isEditing) setEditingText(next); else setText(next);
    setMentionQuery(null);
    requestAnimationFrame(() => {
      const pos = start + insert.length;
      ta.focus();
      ta.setSelectionRange(pos, pos);
    });
  }

  const messagesQ = useQuery({
    queryKey: ["chat/messages", activeTopicId],
    // limit=200 — последние сообщения для cold-start. Покрывает 2-4 дня
    // активного чата. Раньше было 500 (тяжело), потом 60 (регресс: scroll-up
    // упирался в стену когда юзер ожидал увидеть больше истории). Сейчас 200
    // как baseline + scroll-up pagination через startReached → подгружает
    // ещё 80 более старых через fetchChatMessagesBefore.
    queryFn: async () => {
      if (!activeTopicId) return [];
      const fresh = await fetchChatMessages(activeTopicId, 0, 200, !showDeleted);
      // КРИТИЧНО: при scroll-up paginator подгрузил >200 старых сообщений
      // в cache. 30-sec polling возвращает ТОЛЬКО последние 200 → если просто
      // вернуть fresh, React Query заменит массив, Virtuoso потеряет старые
      // prepend'ы → юзера срывает вниз (потому что virtual-idx был в выкинутой
      // части). Решение: сохраняем из кэша всё, что СТАРШЕ минимального id
      // в свежем ответе (там polling не покрывает, и доверяем кэшу).
      const existing = qc.getQueryData<ChatMessage[]>(["chat/messages", activeTopicId]) ?? [];
      if (existing.length === 0 || fresh.length === 0) return fresh;
      let freshMinId = Infinity;
      for (const m of fresh) if (m.id > 0 && m.id < freshMinId) freshMinId = m.id;
      if (!Number.isFinite(freshMinId)) return fresh;
      const olderKept: ChatMessage[] = [];
      for (const m of existing) {
        if (m.id > 0 && m.id < freshMinId) olderKept.push(m);
      }
      if (olderKept.length === 0) return fresh;
      return [...olderKept, ...fresh].sort((a, b) => a.id - b.id);
    },
    enabled: !!activeTopicId,
    // Real-time приходит через SSE (см. startChatStream + chatStream.ts).
    // Polling оставлен как fallback на случай порванного SSE.
    // Пока активен jumpRemountKey (out-of-range pin-jump) — refetch ВЫКЛ,
    // иначе через 30с lента вернётся к tail и юзер «провалится» с pinned.
    refetchInterval: jumpRemountKey != null ? false : 30_000,
    refetchIntervalInBackground: false,
    structuralSharing: true,
    // staleTime: переключение топиков в пределах 60с не дёргает fetch —
    // SSE applyMessageInsert/Edit/Delete держит кэш свежим. Раньше каждый
    // switch топика = polled 200 messages из БД (~80-200ms на mobile).
    // Теперь switch обратно к недавно открытому топику — мгновенно.
    staleTime: 60_000,
    // gcTime поднят: cached message slice выживает в памяти 10мин даже если
    // ChatPage unmount'ится (NavBack/forward) — мгновенный возврат.
    gcTime: 10 * 60_000,
  });

  // Scroll-up paginator + firstItemIndex (Virtuoso backward-infinite-scroll)
  // извлечён в useScrollUpPaginator hook (P2#10e). См. hooks/useScrollUpPaginator.ts.
  const { firstItemIndex, loadMoreOlder, resetFirstItemIndex, loadingMore } = useScrollUpPaginator({
    activeTopicId,
    qc,
    baselineMsgLenRef,
    hideDeleted: !showDeleted,
  });



  const pinnedQ = useQuery({
    queryKey: ["chat/pinned", activeTopicId],
    queryFn: () => activeTopicId ? fetchChatPinned(activeTopicId) : Promise.resolve([]),
    enabled: !!activeTopicId,
    // Pin/unpin invalidate'ит этот ключ вручную (см. pinMut). Polling — fallback
    // на 60с (раньше 15с — pinned редко меняется, незачем дёргать).
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });

  const typingQ = useQuery({
    queryKey: ["chat/typing", activeTopicId],
    queryFn: () => activeTopicId ? fetchChatTyping(activeTopicId) : Promise.resolve([]),
    enabled: !!activeTopicId,
    // Typing приходит через SSE event 'typing' и патчится в этот же кэш-key
    // через qc.setQueryData (chatStream.ts). Polling выключен — раньше было
    // 1500ms (~40 RPS только на typing при 60 онлайн юзерах в фоне).
    refetchInterval: false,
    structuralSharing: true,
  });
  const topicStatsQ = useQuery({
    queryKey: ["chat/topic-stats", activeTopicId],
    queryFn: () => fetchTopicStats(activeTopicId!),
    enabled: !!activeTopicId,
    refetchInterval: 30_000,
    staleTime: 10_000,
    retry: 1,
  });

  const typingAllQ = useQuery({
    queryKey: ["chat/typing-all"],
    queryFn: fetchAllTyping,
    enabled: isMessengerRoute && bootSettled,   // первый fetch — после тихого старта, дальше SSE
    // Bulk-typing патчится через SSE (chatStream.ts applyTyping).
    // Initial fetch один раз при mount, дальше — push.
    refetchInterval: false,
  });
  const typingPingRef = useRef<number>(0);

  // Своя отметка «отправка в полёте» (не sendMut.isPending: тот обновляется
  // через ре-рендер с задержкой, а submit из очереди должен видеть актуальное).
  const sendInFlightRef = useRef(false);
  /** Текущая отправка: AbortController + таймер 20 с. Зависший запрос (iOS
   *  усыпил приложение посреди POST) раньше держал кнопку «Отправить»
   *  заблокированной до перезапуска; теперь обрывается по таймауту или сразу
   *  на возврате в приложение, onError возвращает текст в поле. */
  const sendAbortRef = useRef<{ ctrl: AbortController; startedAt: number; timer: number } | null>(null);
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      const cur = sendAbortRef.current;
      if (cur && Date.now() - cur.startedAt > 8000) {
        cur.ctrl.abort(new DOMException("нет ответа от сервера — проверьте связь и отправьте ещё раз", "AbortError"));
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);
  /** Снимок того, что уходит одним сообщением. Топик фиксируем на момент
   *  submit: если юзер переключит чат, пока сообщение в очереди, оно всё
   *  равно уйдёт туда, где было набрано. */
  type SendPayload = { topicId: number; text: string; replyTo: ChatReplyPreview | null; files: ChatAttachment[] };
  const sendQueueRef = useRef<SendPayload[]>([]);
  const dispatchSendRef = useRef<(p: SendPayload) => void>(() => {});
  const sendMut = useMutation({
    mutationFn: (vars: {
      topicId: number; text: string; replyToId: number | null; attachmentIds: number[];
      // Снапшот аттачей и reply'я для optimistic — нужен на случай быстрых
      // повторных submit'ов, когда state ещё не сбросился (replyTo/pendingFiles
      // могут быть несинхронизированы с предыдущим вызовом).
      _replyTo?: ChatReplyPreview | null;
      _attachments?: ChatAttachment[];
      _recoveryId?: string;
      _composerText?: string;
      _signal?: AbortSignal;
      meta?: Record<string, unknown>;
    }) =>
      sendChatMessage(vars.topicId, {
        text: vars.text,
        reply_to_id: vars.replyToId,
        reply_quote: vars._replyTo?.quote ?? null,
        attachment_ids: vars.attachmentIds,
        meta: vars._recoveryId
          ? { ...vars.meta, client_request_id: vars._recoveryId }
          : vars.meta,
      }, vars._signal),
    onMutate: async (vars) => {
      if (vars._recoveryId) setSendError(null);
      await qc.cancelQueries({ queryKey: ["chat/messages", vars.topicId] });
      const prev = qc.getQueryData<ChatMessage[]>(["chat/messages", vars.topicId]) ?? [];
      // Защита от пустого оптимистика: если по какой-то причине vars пришли
      // без текста и без аттачей — не добавляем bubble в чат (бекенд всё равно
      // вернёт 400). Пользователь видел "пустые сообщения" из-за того, что
      // оптимистик мог построиться раньше, чем пришли аттачи (race на быстрых
      // повторных submit'ах). Без оптимистика — пустота не показывается.
      const hasContent = !!vars.text.trim() || !!(vars._attachments && vars._attachments.length > 0);
      if (!hasContent) {
        return { prev };
      }
      const tempId = -Date.now() - Math.floor(Math.random() * 1000);
      const optimistic: ChatMessage = {
        id: tempId,
        topic_id: vars.topicId,
        author_user_id: user?.id ?? null,
        author_name: user?.display_name || user?.email || "Я",
        text: vars.text,
        created_at: new Date().toISOString().replace("Z", ""),
        reply_to: vars._replyTo ?? null,
        attachments: vars._attachments ?? [],
        // Помечаем текущий optimistic тем же id, что и сохраненный snapshot.
        // Recovery-effect отличит активную отправку от оставшейся после reload
        // и не покажет ложную красную ошибку до прихода ответа сервера.
        meta: vars._recoveryId
          ? { ...vars.meta, client_request_id: vars._recoveryId }
          : vars.meta,
      };
      qc.setQueryData<ChatMessage[]>(["chat/messages", vars.topicId], [...prev, optimistic]);
      // Imperative jumpBottom × 4 удалён — был причиной "подёргивания при
      // отправке" (4 visible scrollTop assignments). Virtuoso followOutput
      // сам pins к bottom при изменении data (после optimistic insert).
      return { prev };
    },
    onError: (err, vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["chat/messages", vars.topicId], ctx.prev);
      // Если сервер успел commit, но HTTP-ответ потерялся, refetch найдёт
      // сообщение по client_request_id и recovery-effect не даст его удвоить.
      void qc.invalidateQueries({ queryKey: ["chat/messages", vars.topicId] });
      if (vars._recoveryId && activeTopicIdRef.current === vars.topicId) {
        // Composer не очищается до success. Эти merge-гарды дополнительно
        // возвращают voice/file snapshot, если отправка шла не из main form.
        setText(current => current || vars._composerText || vars.text);
        setReplyTo(current => current ?? vars._replyTo ?? null);
        setPendingFiles(current => {
          const ids = new Set(current.map(file => file.id));
          const recovered = (vars._attachments ?? []).filter(file => !ids.has(file.id));
          return recovered.length > 0 ? [...current, ...recovered] : current;
        });
        const detail = err instanceof Error ? err.message : "Сетевая ошибка";
        setSendError(`Не удалось отправить: ${detail}. Текст и вложения сохранены — можно повторить.`);
      }
    },
    onSuccess: (real, vars) => {
      // Заменяем optimistic (id < 0) на реальное БЕЗ invalidate'а —
      // invalidate триггерит refetch + replace всего массива → Virtuoso
      // дёргает scroll. setQueryData делает точечную замену.
      qc.setQueryData<ChatMessage[]>(["chat/messages", vars.topicId], (old) => {
        if (!old) return [real];
        // Если в кэше уже есть real (по id) — не дублируем
        if (old.some(m => m.id === real.id)) {
          return old.filter(m => m.id >= 0);
        }
        let replaced = false;
        const out = old.map(m => {
          if (!replaced && m.id < 0) { replaced = true; return real; }
          return m;
        });
        if (!replaced) out.push(real);
        return out;
      });
      // Topics auto-обновится через SSE applyMessageInsert (optimistic setQueryData).
      // НЕ invalidate здесь — invalidate forces refetch + cascade re-render →
      // ChatPage → Virtuoso → visible jitter в момент отправки.
      // Cleanup draft этого топика — backend auto-удалит на своей стороне,
      // фронт зеркалит локально через useDrafts hook.
      if (vars._recoveryId) {
        clearPendingSend(vars._recoveryId);
        if (activeTopicIdRef.current === vars.topicId) {
          setText(current => current === (vars._composerText ?? vars.text) ? "" : current);
          setReplyTo(current => current?.id === vars.replyToId ? null : current);
          const sentIds = new Set(vars.attachmentIds);
          setPendingFiles(current => current.filter(file => !sentIds.has(file.id)));
          setSendError(null);
        }
      }
      clearDraftForTopic(vars.topicId);
    },
    onSettled: () => {
      if (sendAbortRef.current) {
        window.clearTimeout(sendAbortRef.current.timer);
        sendAbortRef.current = null;
      }
      // Следующее из очереди — сразу, в том порядке, как набирали.
      const next = sendQueueRef.current.shift();
      if (next) { dispatchSendRef.current(next); return; }
      sendInFlightRef.current = false;
      // Комментарий к пересылке ушёл — теперь сама пересылка (порядок как в TG).
      const fwdAfter = pendingForwardRef.current;
      if (fwdAfter) { pendingForwardRef.current = null; runForwardRef.current(fwdAfter); }
    },
  });

  // ─── Scheduled messages ─────────────────────────────────────────
  const [schedPickerOpen, setSchedPickerOpen] = useState(false);
  const [schedListOpen, setSchedListOpen] = useState(false);
  const [schedEditing, setSchedEditing] = useState<ScheduledMessage | null>(null);
  // Long-press на кнопку «Отправить» на мобиле → всплывающее меню «Отложить»
  const [sendActionMenuOpen, setSendActionMenuOpen] = useState(false);
  const sendLongPressTimer = useRef<number | null>(null);
  const sendLongPressFired = useRef<boolean>(false);
  const sendTouchMoved = useRef<boolean>(false);
  const scheduledQ = useQuery({
    queryKey: ["chat/scheduled", activeTopicId],
    queryFn: () => activeTopicId ? fetchScheduledInTopic(activeTopicId) : Promise.resolve([]),
    enabled: !!activeTopicId,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
  const createSchedMut = useMutation({
    mutationFn: (vars: { topicId: number; text: string; replyToId: number | null; attachmentIds: number[]; scheduledFor: string }) =>
      createScheduledMessage(vars.topicId, {
        text: vars.text,
        reply_to_id: vars.replyToId,
        attachment_ids: vars.attachmentIds,
        scheduled_for: vars.scheduledFor,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["chat/scheduled", vars.topicId] });
      // Очищаем composer (как после обычной отправки)
      setText("");
      setReplyTo(null);
      setPendingFiles([]);
      clearDraftForTopic(vars.topicId);
    },
    onError: (e: Error) => alert(`Не удалось отложить: ${e.message}`),
  });
  const cancelSchedMut = useMutation({
    mutationFn: (sid: number) => cancelScheduledMessage(sid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat/scheduled", activeTopicId] }),
  });
  const sendSchedNowMut = useMutation({
    mutationFn: (sid: number) => sendScheduledNow(sid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat/scheduled", activeTopicId] });
      qc.invalidateQueries({ queryKey: ["chat/messages", activeTopicId] });
    },
  });
  const editSchedMut = useMutation({
    mutationFn: (vars: { sid: number; text?: string; scheduledFor?: string }) =>
      patchScheduledMessage(vars.sid, {
        text: vars.text,
        scheduled_for: vars.scheduledFor,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat/scheduled", activeTopicId] }),
    onError: (e: Error) => alert(`Не удалось изменить: ${e.message}`),
  });

  const editMut = useMutation({
    mutationFn: (vars: { id: number; text: string }) => editChatMessage(vars.id, vars.text),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["chat/messages", activeTopicId] });
      qc.invalidateQueries({ queryKey: ["chat/topics"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteChatMessage(id),
    onMutate: async (id) => {
      if (!activeTopicId) return;
      await qc.cancelQueries({ queryKey: ["chat/messages", activeTopicId] });
      const prev = qc.getQueryData<ChatMessage[]>(["chat/messages", activeTopicId]) ?? [];
      qc.setQueryData<ChatMessage[]>(
        ["chat/messages", activeTopicId],
        prev.map(m => m.id === id ? { ...m, deleted_at: new Date().toISOString(), text: "" } : m),
      );
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev && activeTopicId) qc.setQueryData(["chat/messages", activeTopicId], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["chat/messages", activeTopicId] });
      qc.invalidateQueries({ queryKey: ["chat/topics"] });
      qc.invalidateQueries({ queryKey: ["chat/pinned", activeTopicId] });
    },
  });

  const restoreMut = useMutation({
    mutationFn: (id: number) => restoreChatMessage(id),
    onMutate: async (id) => {
      if (!activeTopicId) return;
      await qc.cancelQueries({ queryKey: ["chat/messages", activeTopicId] });
      const prev = qc.getQueryData<ChatMessage[]>(["chat/messages", activeTopicId]) ?? [];
      // Оптимистично снимаем deleted_at; текст подтянется при invalidate
      // (на бэке text сохранён — soft-delete его не стирает).
      qc.setQueryData<ChatMessage[]>(
        ["chat/messages", activeTopicId],
        prev.map(m => m.id === id ? { ...m, deleted_at: null } : m),
      );
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev && activeTopicId) qc.setQueryData(["chat/messages", activeTopicId], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["chat/messages", activeTopicId] });
      qc.invalidateQueries({ queryKey: ["chat/topics"] });
    },
  });

  const pinMut = useMutation({
    mutationFn: (vars: { id: number; pin: boolean }) =>
      vars.pin ? pinChatMessage(vars.id) : unpinChatMessage(vars.id) as Promise<unknown>,
    onMutate: async (vars) => {
      if (!activeTopicId) return;
      // Optimistic: ставим/снимаем pinned_at сразу, не ждём network. Если сервер
      // вернёт ошибку — onError откатит. Без этого иконка пина «висит» 100-300мс
      // как зависшая (хуже всего на slow network).
      await qc.cancelQueries({ queryKey: ["chat/messages", activeTopicId] });
      const prev = qc.getQueryData<ChatMessage[]>(["chat/messages", activeTopicId]) ?? [];
      const ts = new Date().toISOString();
      qc.setQueryData<ChatMessage[]>(
        ["chat/messages", activeTopicId],
        prev.map(m => m.id === vars.id ? { ...m, pinned_at: vars.pin ? ts : null } : m),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev && activeTopicId) qc.setQueryData(["chat/messages", activeTopicId], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["chat/messages", activeTopicId] });
      qc.invalidateQueries({ queryKey: ["chat/pinned", activeTopicId] });
    },
  });

  const reactMut = useMutation({
    mutationFn: (vars: { messageId: number; emoji: string }) =>
      toggleChatReaction(vars.messageId, vars.emoji),
    onMutate: async (vars) => {
      if (!activeTopicId) return;
      await qc.cancelQueries({ queryKey: ["chat/messages", activeTopicId] });
      const prev = qc.getQueryData<ChatMessage[]>(["chat/messages", activeTopicId]) ?? [];
      // Снапшот текущего юзера для optimistic users[] — иначе бабл сначала
      // показывает "1" (count fallback), а аватарка появляется только после
      // refetch'а с сервера.
      const myMini = {
        user_id: user?.id ?? "",
        display_name: user?.display_name || user?.email || "Я",
        avatar_filename: user?.avatar_filename ?? null,
      };
      const myUid = myMini.user_id;
      qc.setQueryData<ChatMessage[]>(
        ["chat/messages", activeTopicId],
        prev.map(m => {
          if (m.id !== vars.messageId) return m;
          const reactions = m.reactions ?? [];
          const existing = reactions.find(r => r.emoji === vars.emoji);
          let next = reactions;
          if (existing) {
            if (existing.mine) {
              // Снимаем свою реакцию
              next = existing.count === 1
                ? reactions.filter(r => r.emoji !== vars.emoji)
                : reactions.map(r => r.emoji === vars.emoji ? {
                    ...r,
                    count: r.count - 1,
                    mine: false,
                    users: (r.users || []).filter(u => u.user_id !== myUid),
                  } : r);
            } else {
              // Добавляем свою к существующим
              next = reactions.map(r => r.emoji === vars.emoji ? {
                ...r,
                count: r.count + 1,
                mine: true,
                users: [...(r.users || []), myMini],
              } : r);
            }
          } else {
            // Новая реакция — populate users[] чтобы отрендерить аватарку сразу
            next = [...reactions, { emoji: vars.emoji, count: 1, mine: true, users: [myMini] }];
          }
          return { ...m, reactions: next };
        }),
      );
      return { prev };
    },
    onError: (_err, _v, ctx) => {
      if (ctx?.prev && activeTopicId) qc.setQueryData(["chat/messages", activeTopicId], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["chat/messages", activeTopicId] });
    },
  });

  // Drafts (topic-switch + debounced save) → useDrafts hook (P2#10d).

  // Авто-выбор топика: приоритет ?topic=N → localStorage → первый в списке.
  // localStorage сохраняет последний открытый чат, чтобы рефреш страницы
  // не выбрасывал на "Общий чат" каждый раз — НО только на десктопе. На
  // мобильном тап по «Чат команды» должен показывать список чатов как
  // лендинг (юзер сам выбирает, в какой заходить), а не сразу прыгать в
  // последний открытый.
  useEffect(() => {
    if (!activeTopicId && topicsQ.data && topicsQ.data.length > 0) {
      const isMobile = typeof window !== "undefined" && window.innerWidth < 900;
      const fromQs = search.topic ? Number(search.topic) : NaN;
      // На мобильном — только явный ?topic=N из URL запускает авто-выбор.
      // Без URL-параметра → активный топик остаётся null → mobile-view = "topics".
      if (isMobile) {
        const target = topicsQ.data.find((t) => t.id === fromQs);
        if (target) setActiveTopicId(target.id);
        return;
      }
      // Десктоп: URL → localStorage → первый.
      const fromLs = Number(localStorage.getItem("chat:lastTopicId") || "");
      const target =
        topicsQ.data.find((t) => t.id === fromQs)
        ?? topicsQ.data.find((t) => t.id === fromLs)
        ?? topicsQ.data[0];
      if (target) setActiveTopicId(target.id);
    }
  }, [topicsQ.data, activeTopicId, search.topic]);

  // Сохраняем последний открытый топик в localStorage при каждом переключении
  useEffect(() => {
    if (activeTopicId) {
      try { localStorage.setItem("chat:lastTopicId", String(activeTopicId)); } catch { /* quota */ }
    }
  }, [activeTopicId]);

  // ?topic=N — переключение текущего топика при изменении URL.
  // КРИТИЧНО: НЕ ставим activeTopicId в deps. Иначе при клике юзера на другой
  // чат в sidebar (setActiveTopicId(X)) эффект ре-фаерится, видит что URL.topic
  // (старое значение) ≠ activeTopicId (новое) → насильно возвращает обратно
  // → юзер не может переключиться (баг "экран дёргается и возвращает в общий чат").
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!search.topic) return;
    const id = Number(search.topic);
    if (Number.isFinite(id)) {
      setActiveTopicId(id);
    }
  }, [search.topic]);

  // ?jump=M — после загрузки сообщений автопрокрутка к нужному сообщению + flash.
  // Ref-guard чтобы не дёргать на каждый polling-апдейт messagesQ.data.
  const jumpedToRef = useRef<string | number | null>(null);
  useEffect(() => {
    if (!search.jump || !messagesQ.data) return;
    if (jumpedToRef.current === search.jump) return; // уже прыгали к этому id
    const id = Number(search.jump);
    if (!Number.isFinite(id)) return;
    const exists = messagesQ.data.some((m) => m.id === id);
    if (!exists) return;
    jumpedToRef.current = search.jump;
    const t = window.setTimeout(() => jumpToMessage(id), 80);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.jump, messagesQ.data]);

  // Edge-swipe от левого края на мобильном в режиме messages → возврат
  // к списку чатов (как «назад» в TG). Перехватывает ДО AppShell-handler'а
  // (там history.back) — AppShell проверяет body.kb-in-chat-messages и
  // пропускает свой свайп, чтобы не было конфликта.
  useEffect(() => {
    if (mobileView !== "messages") return;
    const shell = document.querySelector(".chat-shell") as HTMLElement | null;
    if (!shell) return;
    const getMain = () => shell.querySelector(".chat-main") as HTMLElement | null;
    let startX = 0, startY = 0, active = false, moved = false, curDx = 0;

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) { active = false; return; }
      const inOverlay = !!document.querySelector(".media-lightbox, .video-lightbox, .img-upload-modal, .topic-media-modal, .file-preview-modal, .ctx-menu, .focal-export-backdrop, .fwd-bg");
      if (inOverlay) { active = false; return; }
      // Касание в поле ввода — выделение текста/курсор, а не жест «назад» (29.09.2026)
      const el = e.target as HTMLElement | null;
      if (el?.closest?.('input, textarea, [contenteditable="true"], [contenteditable=""]')) { active = false; return; }
      const t = e.touches[0];
      // Старт ТОЛЬКО от левого края (iOS-back), иначе конфликт со скроллом контента
      if (t.clientX <= 28) {
        startX = t.clientX; startY = t.clientY; active = true; moved = false; curDx = 0;
      } else { active = false; }
    };
    const onMove = (e: TouchEvent) => {
      if (!active || !e.touches[0]) return;
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      if (!moved) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        // вертикальный жест — это скролл, не наш свайп
        if (Math.abs(dy) > Math.abs(dx)) { active = false; return; }
        moved = true;
        shell.classList.add("is-swiping-back");  // показать сайдбар под чатом
      }
      // горизонтальный свайп — забираем у iOS (иначе transform заморожен в touchmove)
      e.preventDefault();
      curDx = Math.max(0, dx);
      const main = getMain();
      // setProperty(..., "important") ОБЯЗАТЕЛЕН: в чате transform на .chat-main
      // перебивается CSS-!important (kb-view-in / reduced-motion), инлайн без
      // important молча гасится → чат не едет за пальцем [[inline_style_vs_important]].
      if (main) {
        main.style.setProperty("transition", "none", "important");
        main.style.setProperty("transform", `translateX(${curDx}px)`, "important");
      }
    };
    const finish = (commit: boolean) => {
      const main = getMain();
      const w = shell.clientWidth || window.innerWidth;
      if (main) {
        main.style.setProperty("transition", "transform 0.24s cubic-bezier(0.32, 0.72, 0, 1)", "important");
        main.style.setProperty("transform", commit ? `translateX(${w}px)` : "translateX(0)", "important");
      }
      window.setTimeout(() => {
        if (commit) {
          // is-swipe-exit (в ОДНОМ рендере с mobile-view-topics) гасит kb-view-in-left
          // — сайдбар уже виден под чатом, второй прогон анимации не нужен.
          setSwipeExiting(true);
          // Smart back: drawer (stack>1) → pop; drawer (stack=1) → close; иначе → список
          if (profileDrawerOpen) {
            setProfileDrawerStack((prev) => { if (prev.length > 1) return prev.slice(0, -1); setProfileDrawerOpen(false); return []; });
          } else {
            setActiveTopicId(null);
            setMobileView("topics");
          }
          // Флаг снимается НЕ по таймеру (это перезапускало бы kb-view-in-left,
          // т.к. animation-name менялся none→kb-view-in-left), а когда сайдбар
          // снова скрыт — при открытии чата (эффект ниже, mobileView==messages).
        }
        // очистка после переключения/отката
        shell.classList.remove("is-swiping-back");
        if (main) { main.style.removeProperty("transition"); main.style.removeProperty("transform"); }
      }, commit ? 200 : 240);
    };
    const onEnd = () => {
      if (!active) return;
      active = false;
      if (!moved) return;
      const w = shell.clientWidth || window.innerWidth;
      finish(curDx > Math.min(120, w * 0.32));  // порог: >120px или >32% ширины
    };
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onEnd, { passive: true });
    window.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onEnd);
    };
  }, [mobileView, profileDrawerOpen]);

  // Снимаем is-swipe-exit когда сайдбар СКРЫТ (открыт чат) — тогда смена
  // animation-name none→kb-view-in-left не даёт видимого ре-триггера, а
  // следующий возврат стрелкой снова анимируется нормально.
  useEffect(() => {
    if (mobileView === "messages") setSwipeExiting(false);
  }, [mobileView]);

  // Сбрасываем composer и picker при смене топика. На мобильном —
  // переключаемся на view с сообщениями.
  useEffect(() => {
    setReplyTo(null);
    setPendingFiles([]);
    setSendError(null);
    setEditingId(null);
    setText("");
    setReactionPicker(null);
    setComposerPickerOpen(false);
    setContextMenu(null);
    setPinnedIdx(0);
    if (activeTopicId !== null) setMobileView("messages");
  }, [activeTopicId]);

    // Закрываем контекст-меню только по Escape. scroll/resize listeners УБРАНЫ:
  // на iOS PWA после long-press release фаерится фантомный scroll (rubberband
  // settle от поднятого пальца) через 1-2с, и автозакрытие срабатывало само,
  // создавая ощущение «меню само закрылось через 2-3с после клика».
  useEffect(() => {
    if (contextMenu === null) return;
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape") setContextMenu(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [contextMenu]);

  // Sync sticky → contextMenu. Если contextMenu становится null, держим
  // sticky ещё 300мс чтобы CSS exit-анимация успела проиграться.
  useEffect(() => {
    if (contextMenu !== null) {
      setStickyContext(contextMenu);
      return;
    }
    const t = window.setTimeout(() => setStickyContext(null), 300);
    return () => window.clearTimeout(t);
  }, [contextMenu]);

  // === Vanilla JS context menu (вне React) ===
  // function-declarations (pickReaction, startReply, etc.) подняты в JS,
  // доступны через closure напрямую без ref-индирекции.
  useEffect(() => {
    if (!contextMenu) {
      vanillaCloseContextMenu();
      return;
    }
    const m = messages.find((x) => x.id === contextMenu.messageId);
    if (!m) return;
    const mine = m.author_user_id === myUserId;
    const canEditMine = mine && !m.deleted_at && m.id > 0;
    const canDelete = (mine || !!user?.is_superuser) && !m.deleted_at && m.id > 0;
    const canRestore = (mine || !!user?.is_superuser) && !!m.deleted_at && m.id > 0;
    vanillaOpenContextMenu({
      message: m,
      attachmentId: contextMenu.attachmentId,
      position: {
        pillTop: contextMenu.pillTop,
        pillLeft: contextMenu.pillLeft,
        menuTop: contextMenu.menuTop,
        menuLeft: contextMenu.menuLeft,
        liftOffset: contextMenu.liftOffset,
        menuMaxHeight: contextMenu.menuMaxHeight,
      },
      isMobile: isMobileVw,
      canEditMine,
      canDelete,
      canRestore,
      quickEmoji: REACTION_EMOJI.slice(0, 8),
      quotedText: contextMenu.quote,
      readByEnabled: mine && !m.deleted_at,
      avatarSrc: (filename, userId) => userAvatarUrl(userId, filename) || "",
      callbacks: {
        onReact: (emo: string) => { setContextMenu(null); pickReaction(m.id, emo); },
        onMoreReactions: (anchor: HTMLElement) => {
          const rect = anchor.getBoundingClientRect();
          setContextMenu(null);
          openReactionPicker(m.id, rect);
        },
        onReply: () => { setContextMenu(null); startReply(m); },
        onQuoteReply: (quote: string) => { setContextMenu(null); startReply(m, quote); },
        onCopyText: () => { setContextMenu(null); copyMessageText(m.text); },
        onEdit: () => { setContextMenu(null); startEdit(m); },
        onPin: () => { setContextMenu(null); togglePin(m); },
        onForward: () => {
          setContextMenu(null);
          // Открыть forward-picker для этого сообщения. Раньше копировалась
          // ссылка в clipboard silently — юзер думал что "не работает".
          setSelectedIds(new Set([m.id]));
          setSelectedAttachmentIds(new Set());
          setForwardPickerOpen(true);
        },
        onDelete: () => { setContextMenu(null); confirmDelete(m.id); },
        onRestore: () => { setContextMenu(null); restoreMut.mutate(m.id); },
        onSelect: () => {
          setContextMenu(null);
          setSelectionMode(true);
          setSelectedIds(new Set([m.id]));
        },
        onSaveAttachment: async (aid: number) => {
          setContextMenu(null);
          const att = m.attachments?.find((a) => a.id === aid);
          if (!att) return;
          // Единый механизм для фото/видео/файлов: ПК — <a download> с
          // ?download=1, телефон — Web Share (см. lib/downloadAttachment).
          await downloadChatAttachment({ attachmentId: aid, filename: att.filename });
        },
        onJumpToProfile: (userId: string) => { setContextMenu(null); if (isMessengerRoute) openUserProfile(userId); else setProfileLightboxId(userId); },
        onCopyImage: async (aid: number) => {
          setContextMenu(null);
          try {
            const resp = await fetch(chatAttachmentUrl(aid), { credentials: "include" });
            const blob = await resp.blob();
            const bmp = await createImageBitmap(blob);
            const canvas = document.createElement("canvas");
            canvas.width = bmp.width;
            canvas.height = bmp.height;
            canvas.getContext("2d")!.drawImage(bmp, 0, 0);
            canvas.toBlob(async (pngBlob) => {
              if (!pngBlob) return;
              await navigator.clipboard.write([new ClipboardItem({ "image/png": pngBlob })]);
            }, "image/png");
          } catch (err) {
            console.error("copy image failed", err);
            alert("Не удалось скопировать изображение");
          }
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMenu]);

  // Закрываем emoji-picker (реакций) — по клику вне, Escape или скролл/resize
  useEffect(() => {
    if (reactionPicker === null) return;
    const close = () => setReactionPicker(null);
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".chat-emoji-picker") && !target.closest(".chat-msg-action[data-act='react']")) {
        close();
      }
    };
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape") close(); };
    const feedEl = feedRef.current;
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    feedEl?.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      feedEl?.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [reactionPicker]);

  /** Открыть emoji-picker реакций. position:fixed coords рассчитываются от
   *  rect кнопки. Если кнопка в правой половине экрана — picker выравнивается
   *  по правому краю кнопки (растёт влево), чтобы не упираться в край viewport. */
  function openReactionPicker(messageId: number, anchor: HTMLElement | DOMRect) {
    const rect = anchor instanceof Element ? anchor.getBoundingClientRect() : anchor;
    const PICKER_W = 296;
    const PICKER_H = 200;
    const isMobile = window.innerWidth < 900;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top: number;
    let left: number;
    if (isMobile) {
      // На мобиле — снизу экрана, ближе к большому пальцу
      top = vh - PICKER_H - 100;
      left = (vw - PICKER_W) / 2;
    } else {
      // ПК — правило 5/30/50: 5px от кнопки, флип при 30px до края, 50px при флипе
      const GAP = 5;
      const NEAR_EDGE = 30;
      const FLIP_MARGIN = 50;
      // X: по умолчанию слева от кнопки выравниваем
      left = rect.left;
      if (left + PICKER_W > vw - NEAR_EDGE) {
        // Флип: правый край picker'а у правого края кнопки
        left = rect.right - PICKER_W;
        if (left < FLIP_MARGIN) left = FLIP_MARGIN;
      }
      if (left < FLIP_MARGIN) left = FLIP_MARGIN;
      if (left + PICKER_W > vw - FLIP_MARGIN) left = vw - PICKER_W - FLIP_MARGIN;
      // Y: по умолчанию под кнопкой 5px
      top = rect.bottom + GAP;
      if (top + PICKER_H > vh - NEAR_EDGE) {
        // Флип: над кнопкой
        top = rect.top - PICKER_H - GAP;
        if (top < FLIP_MARGIN) top = FLIP_MARGIN;
      }
      if (top < FLIP_MARGIN) top = FLIP_MARGIN;
    }
    setReactionPicker({ messageId, top, left });
  }

  // Закрываем composer-picker по клику вне
  useEffect(() => {
    if (!composerPickerOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".chat-emoji-picker.is-composer") && !target.closest("[data-act='composer-emoji']")) {
        setComposerPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [composerPickerOpen]);

  // При смене активного topic — сбросить snapshot в undefined чтобы
  // auto-mark-read дождался свежего snapshot.
  useEffect(() => {
    setUnreadSnapshotLastReadId(undefined);
    snapshotTopicRef.current = null;
    scrolledToUnreadRef.current = null;
  }, [activeTopicId]);

  // Tracker: max id для каждого topic — нужно для cleanup mark-read
  // (closure messagesQ.data может уже содержать НОВЫЕ messages).
  useEffect(() => {
    const msgs = messagesQ.data;
    if (!activeTopicId || !msgs || !msgs.length) return;
    const lastId = Math.max(...msgs.filter(m => m.id > 0).map(m => m.id), 0);
    if (lastId > 0) lastMaxMsgIdRef.current[activeTopicId] = lastId;
  }, [activeTopicId, messagesQ.data]);

  // Snapshot при открытии нового topic. Сохраняем last_read_id (НЕ +1).
  // Первый непрочитанный = первый m.id > last_read_id среди реальных messages
  // (ID — глобальный AUTOINCREMENT, пробелы между топиками неизбежны).
  useEffect(() => {
    if (!activeTopicId || !topicsQ.data) return;
    if (snapshotTopicRef.current === activeTopicId) return;
    const t = topicsQ.data.find(x => x.id === activeTopicId);
    if (!t) return;
    snapshotTopicRef.current = activeTopicId;
    const lastReadId = t.last_read_id ?? 0;
    const unread = t.unread_count ?? 0;
    // eslint-disable-next-line no-console
    console.log("[unread-snapshot] topic=" + activeTopicId + " name=" + t.name + " lastReadId=" + lastReadId + " unread=" + unread);
    // ВАЖНО: lastReadId может быть 0 если юзер НИКОГДА не заходил в топик.
    // В этом случае все сообщения непрочитанные — divider должен быть перед
    // самым старым (id > 0 всегда). Раньше условие "&& lastReadId > 0"
    // отправляло snapshot=null → divider не показывался → лента открывалась
    // внизу, а не на первом непрочитанном.
    if (unread > 0) {
      setUnreadSnapshotLastReadId(lastReadId);
    } else {
      setUnreadSnapshotLastReadId(null);
    }
  }, [activeTopicId, topicsQ.data]);

  // Computed: id первого реального непрочитанного message в загруженной ленте.
  // Использует snapshot lastReadId + актуальный messages array.
  const firstUnreadId = useMemo<number | null | undefined>(() => {
    if (unreadSnapshotLastReadId === undefined) return undefined; // snapshot ещё нет
    if (unreadSnapshotLastReadId === null) return null;            // всё прочитано
    const msgs = messagesQ.data;
    if (!msgs || !msgs.length) return undefined; // messages ещё не загружены
    const found = msgs.find(m => m.id > 0 && m.id > unreadSnapshotLastReadId);
    return found ? found.id : null;
  }, [unreadSnapshotLastReadId, messagesQ.data]);

  // Mark-as-read когда меняется max id. УСЛОВИЯ:
  //   - snapshot сделан (firstUnreadId !== undefined). Иначе обнулим last_read_id
  //     до того как snapshot успел его захватить → divider не покажется.
  //   - divider скрыт (firstUnreadId === null) ЛИБО юзер at-bottom (уже видит
  //     все непрочитанные визуально). Раньше блок «if firstUnreadId!==null»
  //     заставлял badge в sidebar висеть до выхода из чата — фикс позволяет
  //     обнулить unread как только лента прокрутилась к bottom.
  useEffect(() => {
    const msgs = messagesQ.data;
    if (!activeTopicId || !msgs || msgs.length === 0) return;
    // УБРАЛИ условие "ждём пока юзер дойдёт до bottom" — теперь чат всегда
    // открывается внизу, marker-read шлём сразу как только знаем lastId.
    const lastId = Math.max(...msgs.filter(m => m.id > 0).map(m => m.id), 0);
    if (lastId <= 0) return;
    if (lastMarkedReadRef.current[activeTopicId] === lastId) return;
    lastMarkedReadRef.current[activeTopicId] = lastId;
    // OPTIMISTIC: моментально обнуляем unread_count в кэше топиков ДО запроса.
    qc.setQueryData<ChatTopic[]>(["chat/topics"], (prev) => {
      if (!prev) return prev;
      return prev.map(t => t.id === activeTopicId ? { ...t, unread_count: 0, manual_unread: false } : t);
    });
    markChatRead(activeTopicId, lastId).then(() => {
      qc.invalidateQueries({ queryKey: ["chat/topics"] });
    }).catch(() => {
      delete lastMarkedReadRef.current[activeTopicId];
    });
  }, [activeTopicId, messagesQ.data, qc, pulseTick]);

  // При смене topic ИЛИ unmount → mark-read для прошлого topic.
  // ВАЖНО: используем lastMaxMsgIdRef[prevTopic] (запомненный max),
  // а НЕ messagesQ.data из closure (она может быть данными нового topic).
  useEffect(() => {
    return () => {
      const prevTopic = snapshotTopicRef.current;
      if (prevTopic == null) return;
      const lastId = lastMaxMsgIdRef.current[prevTopic];
      if (!lastId || lastId <= 0) return;
      markChatRead(prevTopic, lastId).catch(() => {});
      lastMarkedReadRef.current[prevTopic] = lastId;
    };
  }, [activeTopicId]);

  // Подсветка первого непрочитанного + dismiss snapshot если <= 2 unread.
  // САМ scroll к divider'у делает Virtuoso через initialTopMostItemIndex
  // (mount-time, без race c followOutput/alignToBottom). Этот useEffect
  // отвечает только за:
  //   - highlight target (зелёный flash) когда юзер ВПЕРВЫЕ видит divider
  //   - если unread <= 2 — snapshot:=null чтобы убрать divider и оставить
  //     default scroll-в-низ (видно всё всё равно).
  // useEffect для scroll-к-непрочитанному УДАЛЁН: открываем чат всегда внизу.

  // Auto-grow textarea — растём до 200px, дальше scroll внутри.
  // ВАЖНО: используем setProperty(.., 'important'), потому что в
  // messenger-mobile.css прописан `height: auto !important` на textarea —
  // обычный inline style без important проигрывает CSS-правилу с important.
  // ВАЖНО: вынесли в requestAnimationFrame чтобы браузер мог сбатчить
  // reflow с другими DOM-изменениями. Раньше на каждый keystroke было
  // 2 forced layouts (reset height → read scrollHeight → set height) —
  // на длинных сообщениях на iPhone это давало видимый input-lag.
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    // RichComposer (contenteditable, 30.09.2026) растёт сам по CSS: сброс высоты +
    // чтение scrollHeight здесь = два принудительных пересчёта раскладки на каждый
    // символ (замер 07.10: четверть CPU при наборе, лаг и «зависание» на длинном тексте).
    if (ta.el) return;
    let rafId = 0;
    rafId = requestAnimationFrame(() => {
      // 1) сбросить inline-height чтобы scrollHeight корректно посчитал контент
      ta.style.setProperty("height", "auto", "important");
      const next = Math.min(ta.scrollHeight, 200);
      // 2) установить новую высоту + переключить overflow при упоре в 200
      ta.style.setProperty("height", `${next}px`, "important");
      ta.style.setProperty("overflow-y", next >= 200 ? "auto" : "hidden", "important");
    });
    return () => cancelAnimationFrame(rafId);
  }, [text, editingText, editingId]);

  // Auto-scroll вниз: при первом загрузке сообщений топика (0 → N) —
  // принудительно вниз. При последующих обновлениях — только если юзер
  // и так был у низа (≤ 120px), иначе не дёргаем (юзер листает историю).
  const prevMsgCountRef = useRef(0);
  const initialScrollDoneRef = useRef<number | null>(null);  // запомнить topic для которого initial scroll сделан
  useEffect(() => {
    const feedEl = feedRef.current;
    const cnt = (messagesQ.data ?? []).length;
    // Ждём snapshot firstUnreadId перед тем как решать init-scroll
    // (иначе initial-scroll-to-bottom может конфликтовать с scroll-к-divider).
    if (cnt > 0 && firstUnreadId === undefined) return;
    const wasZero = prevMsgCountRef.current === 0;
    const grew = cnt > prevMsgCountRef.current;
    prevMsgCountRef.current = cnt;
    if (!feedEl || cnt === 0) return;

    // Initial scroll на вход в чат — плавно к самому ДНУ.
    // Стратегия: один instant-snap ("телепорт" вниз чтобы быть рядом),
    // затем единственный smooth-scroll догоняет, и пара поздних smooth
    // на случай если картинки/аудио поменяли высоты после load.
    // ВАЖНО: если есть divider "Новые сообщения" (firstUnreadId !== null),
    // initial-to-bottom ПРОПУСКАЕМ — отдельный useEffect скроллит к divider'у.
    // Иначе late-correction через 1.8/2.4с дёргает юзера который уже листает.
    // [DISABLED 2026-05-18] initial-scroll-to-bottom (instant-jump +
    // animateScrollToBottom 1.6s + lateSmoothCorrection × 2) выключен по
    // запросу юзера. Virtuoso initialTopMostItemIndex={999_999} сам показывает
    // последние сообщения на mount. Если контент дорастёт от lazy картинок —
    // ResizeObserver (если ещё включён) удержит позицию, но без late corrections.
    if (false && wasZero && initialScrollDoneRef.current !== activeTopicId && firstUnreadId === null) {
      initialScrollDoneRef.current = activeTopicId;
      return;
    }

    if (!grew) return;
    // Auto-scroll к новому сообщению — теперь делает Virtuoso followOutput.
    // Раньше тут был messagesEndRef.scrollIntoView, но он конфликтовал:
    // прыгал к ref-позиции (предпоследнему), Virtuoso параллельно — к last index.
    void chatLightboxAid;
  }, [messagesQ.data, activeTopicId, chatLightboxAid, firstUnreadId]);

  // Lightbox open → моментально скрыть sticky-date pill. Без этого pill
  // оставался видимым поверх fullscreen image, пока не было scroll-event.
  useEffect(() => {
    if (chatLightboxAid !== null && stickyPillRef.current) {
      stickyPillRef.current.style.visibility = "hidden";
      stickyLastLabelRef.current = null;
    }
  }, [chatLightboxAid]);

  // При смене топика сбрасываем счётчик и initialScrollDone + блок paginator на 1.2с.
  useEffect(() => {
    prevMsgCountRef.current = 0;
    initialScrollDoneRef.current = null;
    paginatorReadyAtRef.current = Date.now() + 1200;
  }, [activeTopicId]);

  // SAFETY: после смены топика — несколько ретраев force-scroll-to-bottom.
  // Virtuoso initialTopMostItemIndex иногда промахивается из-за неизмеренных
  // image-heights, юзер видит чат в середине и должен ручками листать вниз.
  // Эти явные scrollToIndex({align:"end"}) подтягивают вниз сразу после mount
  // и через 400/1000ms когда картинки догрузились.
  useEffect(() => {
    if (!activeTopicId) return;
    // Если юзер начал листать историю (wheel/touchmove) сразу после открытия —
    // НЕ дёргаем его обратно вниз. Раньше forceBottom@500/1200мс срывал вверх-
    // листающего юзера назад к низу («рывок вниз ОДИН РАЗ» при листании сразу
    // после открытия DM/чата). Initial-коррекция@150мс (чат открылся в середине
    // из-за неизмеренных картинок) успевает до того как юзер начнёт листать;
    // image-shift коррекция@500/1200 работает пока юзер пассивно стоит у дна.
    let userScrolled = false;
    const mark = () => { userScrolled = true; };
    const wrap = document.querySelector(".chat-feed-wrap");
    if (wrap) {
      wrap.addEventListener("wheel", mark, { passive: true });
      wrap.addEventListener("touchmove", mark, { passive: true });
    }
    const forceBottom = () => {
      if (userScrolled) return;
      const msgs = messagesRef.current;
      if (msgs.length === 0) return;
      virtuosoRef.current?.scrollToIndex({
        index: firstItemIndex + msgs.length - 1,
        align: "end",
        behavior: "auto",
      });
    };
    const t1 = window.setTimeout(forceBottom, 150);
    const t2 = window.setTimeout(forceBottom, 500);
    const t3 = window.setTimeout(forceBottom, 1200);
    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      if (wrap) {
        wrap.removeEventListener("wheel", mark);
        wrap.removeEventListener("touchmove", mark);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTopicId]);

  // Post-mount scroll-correction перенесён ПОСЛЕ `const messages = allMessages`
  // (см. ниже) — иначе TDZ при минификации (`messages` declaration ниже).

  // Маркер: новое сообщение только что пришло. ResizeObserver скроллит
  // вниз ТОЛЬКО если флаг true — иначе не дёргаем юзера на любой
  // internal resize (lazy-картинки, voice waveform, etc.).
  const newMsgArrivedRef = useRef(false);
  useEffect(() => {
    newMsgArrivedRef.current = true;
    const t = window.setTimeout(() => { newMsgArrivedRef.current = false; }, 250);
    return () => window.clearTimeout(t);
  }, [messagesQ.data?.length]);

  // [DISABLED 2026-05-18] Height-RO выключен по запросу. Virtuoso followOutput
  // с smooth+atBottomThreshold=200 сам дотягивает вниз когда нужно.
  // useEffect(() => { ResizeObserver для scrollHeight... }, [...]);

  // ── Voice recording ─────────────────────────────────────────
  async function startRecording() {
    if (recState !== "idle") return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Подбираем самый совместимый формат (Safari ≠ Chrome)
      const candidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg;codecs=opus",
      ];
      const mimeType = candidates.find(m => MediaRecorder.isTypeSupported(m)) || "";
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const chunks: Blob[] = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      const startedAt = Date.now();
      const timer = setInterval(() => {
        setRecElapsed(Math.floor((Date.now() - startedAt) / 1000));
      }, 250);
      recRef.current = { mr, chunks, stream, timer, startedAt };
      mr.start();
      setRecElapsed(0);
      setRecState("recording");
    } catch (err) {
      console.error("getUserMedia failed", err);
      alert("Не удалось получить доступ к микрофону. Проверьте разрешения в браузере.");
    }
  }
  function cancelRecording() {
    const r = recRef.current;
    if (!r) { setRecState("idle"); return; }
    if (r.timer) clearInterval(r.timer);
    try { r.mr.stop(); } catch { /* ignore */ }
    r.stream.getTracks().forEach(t => t.stop());
    recRef.current = null;
    setRecElapsed(0);
    setRecState("idle");
  }
  async function stopAndSendRecording() {
    const r = recRef.current;
    if (!r || !activeTopicId) return;
    if (r.timer) clearInterval(r.timer);
    // mr.stop() триггерит финальный ondataavailable + onstop, ловим Blob.
    const blob: Blob = await new Promise((resolve) => {
      r.mr.onstop = () => {
        const type = r.mr.mimeType || "audio/webm";
        resolve(new Blob(r.chunks, { type }));
      };
      try { r.mr.stop(); } catch { resolve(new Blob(r.chunks)); }
    });
    r.stream.getTracks().forEach(t => t.stop());
    recRef.current = null;
    setRecState("uploading");
    try {
      // Имя файла включает расширение по mimeType — backend ставит mime по contentType.
      const ext = (blob.type.includes("mp4") ? "mp4"
                : blob.type.includes("ogg") ? "ogg"
                : "webm");
      const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: blob.type });
      const att = await uploadChatFile(file);
      const recovery = createPendingSendRecovery({
        userId: user?.id ?? "",
        topicId: activeTopicId,
        text: "",
        replyTo,
        attachments: [att],
      });
      storePendingSend(recovery);
      sendMut.mutate({
        topicId: activeTopicId,
        text: "",
        replyToId: replyTo?.id ?? null,
        attachmentIds: [att.id],
        _replyTo: replyTo,
        _attachments: [att],
        _recoveryId: recovery.id,
        _composerText: "",
      });
    } catch (err) {
      console.error("voice upload failed", err);
      alert("Не удалось отправить голосовое сообщение");
    } finally {
      setRecElapsed(0);
      setRecState("idle");
    }
  }
  function pauseRecording() {
    const r = recRef.current;
    if (!r) return;
    try { r.mr.pause(); } catch { /* ignore */ }
    if (r.timer) { clearInterval(r.timer); r.timer = null; }
    setRecState("paused");
  }
  function resumeRecording() {
    const r = recRef.current;
    if (!r) return;
    try { r.mr.resume(); } catch { /* ignore */ }
    // Возобновляем таймер с поправкой на уже прошедшее время
    const baseElapsed = recElapsed;
    const resumedAt = Date.now();
    r.timer = setInterval(() => {
      setRecElapsed(baseElapsed + Math.floor((Date.now() - resumedAt) / 1000));
    }, 250);
    setRecState("recording");
  }

  // Дедуп: блокируем синхронные повторные submit'ы в одном event-tick'е
  // (двойной клик по Send / двойной Ctrl+Enter / двойной Enter). Сбрасывается
  // через microtask — следующий event loop тик может слать снова.
  const submittingRef = useRef(false);
  function dispatchSend(payload: SendPayload) {
    sendInFlightRef.current = true;
    // Таймаут отправки 20 с (см. sendAbortRef).
    if (sendAbortRef.current) window.clearTimeout(sendAbortRef.current.timer);
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => {
      ctrl.abort(new DOMException("нет ответа от сервера 20 с — проверьте связь и отправьте ещё раз", "AbortError"));
    }, 20000);
    sendAbortRef.current = { ctrl, startedAt: Date.now(), timer };
    const { topicId, text: composerText, replyTo: replySnapshot, files: filesSnapshot } = payload;
    const attachmentIds = filesSnapshot.map(file => file.id);
    const stored = user?.id ? readPendingSend(user.id, topicId) : null;
    const canReuseStored = !!stored
      && stored.text === composerText
      && (stored.replyTo?.id ?? null) === (replySnapshot?.id ?? null)
      && stored.attachments.length === filesSnapshot.length
      && stored.attachments.every((file, index) => file.id === filesSnapshot[index]?.id);
    const recovery = canReuseStored
      ? stored
      : createPendingSendRecovery({
          userId: user?.id ?? "",
          topicId,
          text: composerText,
          replyTo: replySnapshot,
          attachments: filesSnapshot,
        });
    storePendingSend(recovery);
    sendMut.mutate({
      topicId,
      text: composerText.trim(),
      replyToId: replySnapshot?.id ?? null,
      attachmentIds,
      _replyTo: replySnapshot,
      _attachments: filesSnapshot,
      _recoveryId: recovery.id,
      _composerText: composerText,
      _signal: ctrl.signal,
    });
  }
  dispatchSendRef.current = dispatchSend;

  function submit() {
    // Если активно редактирование — submit сохраняет изменение
    if (editingId !== null) {
      saveEdit();
      return;
    }
    if (submittingRef.current) return;
    const t = text.trim();
    // Снапшот аттачей и reply'я ДО mutate — иначе быстрые повторные submit'ы
    // могут увидеть несинхронизированный state и оптимистик соберёт пустоту.
    const filesSnapshot = pendingFiles;
    const replySnapshot = replyTo;
    // Плашка «Переслать» в этом чате: «Отправить» подтверждает пересылку.
    const fwd = forwardDraft && forwardDraft.targetTopicId === activeTopicId ? forwardDraft : null;
    // Отбрасываем заведомо пустые попытки (нет текста, файлов и пересылки).
    if ((!t && filesSnapshot.length === 0 && !fwd) || !activeTopicId) return;
    submittingRef.current = true;
    queueMicrotask(() => { submittingRef.current = false; });
    if (fwd) {
      setForwardDraft(null);
      // Без комментария — пересылаем сразу.
      if (!t && filesSnapshot.length === 0) { runForwardRef.current(fwd); return; }
      // С комментарием: сначала он, пересылка — сразу после его settle (sendMut.onSettled).
      pendingForwardRef.current = fwd;
    }
    const payload: SendPayload = { topicId: activeTopicId, text, replyTo: replySnapshot, files: filesSnapshot };
    // Композер очищаем сразу, не дожидаясь ответа сервера: владелец хочет
    // печатать следующее сообщение немедленно. При ошибке onError вернёт
    // текст/reply/вложения обратно (merge-гарды там на это рассчитаны).
    setText("");
    setReplyTo(null);
    const sentIds = new Set(filesSnapshot.map(file => file.id));
    setPendingFiles(current => current.filter(file => !sentIds.has(file.id)));
    // Предыдущее сообщение ещё летит: не шлём параллельно (порядок в ленте),
    // а кладём снимок в очередь — уйдёт сразу после settle (onSettled у sendMut).
    if (sendInFlightRef.current) { sendQueueRef.current.push(payload); return; }
    dispatchSend(payload);
  }

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    submit();
    // Курсор остаётся в поле ввода — можно сразу печатать следующее.
    textareaRef.current?.focus({ preventScroll: true });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      // На мобиле Enter = перенос строки. Отправка ТОЛЬКО по кнопке.
      // Детектим тач-устройства без точного указателя (mouse).
      const isTouchOnly = typeof window !== "undefined"
        && window.matchMedia("(pointer: coarse)").matches;
      if (isTouchOnly) return;  // даём дефолтный newline
      e.preventDefault();
      submit();
    } else if (e.key === "Escape") {
      if (replyTo) setReplyTo(null);
      else if (forwardDraft) setForwardDraft(null);
    }
  };

  // ── Загрузка файлов ─────────────────────────────────────────
  // Лимит 10 файлов на одно сообщение (TG также 10 в media-album).
  const MAX_ATTACHMENTS_PER_MESSAGE = 10;
  async function handleFiles(files: FileList | File[]) {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    // Cap: всего 10 файлов суммарно (uploading + pending + new)
    const current = uploadingFiles.length + pendingFiles.length;
    const room = Math.max(0, MAX_ATTACHMENTS_PER_MESSAGE - current);
    if (room === 0) {
      alert(`В одном сообщении максимум ${MAX_ATTACHMENTS_PER_MESSAGE} файлов`);
      return;
    }
    const accepted = arr.slice(0, room);
    // Модалку предпросмотра больше не открываем: превью (загрузка → готовые
    // плитки) показываются полосой .chat-pending-files прямо над строкой
    // ввода, подпись набирается в обычном поле. Фокус — сразу в поле.
    requestAnimationFrame(() => textareaRef.current?.focus({ preventScroll: true }));
    const tasks = accepted.map(f => uploadOneFileWithPreview(f));
    setIsUploading(true);
    try { await Promise.allSettled(tasks); }
    finally { setIsUploading(false); }
  }
  function cancelAllPending(): void {
    // Aborts uploading + clears ready files. Закрытие модалки.
    uploadingFiles.forEach(u => { try { u.abort(); } catch { /* noop */ } if (u.thumbUrl) URL.revokeObjectURL(u.thumbUrl); });
    setUploadingFiles([]);
    setPendingFiles([]);
    setComposerModalOpen(false);
  }
  function sendFromModal(): void {
    // Отправка через модалку: текст уже синхронизирован с `text` state, файлы — pendingFiles
    submit();
    setComposerModalOpen(false);
    requestAnimationFrame(() => textareaRef.current?.focus({ preventScroll: true }));
  }

  async function uploadOneFileWithPreview(rawFile: File) {
    const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const isImgOrig = (rawFile.type || "").startsWith("image/") || /\.(heic|heif)$/i.test(rawFile.name);
    const isVid = (rawFile.type || "").startsWith("video/");
    const abortCtrl = new AbortController();
    // Обычные браузерные форматы показываем мгновенно из локального blob URL,
    // не ждём resize/compress и тем более обратной загрузки с сервера. HEIC/HEIF
    // остаётся без blob-preview до конвертации: большинство браузеров его не
    // декодирует, после optimizeBeforeUpload подставится готовый JPEG.
    const isHeicOrig = /(?:image\/(?:heic|heif))|\.(?:heic|heif)$/i.test(
      `${rawFile.type} ${rawFile.name}`,
    );
    const initialThumbUrl = isVid || (isImgOrig && !isHeicOrig)
      ? URL.createObjectURL(rawFile)
      : null;
    setUploadingFiles(prev => [...prev, {
      tempId, file: rawFile, thumbUrl: initialThumbUrl,
      isImage: isImgOrig, isVideo: isVid,
      loaded: 0, total: rawFile.size,
      abort: () => abortCtrl.abort(),
    }]);

    // 1. Client-side оптимизация (HEIC→JPEG + resize+compress для крупных картинок)
    //    Видео не трогаем — bandwidth оправдан, бэк сам faststart'ит.
    let file = rawFile;
    try {
      file = await optimizeBeforeUpload(rawFile);
    } catch (err) {
      console.warn("optimizeBeforeUpload failed, sending original:", err);
    }
    const isImg = (file.type || "").startsWith("image/");
    // После оптимизации — обновляем preview thumbUrl (теперь это валидный JPEG/PNG)
    if (isImg && !initialThumbUrl) {
      const newThumb = URL.createObjectURL(file);
      setUploadingFiles(prev => prev.map(p => p.tempId === tempId ? { ...p, thumbUrl: newThumb, file, total: file.size } : p));
    } else if (file !== rawFile) {
      setUploadingFiles(prev => prev.map(p => p.tempId === tempId ? { ...p, file, total: file.size } : p));
    }

    try {
      const att = await uploadChatFileWithProgress(
        file,
        (loaded, total) => {
          setUploadingFiles(prev => prev.map(p => p.tempId === tempId ? { ...p, loaded, total } : p));
        },
        abortCtrl.signal,
      );
      // Готово — убираем из uploading, добавляем в pending
      setUploadingFiles(prev => {
        const found = prev.find(p => p.tempId === tempId);
        if (found?.thumbUrl) URL.revokeObjectURL(found.thumbUrl);
        return prev.filter(p => p.tempId !== tempId);
      });
      setPendingFiles(prev => [...prev, att]);
    } catch (err: unknown) {
      if (!(err instanceof DOMException && err.name === "AbortError")) {
        console.error("upload failed", file.name, err);
        const msg = err instanceof Error ? err.message : String(err);
        let detail = "";
        if (/HTTP 413/.test(msg)) detail = " — файл слишком большой (>100 МБ)";
        else if (/HTTP 504|HTTP 502/.test(msg)) detail = " — сервер не успел обработать (попробуйте ещё раз или загрузите файл поменьше)";
        else if (/Network error/.test(msg)) detail = " — пропала сеть, попробуйте ещё раз";
        else if (/HTTP /.test(msg)) detail = ` — ${msg.replace(/^Error:\s*/, "")}`;
        alert(`Не удалось загрузить «${file.name}»${detail}`);
      }
      setUploadingFiles(prev => {
        const found = prev.find(p => p.tempId === tempId);
        if (found?.thumbUrl) URL.revokeObjectURL(found.thumbUrl);
        return prev.filter(p => p.tempId !== tempId);
      });
    }
  }
  function cancelUpload(tempId: string) {
    setUploadingFiles(prev => {
      const found = prev.find(p => p.tempId === tempId);
      if (found) found.abort();
      if (found?.thumbUrl) URL.revokeObjectURL(found.thumbUrl);
      return prev.filter(p => p.tempId !== tempId);
    });
  }

  function removePendingFile(id: number) {
    setPendingFiles(prev => prev.filter(f => f.id !== id));
  }

  // Document-level paste — ловим Ctrl+V с файлами вне зависимости от
  // фокуса (включая caption-textarea внутри ImageUploadModal).
  // handleFilesRef — чтобы listener читал свежую версию handleFiles.
  const handleFilesRef = useRef(handleFiles);
  useEffect(() => { handleFilesRef.current = handleFiles; });
  useEffect(() => {
    const onDocPaste = (e: globalThis.ClipboardEvent) => {
      if (!e.clipboardData) return;
      const files: File[] = [];
      for (const item of Array.from(e.clipboardData.items)) {
        if (item.kind === "file") {
          const f = item.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        void handleFilesRef.current(files);
      }
    };
    document.addEventListener("paste", onDocPaste);
    return () => document.removeEventListener("paste", onDocPaste);
  }, []);

  // Native contextmenu handler — открываем наше меню для сообщения. Тройной
  // подход: CAPTURE phase contextmenu + BUBBLE phase contextmenu + mousedown
  // fallback (button === 2). Один из них точно сработает, что бы ни мешало.
  useEffect(() => {
    function tryOpenForTarget(tgt: HTMLElement | null, x: number, y: number, quote?: string): boolean {
      if (!tgt) return false;
      if (tgt.closest(".media-lightbox, .video-lightbox, .img-upload-modal")) return false;
      if (!tgt.closest(".chat-shell")) return false;
      if (tgt.closest(".chat-context-backdrop")) return false;
      const bubble = tgt.closest(".chat-bubble") as HTMLElement | null;
      if (!bubble) return false;
      const msgEl = bubble.closest("[id^='chat-msg-']") as HTMLElement | null;
      if (!msgEl) return false;
      const id = Number(msgEl.id.slice("chat-msg-".length));
      if (!Number.isFinite(id) || id < 0) return false;
      openContextMenu(id, x, y, undefined, quote);
      return true;
    }
    // Если юзер выделил кусок текста внутри bubble и right-click'нул —
    // отдаём нативное браузерное меню (там native Copy для выделения).
    // Наше меню копирует ВЕСЬ message.text, что для частичного выделения
    // нежелательно.
    const hasTextSelectionInChat = (): boolean => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) return false;
      const range = sel.getRangeAt(0);
      const node = range.commonAncestorContainer;
      const el = (node.nodeType === 1 ? node as HTMLElement : node.parentElement) as HTMLElement | null;
      if (!el) return false;
      return !!el.closest(".chat-bubble, .chat-bubble-text, .chat-bubble-reply-text, .chat-voice-text");
    };
    void hasTextSelectionInChat;
    // Захватываем выделение СИНХРОННО в момент contextmenu event —
    // это гарантированный момент когда selection ещё активна.
    const grabQuote = (): string | undefined => {
      try {
        const sel = window.getSelection();
        if (sel && !sel.isCollapsed && sel.rangeCount > 0) {
          const t = sel.toString().trim();
          if (t) return t.slice(0, 500);
        }
      } catch { /* ignore */ }
      // Fallback на ref, если браузер уже успел очистить
      const r = lastSelectionTextRef.current;
      return r ? r.slice(0, 500) : undefined;
    };
    const ctxHandler = (e: globalThis.MouseEvent) => {
      const tgt = e.target as HTMLElement | null;
      const isInput = !!tgt && (
        tgt.tagName === "TEXTAREA" ||
        tgt.tagName === "INPUT" ||
        (tgt as HTMLElement).isContentEditable
      );
      // Захватываем цитату ДО preventDefault (на случай если он клирит selection)
      const quote = isInput ? undefined : grabQuote();
      if (tgt && tgt.closest(".chat-shell") && !tgt.closest(".media-lightbox, .video-lightbox, .img-upload-modal") && !isInput) {
        e.preventDefault();
      }
      if (!isInput) tryOpenForTarget(tgt, e.clientX, e.clientY, quote);
    };
    const mouseDownHandler = (e: globalThis.MouseEvent) => {
      // Fallback: правая кнопка через mousedown (если contextmenu кем-то блокируется)
      if (e.button !== 2) return;
      const tgt = e.target as HTMLElement | null;
      // НЕ перехватываем в полях ввода — пусть браузер показывает нативное меню
      const isInput = !!tgt && (
        tgt.tagName === "TEXTAREA" ||
        tgt.tagName === "INPUT" ||
        (tgt as HTMLElement).isContentEditable
      );
      if (isInput) return;
      const quote = grabQuote();
      tryOpenForTarget(tgt, e.clientX, e.clientY, quote);
    };
    document.addEventListener("contextmenu", ctxHandler, true);  // capture
    document.addEventListener("contextmenu", ctxHandler);         // bubble
    document.addEventListener("mousedown", mouseDownHandler, true); // capture mousedown fallback
    return () => {
      document.removeEventListener("contextmenu", ctxHandler, true);
      document.removeEventListener("contextmenu", ctxHandler);
      document.removeEventListener("mousedown", mouseDownHandler, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drag-n-drop файлов — работает на всём chat-shell.
  // Window-уровень блокирует браузерный default (открытие файла как страницы)
  // если юзер промахнулся мимо drop-зоны.
  useEffect(() => {
    const guard = (e: globalThis.DragEvent) => {
      if (e.dataTransfer?.types?.includes("Files")) e.preventDefault();
    };
    window.addEventListener("dragover", guard);
    window.addEventListener("drop", guard);
    return () => {
      window.removeEventListener("dragover", guard);
      window.removeEventListener("drop", guard);
    };
  }, []);

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (e.dataTransfer.types.includes("Files")) {
      e.preventDefault();
      setIsDropping(true);
    }
  };
  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    // currentTarget — chat-shell, target — внутренний элемент. Снимаем
    // подсветку только когда курсор реально вышел из shell'а
    if (e.currentTarget === e.target) setIsDropping(false);
  };
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDropping(false);
    if (!activeTopicId) {
      alert("Выберите чат, чтобы прикрепить файл");
      return;
    }
    if (e.dataTransfer.files.length > 0) {
      void handleFiles(e.dataTransfer.files);
    }
  };

  // Paste image из буфера — теперь обрабатывается document-level listener выше
  // (чтобы работал в любом textarea, включая caption-textarea модалки).

  // ── Edit / Delete handlers ──────────────────────────────────
  function startEdit(m: ChatMessage) {
    setEditingId(m.id);
    setEditingText(m.text);
  }
  function cancelEdit() {
    setEditingId(null);
    setEditingText("");
  }
  function saveEdit() {
    if (!editingId) return;
    const t = editingText.trim();
    if (!t) return;
    // Всегда вызываем API даже без видимых изменений — иначе предикат
    // "no change → cancel" иногда ложно срабатывает (whitespace/cache),
    // и юзер думает что ничего не сохранилось.
    editMut.mutate({ id: editingId, text: t }, {
      onSuccess: () => cancelEdit(),
    });
  }
  const startReply = useCallback((m: ChatMessage, quote?: string) => {
    // Reply во время редактирования — сначала отменяем edit, чтобы reply
    // адресовался текущему compose'у а не сохранял старое сообщение
    if (editingId !== null) {
      setEditingId(null);
      setEditingText("");
    }
    // Превью attachment: голосовое → "🎙 0:15", иначе "файл"
    let preview = m.text || "";
    if (!preview && m.attachments?.length) {
      const a = m.attachments[0];
      const mime = (a.mime || "").toLowerCase();
      if (mime.startsWith("audio/")) {
        const d = Math.floor(a.duration_sec || 0);
        preview = `🎙 ${Math.floor(d / 60)}:${String(d % 60).padStart(2, "0")}`;
      } else if (mime.startsWith("image/")) {
        preview = `🖼 ${a.filename}`;
      } else {
        preview = `📎 ${a.filename}`;
      }
    }
    // Если передан quote — обрезаем до 500 символов и сохраняем в reply.
    // Backend проверяет max_length=500 в схеме MessageCreate.reply_quote.
    const cleanQuote = quote ? quote.trim().slice(0, 500) : null;
    // Превью в плашке ответа: все не-голосовые вложения (фото / видео / файлы),
    // крупными плитками над названием. Голосовые остаются текстом «🎙 0:15».
    const previewAtts = (m.attachments || [])
      .filter(att => !(att.mime || "").toLowerCase().startsWith("audio/"))
      .slice(0, 10)
      .map(att => ({ id: att.id, mime: att.mime, filename: att.filename, size_bytes: att.size_bytes }));
    if (!m.text && previewAtts.length > 1) {
      const allImg = previewAtts.every(att => isImage(att.mime, att.filename));
      preview = allImg
        ? `${previewAtts.length} фото`
        : `${previewAtts.length} ${pluralRu(previewAtts.length, ["вложение", "вложения", "вложений"])}`;
    }
    setReplyTo({
      id: m.id,
      author_name: m.author_name,
      preview: preview.slice(0, 120),
      deleted: false,
      quote: cleanQuote,
      attachments: previewAtts,
    });
    // Сразу фокусируем textarea — чтобы пользователь не кликал по ней отдельно.
    // preventScroll: браузер НЕ прокручивает контейнер к полю при фокусе
    // (иначе на реплае лента могла дёрнуться к низу).
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (ta) {
        ta.focus({ preventScroll: true });
        const pos = ta.value.length;
        ta.setSelectionRange(pos, pos);
      }
    });
  }, [editingId]);
  function confirmDelete(id: number) {
    if (!confirm("Удалить это сообщение?")) return;
    deleteMut.mutate(id);
  }

  useEffect(() => {
    setProfileDrawerStack([]);
    setProfileDrawerOpen(false);
  }, [activeTopicId]);

  // Форматтер last-seen для DM-партнёра. Использует minuteTick чтобы
  // re-evaluate каждую минуту без новых данных.
  function formatDmLastSeen(iso: string | null | undefined): string {
    void minuteTick;
    if (!iso) return "был в сети давно";
    // Backend отдаёт naive UTC ("YYYY-MM-DD HH:MM:SS.fff" без 'Z') — без явной
    // 'Z' браузер парсит строку как LOCAL time, и в Москве (UTC+3) diff
    // всегда +3 часа: «был в сети 3 часа назад» у всех. Добавляем 'Z'.
    const isoZ = iso.endsWith("Z") ? iso : (iso.includes("T") ? iso + "Z" : iso.replace(" ", "T") + "Z");
    const t = new Date(isoZ).getTime();
    // Используем тот же 75s порог что и isOnline() — иначе в окне 60-75с
    // кружок зелёный, а текст уже «был в сети 1 минуту назад».
    if (isOnline(iso)) return "в сети";
    const diffMin = Math.floor((Date.now() - t) / 60_000);
    if (diffMin < 60) return `был в сети ${diffMin} ${pluralRu(diffMin, ["минуту", "минуты", "минут"])} назад`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `был в сети ${diffH} ${pluralRu(diffH, ["час", "часа", "часов"])} назад`;
    const diffD = Math.floor(diffH / 24);
    return `был в сети ${diffD} ${pluralRu(diffD, ["день", "дня", "дней"])} назад`;
  }
  function pluralRu(n: number, forms: [string, string, string]): string {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return forms[0];
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
    return forms[2];
  }
  function formatGroupSubtitle(stats: { members_count: number; online_count: number } | null | undefined, msgCount: number): string {
    if (!stats) {
      // stats ещё не загрузились или query упал — fallback
      return `${msgCount} ${msgCount === 1 ? "сообщение" : "сообщений"}`;
    }
    const total = Number(stats.members_count) || 0;
    const online = Number(stats.online_count) || 0;
    if (total === 0) {
      return `${msgCount} ${msgCount === 1 ? "сообщение" : "сообщений"}`;
    }
    const totalLabel = `${total} ${pluralRu(total, ["участник", "участника", "участников"])}`;
    if (online > 0) return `${totalLabel}, ${online} онлайн`;
    return totalLabel;
  }

  const jumpToMessage = useCallback((id: number, topicId?: number) => {
    // Highlight target после scroll
    const triggerHighlight = () => {
      if (jumpHighlightTimerRef.current) window.clearTimeout(jumpHighlightTimerRef.current);
      setJumpTargetId(id);
      jumpHighlightTimerRef.current = window.setTimeout(() => {
        jumpHighlightTimerRef.current = null;
        setJumpTargetId(current => (current === id ? null : current));
      }, 2500);
    };

    // Найти scroller-контейнер Virtuoso (нативный div с overflow:auto)
    const findScroller = (): HTMLElement | null => {
      const wrap = document.querySelector(".chat-feed-wrap");
      if (!wrap) return null;
      const all = wrap.querySelectorAll("div");
      for (const div of Array.from(all)) {
        const cs = window.getComputedStyle(div);
        if ((cs.overflowY === "auto" || cs.overflowY === "scroll") && div.scrollHeight > div.clientHeight) {
          return div as HTMLElement;
        }
      }
      return null;
    };

    // IN-RANGE: target уже в текущем кэше messages.
    //   1) Пропорциональный scrollTop-прыжок (грубо к позиции)
    //   2) directedSeek: если target ещё не в DOM — крутим scrollTop в нужную
    //      сторону пока не увидим target-DOM-ноду
    //   3) Один scrollIntoView + highlight (БЕЗ повторов через RAF/setTimeout —
    //      именно они были источником «дёргается конкретно»).
    let retried = false;   // одна повторная доводка, если узел выгрузился после финала
    const targetTopicIdAtStart = activeTopicIdRef.current;
    const doInRange = (): boolean => {
      const idx = messagesRef.current.findIndex(m => m.id === id);
      const total = messagesRef.current.length;
      if (idx < 0) return false;

      const scroller = findScroller();
      if (!scroller) {
        curtainOff();
        triggerHighlight();
        return true;
      }

      // Цель УЖЕ отрендерена в DOM (в пределах overscan Virtuoso) — НЕ телепортим
      // scrollTop, а плавно скроллим прямо к ней. scrollIntoView сам выбирает
      // направление: цель выше вьюпорта → скролл ВВЕРХ, ниже → ВНИЗ. Это и есть
      // органичная анимация в естественную сторону. Раньше безусловный телепорт
      // (ниже) + короткий доскролл всегда давал одинаковое «снизу» направление.
      // После плавного центрирования Virtuoso может домерить высоты соседей и
      // сдвинуть ленту — цель уедет с экрана. Один раз проверяем и поправляем
      // мгновенно, только если цель действительно не видна (иначе не трогаем).
      const ensureVisibleLater = () => {
        window.setTimeout(() => {
          if (targetTopicIdAtStart !== activeTopicIdRef.current) return;
          const el2 = document.getElementById(`chat-msg-${id}`);
          if (!el2) {
            // Узел выгрузился (Virtuoso сдвинул ленту при домере) — одна повторная доводка.
            if (!retried) { retried = true; doInRange(); }
            return;
          }
          const r = el2.getBoundingClientRect();
          if (r.bottom <= 0 || r.top >= window.innerHeight) {
            el2.scrollIntoView({ behavior: "auto", block: "center" });
            triggerHighlight();
          }
        }, 800);
      };
      // Плавно едем только последний экран: если цель дальше 2 экранов —
      // сначала мгновенно подтягиваемся до 1 экрана от неё.
      const glideTo = (el: HTMLElement) => {
        const r = el.getBoundingClientRect();
        const dist = (r.top + r.bottom) / 2 - window.innerHeight / 2;
        const vh = scroller.clientHeight;
        if (Math.abs(dist) > 2 * vh) {
          scroller.scrollTop += dist - Math.sign(dist) * vh;
        }
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      };
      // Прибытие: плавный доезд последнего экрана; шторку (если была) снимаем,
      // когда цель встала по центру ленты или лента замерла (потолок 900 мс),
      // и в этот же момент включаем подсветку — одно проявление на месте.
      const arrive = (el: HTMLElement) => {
        glideTo(el);
        ensureVisibleLater();
        const t0 = performance.now();
        let lastTop = -1, idle = 0;
        const tick = () => {
          if (targetTopicIdAtStart !== activeTopicIdRef.current) { curtainOff(); return; }
          const e2 = document.getElementById(`chat-msg-${id}`);
          const st = scroller.scrollTop;
          idle = st === lastTop ? idle + 1 : 0;
          lastTop = st;
          let centered = false;
          if (e2) {
            const r = e2.getBoundingClientRect();
            const sr = scroller.getBoundingClientRect();
            centered = Math.abs((r.top + r.bottom) / 2 - (sr.top + sr.bottom) / 2) < 24;
          }
          const elapsed = performance.now() - t0;
          if (centered || (idle >= 6 && elapsed > 250) || elapsed > 1500) {
            // Не по центру (лента замерла раньше или вышло время) — доставим
            // мгновенно, пока шторка ещё закрыта: проявление всегда на цели.
            if (!centered && e2) e2.scrollIntoView({ behavior: "auto", block: "center" });
            curtainOff();
            triggerHighlight();
            return;
          }
          requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      };
      const already = document.getElementById(`chat-msg-${id}`);
      if (already) {
        arrive(already);
        return true;
      }
      // Цель не в DOM — впереди телепорт и доводка: прикрываем ленту.
      curtainOn();

      // Грубый прыжок (цель вне DOM — далеко за overscan)
      const approxPos = (idx / Math.max(1, total - 1)) * (scroller.scrollHeight - scroller.clientHeight);
      scroller.scrollTop = Math.max(0, approxPos - scroller.clientHeight / 3);

      const targetTopicId = activeTopicIdRef.current;
      let seeks = 0;
      const MAX_SEEKS = 40;
      let stuck = 0;   // тики подряд без сдвига scrollTop
      const step = () => {
        if (targetTopicId !== activeTopicIdRef.current) { curtainOff(); return; }
        const el = document.getElementById(`chat-msg-${id}`);
        if (el) {
          // Финальное центрирование — плавно, под шторкой до самой центровки.
          arrive(el);
          return;
        }
        if (seeks++ >= MAX_SEEKS) { curtainOff(); triggerHighlight(); return; }

        // Что видно в DOM
        const visibleEls = document.querySelectorAll<HTMLElement>('[id^="chat-msg-"]');
        let minId = Infinity, maxId = -Infinity;
        for (const vel of Array.from(visibleEls)) {
          const elId = parseInt(vel.id.substring("chat-msg-".length), 10);
          if (!Number.isNaN(elId)) {
            if (elId < minId) minId = elId;
            if (elId > maxId) maxId = elId;
          }
        }
        const before = scroller.scrollTop;
        const stepSize = scroller.clientHeight * 0.6;
        if (!Number.isFinite(minId)) {
          scroller.scrollTop = before + stepSize;
        } else if (id < minId) {
          scroller.scrollTop = Math.max(0, before - stepSize);
        } else if (id > maxId) {
          scroller.scrollTop = before + stepSize;
        }
        if (scroller.scrollTop === before) {
          // Не сдвинулись: край ленты — или DOM ещё не обновился после
          // телепорта. Раньше сдавались сразу; теперь терпим до 5 тиков.
          if (++stuck >= 5) { curtainOff(); triggerHighlight(); return; }
        } else {
          stuck = 0;
        }
        setTimeout(step, 60);   // было 100: та же ползучка, вдвое быстрее
      };
      setTimeout(step, 120);
      return true;
    };

    // OUT-OF-RANGE: пагинируем чат вверх ИМПЕРАТИВНО через loadMoreOlder
    // пока target не загрузится, потом DOM scrollIntoView (один раз).
    const doOutOfRange = async () => {
      const _pinTopicId = activeTopicIdRef.current;
      if (!_pinTopicId) return;
      resetFirstItemIndex();
      setPinJumpActive(_pinTopicId, true);
      curtainOn();

      // После переключения топика (поиск в другой чат) базовые сообщения ещё
      // грузятся — ждём, иначе loadMoreOlder no-op'ит на пустом кэше и цикл
      // рвётся «before===after». До 4с.
      let waitedBase = 0;
      while (((qc.getQueryData<ChatMessage[]>(["chat/messages", _pinTopicId]) ?? []).length === 0)
             && waitedBase < 4000 && _pinTopicId === activeTopicIdRef.current) {
        await new Promise(r => setTimeout(r, 100));
        waitedBase += 100;
      }
      if (_pinTopicId !== activeTopicIdRef.current) { curtainOff(); return; }

      const MAX_PAGINATIONS = 40;
      let paginations = 0;

      const isFound = () => {
        const cached = qc.getQueryData<ChatMessage[]>(["chat/messages", _pinTopicId]) ?? [];
        return cached.some(m => m.id === id);
      };

      while (!isFound() && paginations < MAX_PAGINATIONS) {
        const before = (qc.getQueryData<ChatMessage[]>(["chat/messages", _pinTopicId]) ?? []).length;
        await loadMoreOlder(JUMP_HISTORY_PAGE);
        await new Promise(r => setTimeout(r, 60));
        const after = (qc.getQueryData<ChatMessage[]>(["chat/messages", _pinTopicId]) ?? []).length;
        paginations++;
        if (after === before) break;
      }

      if (!isFound()) {
        setPinJumpActive(_pinTopicId, false);
        curtainOff();
        return;
      }

      // Сообщение в кэше. Твоя идея (и она работает): подождать чтобы
      // React закоммитил новые messages, ref обновился, Virtuoso принял
      // новый data — и потом просто вызвать doInRange, тот самый путь
      // который работает на «второй тап». Никаких лишних scrollToIndex
      // прыжков далеко от viewport (они как раз и ломают позиционирование).
      setTimeout(() => {
        if (_pinTopicId !== activeTopicIdRef.current) { curtainOff(); return; }
        doInRange();
        setTimeout(() => setPinJumpActive(_pinTopicId, false), 1500);
      }, 600);
    };

    // Switch topic если надо (сравнение со СВЕЖИМ ref — не stale-замыкание)
    if (topicId != null && topicId !== activeTopicIdRef.current) {
      setActiveTopicId(topicId);
      setMobileView("messages");
      // Ждём пока новый топик загрузится, потом пытаемся
      const tryAfterTopicSwitch = (retries: number) => {
        if (doInRange()) return;
        if (retries > 0) {
          window.setTimeout(() => tryAfterTopicSwitch(retries - 1), 200);
        } else {
          doOutOfRange();
        }
      };
      window.setTimeout(() => tryAfterTopicSwitch(10), 300);
      return;
    }

    // Same topic
    if (doInRange()) {
      return;
    }
    doOutOfRange();
  }, [activeTopicId, firstItemIndex, resetFirstItemIndex, qc, showDeleted, loadMoreOlder, curtainOn, curtainOff]);
  // Открыть профиль юзера в новом drawer. Используем user?.id вместо myUserId
  // (myUserId объявлен ниже — иначе TDZ).
  const openUserProfile = useCallback(async (userId: string) => {
    if (!userId) return;
    try {
      const meId = user?.id;
      let display: string | null = null;
      let avatar: string | null = null;
      if (userId === meId) {
        display = user?.display_name || "Я";
        avatar = user?.avatar_filename || null;
      } else {
        // Просто тянем профиль — НЕ создаём DM упреждающе
        try {
          const p = await fetchUserPublic(userId);
          display = p.display_name;
          avatar = p.avatar_filename;
        } catch (e) {
          console.warn("fetchUserPublic failed", e);
        }
      }
      // Ищем существующий DM в списке топиков (cache) ИЛИ через backend find
      let dmTopicId: number | null = null;
      const existingDm = (topicsQ.data ?? []).find(
        (top) => top.kind === "dm" && top.dm_partner?.user_id === userId
      );
      if (existingDm) dmTopicId = existingDm.id;
      else {
        try {
          const r = await findDm(userId);
          dmTopicId = r.topic_id;
        } catch (e) { console.warn("findDm failed", e); }
      }
      const nextTopic: ChatTopic = dmTopicId
        ? {
            id: dmTopicId, slug: `dm-${userId}`, name: display || "Профиль",
            icon: null, sort_order: 0, last_message: null, unread_count: 0,
            kind: "dm",
            dm_partner: { user_id: userId, display_name: display, avatar_filename: avatar },
          }
        : {
            id: -1, slug: `user-${userId}`, name: display || "Профиль",
            icon: null, sort_order: 0, last_message: null, unread_count: 0,
            kind: "dm",
            dm_partner: { user_id: userId, display_name: display, avatar_filename: avatar },
          };
      setProfileDrawerStack((prev) => [...prev, nextTopic]);
      setProfileDrawerOpen(true);
    } catch (e) {
      console.error("openUserProfile failed", e);
    }
  }, [user]);
  const onToggleSelect = useCallback((id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  // TG-style explode: если на момент клика выделено ЦЕЛОЕ сообщение
  // (msgId in selectedIds), снятие чека с одного аттача "разваливает"
  // выделение — msgId уходит из selectedIds, а ВСЕ остальные аттачи
  // того же сообщения добавляются индивидуально в selectedAttachmentIds.
  // Обычное поведение (если whole-msg НЕ выделено) — простой toggle.
  const onToggleAttachmentSelect = useCallback(
    (msgId: number, attId: number, allAttIds: number[]) => {
      setSelectedIds(prevIds => {
        const wholeSelected = prevIds.has(msgId);
        setSelectedAttachmentIds(prevAtt => {
          const next = new Set(prevAtt);
          if (wholeSelected) {
            // Explode: добавляем все ОСТАЛЬНЫЕ аттачи (тот, по которому
            // кликнули — снимаем = не добавляем)
            for (const id of allAttIds) {
              if (id !== attId) next.add(id);
            }
          } else {
            if (next.has(attId)) next.delete(attId);
            else next.add(attId);
          }
          return next;
        });
        if (wholeSelected) {
          const next = new Set(prevIds);
          next.delete(msgId);
          return next;
        }
        return prevIds;
      });
    },
    [],
  );
  // Total selected (msgs + attachments) — для счётчика в selection-bar
  const totalSelectedCount = selectedIds.size + selectedAttachmentIds.size;
  // Reply-banner click: jump к оригиналу (с подсветкой)
  // Делегируем в jumpToMessage — там retry + on-demand 5000-fetch
  // fallback для target вне loaded ленты. Раньше тут был
  // getElementById+scrollIntoView который молча отказывал для сообщений
  // за пределами Virtuoso viewport.
  const jumpToReply = useCallback((id: number) => {
    jumpToMessage(id);
  }, [jumpToMessage]);

  // Клик по inline-кнопке бота: текст в чате = label (читабельно),
  // скрытая команда уходит в meta.bot_command (как TG callback_data).
  // Backend autoreply hook читает meta.bot_command вместо text.
  const onBotButtonClick = useCallback((command: string, label: string) => {
    if (!activeTopicId || !command) return;
    sendMut.mutate({
      topicId: activeTopicId,
      text: label || command,
      replyToId: null,
      attachmentIds: [],
      meta: { bot_command: command },
    });
  }, [activeTopicId, sendMut]);

  function togglePin(m: ChatMessage) {
    pinMut.mutate({ id: m.id, pin: !m.pinned_at });
  }

  // Reactions burst (TG-style overlay при ДОБАВЛЕНИИ своей реакции) — извлечён
  // в useReactionBurst hook (P2#10e). См. hooks/useReactionBurst.ts.
  const { burst } = useReactionBurst({
    activeTopicId,
    qc,
  });

  function pickReaction(messageId: number, emoji: string) {
    // burst-анимация убрана по запросу
    reactMut.mutate({ messageId, emoji });
    setReactionPicker(null);
  }
  // Стабильный callback для ChatMsg — toggle reaction по клику на чип
  const reactToggleByClick = useCallback((messageId: number, emoji: string) => {
    // burst-анимация убрана
    reactMut.mutate({ messageId, emoji });
  }, [reactMut]);

  /** Открыть контекстное меню сообщения. TG-style позиционирование:
   *   • маленькое сообщение (помещается на экране) → меню СНИЗУ bubble,
   *     emoji-pill СВЕРХУ bubble (как в TG)
   *   • большое сообщение (выходит за экран) → меню справа от пальца
   *     (~5px от точки тапа), emoji-pill сверху видимой части
   *
   *  Поскольку wrap кладёт pill+menu в один column-flex с gap, мы можем
   *  позиционировать только сам wrap. Стратегия:
   *    A) Если bubble вмещается на экране → top = bubble.top - PILL_H,
   *       left = bubble.left. Pill окажется поверх bubble.top, меню под ним.
   *    B) Если bubble выходит за экран → top около точки тапа (но не ниже,
   *       чем PILL_H от верха), left ~ x + 5px (или x - MENU_W - 5px если
   *       не помещается справа).
   */
  const openContextMenu = useCallback((messageId: number, x: number, y: number, attachmentId?: number, explicitQuote?: string) => {
    document.dispatchEvent(new CustomEvent("kb-close-ctx", { detail: { except: "bubble" } }));
    // Захватываем выделение. Двойной подход:
    //   1) window.getSelection() прямо сейчас — если ещё живо
    //   2) lastSelectionTextRef — последнее непустое выделение (обновляется
    //      selectionchange event'ом ДО contextmenu event'a)
    // Второй нужен потому что браузеры могут очистить selection при
    // e.preventDefault() на contextmenu (Chrome иногда), а на iOS long-press
    // селекция схлопывается когда native tooltip выезжает.
    let capturedQuote: string | undefined = explicitQuote;
    try {
      if (!capturedQuote) {
        const sel = window.getSelection();
        if (sel && !sel.isCollapsed && sel.rangeCount > 0) {
          const txt = sel.toString().trim();
          if (txt) capturedQuote = txt.slice(0, 500);
        }
      }
      if (!capturedQuote && lastSelectionTextRef.current) {
        capturedQuote = lastSelectionTextRef.current.slice(0, 500);
      }
      // НЕ очищаем ref здесь — openContextMenu вызывается ДВАЖДЫ на один
      // right-click (capture-phase document handler + React onContextMenu),
      // второй вызов найдёт пустой ref и перезапишет state с quote=undefined.
      // Ref живёт до следующей селекции (selectionchange заменит) или до
      // закрытия меню (setMessengerCtxMenu(null) → чистим там).
    } catch { /* ignore selection errors */ }
    if (isMessengerRoute) {
      setMessengerCtxMenu({ messageId, x, y, attachmentId, quote: capturedQuote });
      return;
    }
    void x;
    // Размеры — синхронизированы с CSS (mobile vs PC варианты)
    const PILL_W_MOBILE_SMALL = 280;     // 8 × 32 + 16 padding (vw ≤ 380)
    const PILL_W_MOBILE = 332;            // 8 × 36 + 16 padding (mobile default)
    const PILL_W_PC = 316;                // 9 × 32 emoji + 8×2 gap + chevron 2 + 10 padding
    const MENU_W_MOBILE = 260;            // mobile (max-width)
    const MENU_W_PC = 220;                // PC (max-width = 220 в CSS)
    const PILL_H_PC = 40;                 // PC pill: 32 emoji + 4×2 padding
    const PILL_H_MOBILE = 48;
    const MENU_H_EST = 320;
    const MENU_H_MIN = 140;
    const GAP = 5;          // 5px от bubble/клика
    const EDGE = 30;        // 30px от края экрана (порог флипа)
    const FLIP_MARGIN = 50; // 50px от края при флипе

    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const isMobile = vw < 900;
    const PILL_W = isMobile
      ? (vw <= 380 ? PILL_W_MOBILE_SMALL : PILL_W_MOBILE)
      : PILL_W_PC;
    const MENU_W = isMobile ? MENU_W_MOBILE : MENU_W_PC;
    const PILL_H_EST = isMobile ? PILL_H_MOBILE : PILL_H_PC;

    const headerEl = document.querySelector(".chat-main .chat-header") as HTMLElement | null;
    const headerBottom = headerEl ? headerEl.getBoundingClientRect().bottom : 0;
    const composerEl = document.querySelector(".chat-composer, .chat-form") as HTMLElement | null;
    const composerTop = composerEl ? composerEl.getBoundingClientRect().top : vh;

    // Доступная вертикальная зона: [TOP, BOTTOM]
    const TOP = headerBottom + EDGE;
    const BOTTOM = composerTop - EDGE;
    const usableH = Math.max(0, BOTTOM - TOP);

    const bubbleEl = document.getElementById(`chat-msg-${messageId}`)?.querySelector(".chat-bubble") as HTMLElement | null;

    if (!bubbleEl) {
      const pillTop = Math.max(TOP, y - PILL_H_EST - GAP);
      const menuTop = Math.min(BOTTOM - MENU_H_EST, y + GAP);
      const pillLeft = Math.max(EDGE, Math.min(vw - PILL_W - EDGE, x));
      const menuLeft = Math.max(EDGE, Math.min(vw - MENU_W - EDGE, x));
      setContextMenu({ messageId, attachmentId, pillTop, pillLeft, menuTop, menuLeft, liftOffset: 0, menuMaxHeight: MENU_H_EST, quote: capturedQuote });
      return;
    }

    const r = bubbleEl.getBoundingClientRect();
    const bubbleH = r.height;

    let liftOffset = 0;
    let menuMaxHeight = MENU_H_EST;
    let pillTop: number, pillLeft: number, menuTop: number, menuLeft: number;

    if (isMobile) {
      // ── МОБИЛЬНЫЙ: bubble лифтуется, pill сверху, menu снизу ──
      // Юзер согласен что меню МОЖЕТ наезжать на bubble на больших сообщениях
      // (видео/много фото) — лучше чем cut-off менюшки.
      const wantedTotal = PILL_H_EST + GAP + bubbleH + GAP + MENU_H_EST;
      if (wantedTotal <= usableH) {
        // Всё помещается — центрируем bubble в свободном пространстве
        const slack = usableH - wantedTotal;
        const targetBubbleTop = TOP + PILL_H_EST + GAP + slack / 2;
        liftOffset = targetBubbleTop - r.top;
        menuMaxHeight = MENU_H_EST;
        // Не толкаем bubble вниз — только вверх
        if (r.top < targetBubbleTop && r.bottom < BOTTOM) liftOffset = 0;
      } else {
        // Не помещается — bubble к верху доступной зоны, menu занимает максимум места
        // Позволяем menu НАЕЗЖАТЬ на bubble (юзер так просил)
        const targetBubbleTop = TOP + PILL_H_EST + GAP;
        liftOffset = targetBubbleTop - r.top;
        // Меню — максимально высокое, но не больше "useableH - pill - gap*2"
        menuMaxHeight = Math.min(MENU_H_EST, usableH - PILL_H_EST - GAP - GAP);
        if (menuMaxHeight < MENU_H_MIN) menuMaxHeight = MENU_H_MIN;
      }
      if (liftOffset > 0) liftOffset = 0;

      const liftedTop = r.top + liftOffset;
      const liftedBottom = r.bottom + liftOffset;

      pillTop = liftedTop - GAP - PILL_H_EST;
      if (pillTop < TOP) pillTop = TOP;
      pillLeft = r.left;
      if (pillLeft < EDGE) pillLeft = EDGE;
      if (pillLeft + PILL_W > vw - EDGE) pillLeft = vw - PILL_W - EDGE;

      menuTop = liftedBottom + GAP;
      // Финальный clamp: меню всегда полностью в видимой зоне (даже если
      // придётся залезть на bubble — это OK).
      if (menuTop + menuMaxHeight > BOTTOM) {
        menuTop = BOTTOM - menuMaxHeight;
      }
      if (menuTop < TOP) menuTop = TOP;
      menuLeft = r.left;
      if (menuLeft < EDGE) menuLeft = EDGE;
      if (menuLeft + MENU_W > vw - EDGE) menuLeft = vw - MENU_W - EDGE;
    } else {
      // ── ДЕСКТОП: TG-style стек — pill и menu вместе, без разделения bubble'ом ──
      // Стек = [pill, STACK_GAP, menu]. Расположен СПРАВА от bubble (или снизу слева
      // если справа не помещается). Без лифта (bubble остаётся на месте).
      const STACK_GAP = 14;            // gap между pill и menu

      // Click-relative позиционирование: 5px от клика по умолчанию.
      // Если стек ближе EDGE (30px) к краю — флип на противоположную сторону клика.
      // После флипа держим минимум FLIP_MARGIN (50px) от края.
      const stackW = Math.max(PILL_W, MENU_W);
      menuMaxHeight = Math.min(MENU_H_EST, usableH - PILL_H_EST - STACK_GAP);
      if (menuMaxHeight < MENU_H_MIN) menuMaxHeight = MENU_H_MIN;
      const stackH = PILL_H_EST + STACK_GAP + menuMaxHeight;

      // X: пытаемся поставить справа от клика, флипаем влево если близко к правому краю
      let stackLeft = x + GAP;
      if (stackLeft + stackW > vw - EDGE) {
        stackLeft = x - stackW - GAP;
        if (stackLeft < FLIP_MARGIN) stackLeft = FLIP_MARGIN;
      }
      // Финальный safety-clamp в viewport
      if (stackLeft < EDGE) stackLeft = EDGE;
      if (stackLeft + stackW > vw - EDGE) stackLeft = vw - stackW - EDGE;

      // Y: пытаемся снизу от клика, флипаем вверх если близко к низу
      let stackTop = y + GAP;
      if (stackTop + stackH > BOTTOM) {
        stackTop = y - stackH - GAP;
        if (stackTop < TOP + FLIP_MARGIN) stackTop = TOP + FLIP_MARGIN;
      }
      if (stackTop < TOP) stackTop = TOP;
      if (stackTop + stackH > BOTTOM) stackTop = BOTTOM - stackH;

      // Центрируем pill и menu относительно общей оси stackCenterX —
      // у pill и menu разная ширина, без этого они визуально не выровнены
      // (жалоба юзера: «плашка эмодзи и кнопок должны быть оцентрированы»).
      const stackCenterX = stackLeft + stackW / 2;
      pillTop = stackTop;
      pillLeft = Math.round(stackCenterX - PILL_W / 2);
      menuTop = stackTop + PILL_H_EST + STACK_GAP;
      menuLeft = Math.round(stackCenterX - MENU_W / 2);
      // Финальный safety-clamp: pill и menu всегда полностью в viewport,
      // даже если bubble около правого края или PILL_W ≈ vw. Без этого
      // pill заезжает за правый край экрана (повторная жалоба юзера).
      pillLeft = Math.max(EDGE, Math.min(pillLeft, vw - PILL_W - EDGE));
      menuLeft = Math.max(EDGE, Math.min(menuLeft, vw - MENU_W - EDGE));
    }

    ctxMenuOpenedAtRef.current = Date.now();
    setContextMenu({ messageId, attachmentId, pillTop, pillLeft, menuTop, menuLeft, liftOffset, menuMaxHeight, quote: capturedQuote });
  }, []);

  function copyMessageText(text: string) {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
  }

  /** Long-press → context menu (правый клик недоступен на тач).
   *  Свайп влево/вправо ≥60px → quick reply, с live-анимацией перетягивания.
   *  Long-press отменяется как только палец сдвинулся >8px.
   */
  const swipeStartRef = useRef<{ x: number; y: number; messageId: number } | null>(null);
  // Активно свайпаемый пузырь ведём ИМПЕРАТИВНО (прямо в DOM), без per-frame
  // setState — ref держит элемент для снапбэка на touchEnd. Раньше setSwipeBubble
  // на каждый touchmove ре-рендерил ChatPage → лаг жеста reply-swipe на слабых iOS.
  const swipeElRef = useRef<HTMLElement | null>(null);
  // Long-press visual feedback: scale-up bubble в первые 200мс зажатия (TG)
  // Подсветка нажатого пузыря (.is-pressed) ставится императивно на элемент
  // (16.09.2026): раньше setState здесь давал синхронный re-render всего
  // ChatPage на каждое касание ленты (~150 мс при CPU ×4 — рывок в начале свайпа).
  const onBubbleTouchStart = useCallback((e: React.TouchEvent, messageId: number, blocked: boolean) => {
    if (blocked) return;
    longPressFiredRef.current = false;
    if (longPressTimer.current) window.clearTimeout(longPressTimer.current);
    const t = e.touches[0];
    const x = t.clientX, y = t.clientY;
    const tgt = e.target as HTMLElement;
    const isInteractive = !!tgt.closest(".chat-voice-tg-wave, .chat-voice-tg-play, video");
    if (!isInteractive) {
      swipeStartRef.current = { x, y, messageId };
    }
    // Voice waveform: палец ездит по барам чтобы перемотать аудио. Это НЕ
    // long-press — таймер не запускаем, иначе через 300мс выскочит ctx-меню.
    if (isInteractive) {
      return;
    }
    // Если зажали конкретное фото/видео в альбоме — извлекаем attachmentId,
    // чтобы long-press показал «Скачать» / «Скопировать img» именно для него.
    const imgBtn = tgt.closest(".chat-bubble-img-btn, .chat-bubble-album-cell, .chat-video-tg") as HTMLElement | null;
    let attId: number | undefined;
    if (imgBtn) {
      const aidStr = imgBtn.dataset.attId;
      if (aidStr) attId = Number(aidStr);
    }
    const pressedEl = e.currentTarget as HTMLElement;
    pressedEl.classList.add("is-pressed");
    longPressTimer.current = window.setTimeout(() => {
      longPressFiredRef.current = true;
      if ("vibrate" in navigator) navigator.vibrate?.(20);
      // Lift WHOLE chat-msg (включая avatar + bubble + tail-pseudo)
      const msgEl = (e.target as HTMLElement).closest(".chat-msg") as HTMLElement | null;
      if (msgEl) _liftEl(msgEl);
      openContextMenu(messageId, x, y, attId);
      pressedEl.classList.remove("is-pressed");
    }, 300);
  }, [openContextMenu]);
  const onBubbleTouchMove = useCallback((e: React.TouchEvent) => {
    const s = swipeStartRef.current;
    if (!s) return;

    const t = e.touches[0];
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (Math.hypot(dx, dy) > 8 && longPressTimer.current) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
      (e.currentTarget as HTMLElement).classList.remove("is-pressed");
    }
    if (dx < -10 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      const resisted = Math.max(-120, dx * 0.6);
      const bubble = e.currentTarget as HTMLElement;
      swipeElRef.current = bubble;
      bubble.style.transition = "none";
      bubble.style.transform = `translateX(${resisted}px)`;
      bubble.style.willChange = "transform";
      const hint = bubble.parentElement?.querySelector(".chat-swipe-reply-hint") as HTMLElement | null;
      if (hint) {
        hint.style.opacity = resisted < -20 ? String(Math.min(1, Math.abs(resisted) / 60)) : "0";
        hint.style.transform = `scale(${Math.min(1.1, 0.6 + Math.abs(resisted) / 100)})`;
      }
    }
  }, []);
  // Use ref для messages — touchEnd замыкается на messages, но мы не хотим
  // пересоздавать колбэк каждый рендер (мемо ChatMsg сломается)
  const messagesRef = useRef<ChatMessage[]>([]);
  const onBubbleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (longPressTimer.current) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    (e.currentTarget as HTMLElement).classList.remove("is-pressed");
    const s = swipeStartRef.current;
    swipeStartRef.current = null;
    // Снапбэк императивно ведомого пузыря в исходное + очистка inline-стилей
    // (после анимации возврата отдаём стиль обратно React'у).
    const swiped = swipeElRef.current;
    swipeElRef.current = null;
    if (swiped) {
      swiped.style.transition = "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
      swiped.style.transform = "translateX(0)";
      const hint = swiped.parentElement?.querySelector(".chat-swipe-reply-hint") as HTMLElement | null;
      if (hint) hint.style.opacity = "0";
      window.setTimeout(() => {
        swiped.style.transform = "";
        swiped.style.transition = "";
        swiped.style.willChange = "";
      }, 260);
    }
    if (!s || !e.changedTouches[0]) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (dx <= -60 && Math.abs(dx) > Math.abs(dy) * 1.8) {
      const m = messagesRef.current.find(x => x.id === s.messageId);
      if (m && !m.deleted_at) {
        startReply(m);
        if ("vibrate" in navigator) navigator.vibrate?.(15);
      }
    }
  }, [startReply]);

  /** Throttled ping серверу: «я печатаю». Не чаще 1 раза в 4 секунды.
   *  Раньше 2с — отдавало вдвое больше POST'ов без выигрыша в UX.
   *  Telegram эталон: client throttle 4-5с, server TTL 5-6с. У нас backend TTL=5с
   *  (TYPING_TTL_SECONDS в chat.py); 1 пропущенный пинг при 4с throttle и 5с TTL
   *  даст «жирный» лаг до 1с — приемлемо. */
  function pingTyping() {
    if (!activeTopicId) return;
    const now = Date.now();
    if (now - typingPingRef.current < 4000) return;
    typingPingRef.current = now;
    void postChatTyping(activeTopicId).catch(() => {});
  }

  function insertEmoji(emoji: string) {
    const isEdit = editingId !== null;
    const setter = isEdit ? setEditingText : setText;
    const source = isEdit ? editingText : text;
    const ta = textareaRef.current;
    if (!ta) {
      setter((t) => t + emoji);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const next = source.slice(0, start) + emoji + source.slice(end);
    setter(next);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + emoji.length;
      ta.setSelectionRange(pos, pos);
    });
  }

  const topics = topicsQ.data || [];
  const activeTopic = useMemo(
    () => topics.find(t => t.id === activeTopicId),
    [topics, activeTopicId],
  );
  // Sidebar sort+filter мемоизирован — раньше шёл inline в JSX и пересчитывался
  // на КАЖДЫЙ render ChatPage (любой setState, SSE-update). O(n log n) сорт +
  // search-filter на 100+ топиках = заметный лаг при каждой новой сообщении.
  // Приложения (не чаты) — открываются из модалки-лаунчера (кнопка над папками).
  // В списке они остаются в DOM, но скрыты CSS на десктопе, на мобиле видны.
  const [appsModalOpen, setAppsModalOpen] = useState(false);
  const [appsModalClosing, setAppsModalClosing] = useState(false);
  const closeAppsModal = useCallback(() => {
    setAppsModalClosing(true);
    window.setTimeout(() => { setAppsModalOpen(false); setAppsModalClosing(false); }, 180);
  }, []);
  useEffect(() => {
    if (!appsModalOpen) return;
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape") closeAppsModal(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [appsModalOpen, closeAppsModal]);
  const appTopics = useMemo(
    () => topics.filter(x =>
      x.kind === "group" || x.kind === "docs" || x.kind === "reports"
      || x.kind === "focal" || x.kind === "visits_schedule" || x.kind === "visits_funnel"
    ).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [topics],
  );
  // Окно «Приложения» карточками по секциям организаций — когда приложений много или они из разных организаций
  // (06.10.2026, lib/appCards.ts). Секция «своей» организации — первой, остальные по имени.
  const appSections = useMemo(() => {
    const by = new Map<number, { id: number; name: string; apps: ChatTopic[] }>();
    for (const a of appTopics) {
      const o = topicOrgs.orgOf(a);
      const k = o?.id ?? 0;
      if (!by.has(k)) by.set(k, { id: k, name: o?.name ?? "Приложения", apps: [] });
      by.get(k)!.apps.push(a);
    }
    const home = topicOrgs.home?.id;
    return [...by.values()].sort((x, y) => (x.id === home ? -1 : y.id === home ? 1 : x.name.localeCompare(y.name, "ru")));
  }, [appTopics, topicOrgs]);
  const appsAsCards = appTopics.length >= APPS_CARDS_FROM || appSections.length > 1;
  const openApp = (app: ChatTopic) => {
    closeAppsModal();
    const ext = topicOrgs.appUrlOf(app);   // раздел компании brixcrm — в её CRM (04.10.2026)
    if (ext) { window.open(ext, "_blank", "noopener"); return; }
    if (app.kind === "group") { setActiveGroupId(app.id); return; }
    setActiveGroupId(null);
    setActiveTopicId(app.id);
    setMobileView("messages");
  };
  // kb-apps-swipe: на телефоне свайп слева направо по списку чатов открывает
  // панель приложений. Passive-слушатели без preventDefault — вертикальный
  // скролл и тапы не затронуты; фаерим по порогу на touchmove (не на start).
  useEffect(() => {
    if (!isMessengerRoute || appTopics.length === 0) return;
    let sx = 0, sy = 0, active = false, fired = false;
    const onStart = (e: TouchEvent) => {
      active = false;
      if (window.innerWidth > 900 || e.touches.length !== 1) return;
      const tgt = e.target as HTMLElement | null;
      if (!tgt || !tgt.closest(".chat-sidebar")) return;
      if (tgt.closest(".chat-folders-bar-inline")) return;  // горизонтальный скролл пилюль
      sx = e.touches[0].clientX; sy = e.touches[0].clientY;
      active = true; fired = false;
    };
    const onMove = (e: TouchEvent) => {
      if (!active || fired) return;
      const dx = e.touches[0].clientX - sx;
      const dy = e.touches[0].clientY - sy;
      if (dx > 60 && Math.abs(dx) > 1.5 * Math.abs(dy)) {
        fired = true;
        setAppsModalOpen(true);
      }
    };
    const onEnd = () => { active = false; };
    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
    };
  }, [isMessengerRoute, appTopics.length]);

  const sidebarTopicsSorted = useMemo(() => {
    const folderTids = activeFolderId !== null
      ? new Set((foldersQ.data ?? []).find(f => f.id === activeFolderId)?.topic_ids ?? [])
      : null;
    const filtered = folderTids
      ? topics.filter((t: ChatTopic) => folderTids.has(t.id))
      : topics;
    return [...filtered].sort((a: ChatTopic, b: ChatTopic) => {
      // kind='group' (Диск, Календарь) + kind='docs' (Редактор документов) —
      // ВСЕГДА сверху для всех, выше pinned.
      const aTop = a.kind === "group" || a.kind === "docs" || a.kind === "reports" || a.kind === "focal" || a.kind === "visits_schedule" || a.kind === "visits_funnel";
      const bTop = b.kind === "group" || b.kind === "docs" || b.kind === "reports" || b.kind === "focal" || b.kind === "visits_schedule" || b.kind === "visits_funnel";
      if (aTop !== bTop) return aTop ? -1 : 1;
      if (aTop && bTop) return (a.sort_order ?? 0) - (b.sort_order ?? 0);
      // «Избранное» — сразу под apps, выше pinned-чатов
      const aFav = a.kind === "favorites";
      const bFav = b.kind === "favorites";
      if (aFav !== bFav) return aFav ? -1 : 1;
      const aPinned = !!(a as any).is_pinned;
      const bPinned = !!(b as any).is_pinned;
      if (aPinned !== bPinned) return aPinned ? -1 : 1;
      // Закреплённые между собой — СТАБИЛЬНЫЙ порядок по времени закрепления
      // (pinned_at), а НЕ по последнему сообщению. Иначе закреп с новым
      // сообщением прыгает наверх всех закреплённых (жалоба владельца 14.07).
      if (aPinned && bPinned) {
        // Ручной порядок (pin_order) имеет приоритет; fallback — pinned_at.
        const ao = (a as any).pin_order; const bo = (b as any).pin_order;
        if (typeof ao === "number" && typeof bo === "number" && ao !== bo) return ao - bo;
        const ap = (a as any).pinned_at ? (parseBackendIso((a as any).pinned_at)?.getTime() ?? 0) : 0;
        const bp = (b as any).pinned_at ? (parseBackendIso((b as any).pinned_at)?.getTime() ?? 0) : 0;
        if (ap !== bp) return bp - ap;
        return (a.sort_order ?? 0) - (b.sort_order ?? 0);
      }
      const aTs = a.last_message?.ts ? (parseBackendIso(a.last_message.ts)?.getTime() ?? 0) : 0;
      const bTs = b.last_message?.ts ? (parseBackendIso(b.last_message.ts)?.getTime() ?? 0) : 0;
      if (aTs !== bTs) return bTs - aTs;
      return (a.sort_order ?? 0) - (b.sort_order ?? 0);
    });
  }, [topics, activeFolderId, foldersQ.data]);
  const sidebarTopicsFiltered = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (q.length < 1) return sidebarTopicsSorted;
    return sidebarTopicsSorted.filter((t: ChatTopic) => {
      const name = (t.name || "").toLowerCase();
      const dmName = ((t as any).dm_partner?.display_name || "").toLowerCase();
      const lastAuthor = (t.last_message?.author || "").toLowerCase();
      const lastText = (t.last_message?.text || "").toLowerCase();
      return name.includes(q) || dmName.includes(q) || lastAuthor.includes(q) || lastText.includes(q);
    });
  }, [sidebarTopicsSorted, searchQ]);
  // Sidebar tree: либо top-level (skipping children с parent_topic_id),
  // либо children-only когда активна группа (TG-style slide-in).
  // При поиске — flat (чтобы child тоже находились).
  const sidebarTopicsTree = useMemo(() => {
    type Item = { topic: ChatTopic; isChild: boolean; childCount: number };
    const q = searchQ.trim().toLowerCase();
    if (q.length >= 1) {
      return sidebarTopicsFiltered.map<Item>(t => ({ topic: t, isChild: false, childCount: 0 }));
    }
    if (activeGroupId != null) {
      // Children-only view: только дети активной группы
      return sidebarTopicsFiltered
        .filter(t => t.parent_topic_id === activeGroupId)
        .map<Item>(t => ({ topic: t, isChild: false, childCount: 0 }));
    }
    // Top-level: скрываем дочерние (parent_topic_id != null).
    // Для kind=group считаем сколько у них детей чтобы показать в badge.
    const childCountByParent = new Map<number, number>();
    for (const t of sidebarTopicsFiltered) {
      if (t.parent_topic_id != null) {
        childCountByParent.set(t.parent_topic_id, (childCountByParent.get(t.parent_topic_id) ?? 0) + 1);
      }
    }
    return sidebarTopicsFiltered
      .filter(t => t.parent_topic_id == null)
      .map<Item>(t => ({ topic: t, isChild: false, childCount: childCountByParent.get(t.id) ?? 0 }));
  }, [sidebarTopicsFiltered, searchQ, activeGroupId]);

  // Строки сайдбара мемоизированы: набор текста в композере (setText)
  // больше НЕ перерисовывает список чатов. Deps не включают `text` —
  // сайдбар его не читает. Логика перенесена из JSX-IIFE ДОСЛОВНО.
  const sidebarRows = useMemo(() => {
          const treeItems = sidebarTopicsTree;
          const q = searchQ.trim().toLowerCase();
          if (treeItems.length === 0 && activeFolderId !== null && q.length === 0) {
            return (
              <div className="chat-empty-folder">
                В папке нет чатов
              </div>
            );
          }
          return treeItems.map(({ topic: t, childCount }) => {
          const isActive = t.id === activeTopicId;
          const hasUnread = (t.unread_count ?? 0) > 0 || !!t.manual_unread;
          const isDragging = draggingTopicId === t.id;
          const isGroup = t.kind === "group";
          const isAppKind = isGroup || t.kind === "docs" || t.kind === "reports" || t.kind === "focal" || t.kind === "visits_schedule" || t.kind === "visits_funnel";
          // Перетаскивать можно ТОЛЬКО закреплённые чаты (не приложения) — их порядок per-user.
          const draggable = !!t.is_pinned && !isAppKind;
          return (
            <button
              key={t.id}
              data-topic-id={t.id}
              type="button"
              draggable={draggable}
              onDragStart={() => { if (draggable) setDraggingTopicId(t.id); }}
              onDragEnd={() => setDraggingTopicId(null)}
              onDragOver={(e) => { if (draggable && draggingTopicId !== null) e.preventDefault(); }}
              onDrop={(e) => { if (draggable) { e.preventDefault(); handlePinDrop(t.id); } }}
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                document.dispatchEvent(new CustomEvent("kb-close-ctx", { detail: { except: "topic" } }));
                setTopicCtxMenu({ topicId: t.id, x: e.clientX, y: e.clientY });
              }}
              onClick={() => {
                if (isGroup) {
                  // kind='group' — TG-style: sidebar «уезжает» в группу
                  setActiveGroupId(t.id);
                  setSearchQ("");
                  return;
                }
                if (t.id === activeTopicId) {
                  setSearchQ("");
                  setMobileView("messages");
                } else {
                  setActiveTopicId(t.id);
                  // Открыли чат из поиска → чистим поиск (список вернётся к
                  // норме с этим чатом активным). Чистка ЗДЕСЬ, а не на mousedown.
                  setSearchQ("");
                }
              }}
              className={`chat-topic${isActive ? " is-active" : ""}${isDragging ? " is-dragging" : ""}${draggable && !isGroup ? " is-draggable" : ""}${isGroup ? " chat-topic-group" : ""}${(t.kind === "docs" || t.kind === "group" || t.kind === "reports" || t.kind === "focal" || t.kind === "visits_schedule" || t.kind === "visits_funnel") ? ` chat-topic-app chat-topic-app-${t.kind === "docs" ? "docs" : t.kind === "reports" ? "reports" : t.kind === "focal" ? "focal" : t.kind === "visits_schedule" ? "visits_schedule" : t.kind === "visits_funnel" ? "visits_funnel" : (t.slug === "drive-root" || (t.slug ?? "").endsWith("-drive-root") ? "drive" : "tasks")}` : ""}${t.kind === "favorites" ? " chat-topic-favorites" : ""}`}
            >
              <span className="chat-topic-icon" data-slug={t.slug}>
                {t.kind === "dm" && t.dm_partner ? (
                  <Avatar
                    name={t.dm_partner.display_name || "?"}
                    userId={t.dm_partner.user_id}
                    avatarFilename={t.dm_partner.avatar_filename ?? null}
                    size={isMessengerRoute ? 54 : 28}
                    color={isMessengerRoute ? pickAvatarGradient(t.dm_partner.user_id) : "#AB2B2B"}
                    online={isOnline(t.dm_partner.last_seen_at)}
                  />
                ) : isMessengerRoute ? (() => {
                  // 1. Загруженная аватарка (картинка) — топовый приоритет
                  if (t.avatar_filename) {
                    const url = topicAvatarUrl(t.id, t.avatar_filename);
                    if (url) return <img src={url} alt="" style={{ width: 54, height: 54, borderRadius: "50%", objectFit: "cover" }}/>;
                  }
                  // 2. Эмодзи/Lottie иконка
                  if (t.icon) {
                    const parsed = parseTopicIcon(t.icon);
                    if (parsed.kind === "lottie") return <LottieFromUrl url={parsed.lottie.url} size={54} fallback={parsed.lottie.fallback}/>;
                    if (parsed.kind === "lottie_url") return <LottieFromUrl url={parsed.url} size={54} fallback="🎬"/>;
                    if (parsed.kind === "pack") {
                      return <img src={parsed.url} alt="" width={54} height={54} loading="lazy" decoding="async"
                        style={{ width: 54, height: 54, objectFit: "contain", borderRadius: "50%" }}/>;
                    }
                    if (parsed.kind === "emoji") {
                      // Спец-градиенты для sidebar-разделов
                      let bg = pickAvatarGradient(t.id);
                      if (t.kind === "docs") {
                        bg = "linear-gradient(135deg, #6e8efb 0%, #a777e3 100%)"; // blue→violet
                      } else if (t.slug === "drive-root" || t.name === "Диск") {
                        bg = "linear-gradient(135deg, #f6d365 0%, #fda085 100%)"; // sunny yellow→peach
                      } else if (t.slug === "tasks-root" || t.name === "Календарь") {
                        bg = "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)"; // mint→sky
                      }
                      return (
                        <div style={{
                          width: 54, height: 54, borderRadius: "50%",
                          background: bg,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 28, lineHeight: 1,
                          boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                        }}>{parsed.emoji}</div>
                      );
                    }
                  }
                  // 3. Fallback — инициалы
                  return (
                    <Avatar
                      name={t.name}
                      userId={String(t.id)}
                      avatarFilename={null}
                      size={54}
                      color={pickAvatarGradient(t.id)}
                    />
                  );
                })() : t.icon ? (() => {
                  const parsed = parseTopicIcon(t.icon);
                  if (parsed.kind === "lottie") return <LottieFromUrl url={parsed.lottie.url} size={26} fallback={parsed.lottie.fallback}/>;
                  if (parsed.kind === "lottie_url") return <LottieFromUrl url={parsed.url} size={26} fallback="🎬"/>;
                  if (parsed.kind === "pack") return <img src={parsed.url} alt="" width={26} height={26} loading="lazy" decoding="async"
                    style={{ width: 26, height: 26, objectFit: "contain", borderRadius: "50%" }}/>;
                  if (parsed.kind === "emoji") return <span className="chat-topic-emoji">{parsed.emoji}</span>;
                  return null;
                })() : (
                  <Icon name={t.kind === "private_group" ? "users" : topicIconName(t.slug)} size={16}/>
                )}
              </span>
              <div className="chat-topic-body">
                <div className="chat-topic-row">
                  <span className={`chat-topic-name${hasUnread ? " has-unread" : ""}`}>
                    {t.kind === "dm" && t.dm_partner ? (t.dm_partner.display_name || "Личный чат") : t.name}
                    {isGroup && childCount > 0 && (
                      <span className="chat-topic-group-count">{childCount}</span>
                    )}
                    {(() => {
                      // DM → должность партнёра (нейтральный pill); прочие — этикетка отдела.
                      // Бейдж СРАЗУ после имени, чтобы стрелка › оставалась последним элементом
                      // (как у Drive/Tasks/Docs/Reports).
                      if (t.kind === "dm") {
                        const pos = topicPartnerPosition(t.dm_partner, taxRoleLabels);
                        if (!pos) return null;
                        const cls = pos.roleKey ? `dept-tag dept-tag-role-${pos.roleKey}` : "dept-tag dept-tag-neutral";
                        return <span className={cls}>{pos.label}</span>;
                      }
                      // Приложения (Диск/Календарь/Редактор/Фокусные/Контроль/выезды) —
                      // без этикетки отдела (у них своя app-плашка со стрелкой).
                      const isAppKind = isGroup || t.kind === "docs" || t.kind === "reports" || t.kind === "focal" || t.kind === "visits_schedule" || t.kind === "visits_funnel";
                      if (isAppKind) return null;
                      const dept = t.department;
                      if (!dept || !taxDeptLabels[dept]) return null;
                      return <span className={`dept-tag dept-tag-${dept}`}>{taxDeptLabels[dept]}</span>;
                    })()}
                    {(() => {
                      const om = topicOrgs.markOf(t);
                      return om ? <span className="dept-tag dept-tag-org" title={`Организация: ${om.name}`}>{om.name}</span> : null;
                    })()}
                    {(isGroup || t.kind === "docs" || t.kind === "reports" || t.kind === "focal" || t.kind === "visits_schedule" || t.kind === "visits_funnel") && (
                      <span className="chat-topic-group-arrow" aria-hidden="true">›</span>
                    )}
                    {/* «Канцелярка» закрепа не нужна для app-style топиков (group/docs/reports/focal) —
                        у них есть стрелка › и они и так всегда сверху сайдбара. */}
                    {t.is_pinned && t.kind !== "group" && t.kind !== "docs" && t.kind !== "reports" && t.kind !== "focal" && (
                      <span title="Закреплено" className="chat-topic-pin" style={{ marginLeft: 6, color: "var(--tg-fg-hint, #9e9e9e)", display: "inline-flex" }}><svg viewBox="0 0 16 16" width="12" height="12" fill="currentColor"><path d="M9.828.722a.5.5 0 0 1 .354.146l4.95 4.95a.5.5 0 0 1 0 .707c-.48.48-1.072.588-1.503.588-.177 0-.335-.018-.46-.039l-3.134 3.134a5.927 5.927 0 0 1 .16 1.013c.046.702-.032 1.687-.72 2.375a.5.5 0 0 1-.707 0l-2.829-2.828-3.182 3.182c-.195.195-1.219.902-1.414.707-.195-.195.512-1.22.707-1.414l3.182-3.182-2.828-2.829a.5.5 0 0 1 0-.707c.688-.688 1.673-.767 2.375-.72a5.922 5.922 0 0 1 1.013.16l3.134-3.133a2.772 2.772 0 0 1-.04-.461c0-.43.108-1.022.589-1.503a.5.5 0 0 1 .353-.146z"/></svg></span>
                    )}
                  </span>
                  {t.last_message && t.kind !== "docs" && t.kind !== "reports" && t.kind !== "focal" && !isGroup && (
                    <span className="chat-topic-meta">
                      {t.last_message.from_me && (
                        <span className={`chat-topic-check${t.last_message.read_by_others ? " is-read" : " is-sent"}`} title={t.last_message.read_by_others ? "Прочитано" : "Отправлено"}>
                          {t.last_message.read_by_others ? (
                            <svg viewBox="0 0 18 12" width="18" height="12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 6l4 4 8-8"/><path d="M6 6l4 4 8-8"/>
                            </svg>
                          ) : (
                            <svg viewBox="0 0 18 12" width="18" height="12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 6l4 4 8-8"/>
                            </svg>
                          )}
                        </span>
                      )}
                      <span className="chat-topic-time">{fmtSidebarDate(t.last_message.ts)}</span>
                    </span>
                  )}
                </div>
                {(() => {
                  // App-style топики (docs/reports/focal/group) — без превью сообщений.
                  // У них своя визитка («Отдел продаж» chip + стрелка ›).
                  if (t.kind === "docs" || t.kind === "reports" || t.kind === "focal" || t.kind === "visits_schedule" || t.kind === "visits_funnel" || isGroup) {
                    return null;
                  }
                  // Если этот топик сейчас активен — не показываем typing в сайдбаре
                  // (юзер и так видит индикатор в шапке открытого чата).
                  const typingForTopic = (t.id === activeTopicId)
                    ? []
                    : (typingAllQ.data?.[String(t.id)] || []);
                  const typingStr = typingForTopic.length > 0 ? fmtTyping(typingForTopic) : null;
                  const mentions = t.unread_mentions ?? 0;
                  const badge = hasUnread ? (
                    mentions > 0 ? (
                      <span className="chat-badge chat-badge-mention" title={`Вас упомянули (${mentions})`}>
                        @{(t.unread_count ?? 0) > 1 ? (t.unread_count ?? 0) : ""}
                      </span>
                    ) : (
                      <span className="chat-badge">{(t.unread_count ?? 0) > 0 ? t.unread_count : ""}</span>
                    )
                  ) : null;
                  if (typingStr) {
                    return (
                      <div className="chat-topic-preview-line">
                        <div className="chat-topic-preview is-typing">{typingStr}</div>
                        {badge}
                      </div>
                    );
                  }
                  // Draft — приоритет над last_message. Показываем "Черновик: ..."
                  // красным (как TG), чтобы юзер увидел что оставил недописанное
                  // сообщение при закрытии чата/вкладки. Черновики персистятся
                  // на сервере (см. useDrafts hook + chat_drafts таблица).
                  const draft = draftsByTopic.get(t.id);
                  const draftText = draft?.text.trim();
                  if (draftText) {
                    return (
                      <div className="chat-topic-preview-line">
                        <div className="chat-topic-preview">
                          <span className="chat-topic-preview-draft">Черновик:</span>{" "}
                          {draftText}
                        </div>
                        {badge}
                      </div>
                    );
                  }
                  if (t.last_message) {
                    return (
                      <div className="chat-topic-preview-line">
                        <div className="chat-topic-preview">
                          <span className="chat-topic-preview-author">{t.last_message.from_me ? "Вы" : t.last_message.author}:</span>{" "}
                          {t.last_message.preview}
                        </div>
                        {badge}
                      </div>
                    );
                  }
                  // нет preview, но есть unread — отрисуем badge в пустой строке
                  if (badge) {
                    return (
                      <div className="chat-topic-preview-line">
                        <div className="chat-topic-preview"/>
                        {badge}
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </button>
          );
        });
        }, [sidebarTopicsTree, activeTopicId, draggingTopicId, searchQ, activeFolderId, typingAllQ.data, draftsByTopic, taxRoleLabels, taxDeptLabels]);

  // FLIP: при переупорядочивании списка чатов (новое сообщение поднимает чат
  // наверх) строки плавно доезжают, а не прыгают скачком. Анимируем ТОЛЬКО чистый
  // reorder — тот же набор id: при смене набора (поиск/папка/фильтр) и во время
  // drag анимации нет, иначе строки «летали» бы. Замер (forced layout) — лишь при
  // смене sidebarTopicsTree (мемо-ссылка), не на каждый рендер.
  const sidebarRef = useRef<HTMLElement | null>(null);
  const flipPrevTops = useRef<Map<number, number>>(new Map());
  useLayoutEffect(() => {
    const el = sidebarRef.current;
    if (!el) return;
    const rows = Array.from(el.querySelectorAll<HTMLElement>(".chat-topic[data-topic-id]"));
    const newTops = new Map<number, number>();
    // Позиция строки ВНУТРИ контента сайдбара: rectTop минус rectTop контейнера
    // плюс его scrollTop. Так координата scroll-независима (проскроллил между
    // reorder'ами — дельта не соврёт) и не ломается на скрытых app-топиках
    // (offsetTop/offsetParent у display:none строк = 0/null). Строки — прямые
    // дети aside, значит именно aside скроллится → его scrollTop корректен.
    const contTop = el.getBoundingClientRect().top;
    const scroll = el.scrollTop;
    for (const r of rows) {
      newTops.set(Number(r.dataset.topicId), r.getBoundingClientRect().top - contTop + scroll);
    }
    const prev = flipPrevTops.current;
    const sameSet = prev.size === newTops.size && [...newTops.keys()].every((id) => prev.has(id));
    if (sameSet && draggingTopicId === null) {
      for (const r of rows) {
        const id = Number(r.dataset.topicId);
        const delta = (prev.get(id) ?? 0) - (newTops.get(id) ?? 0);
        // Кап 600px: чат, прыгающий издалека (из-за фолда), снапается мгновенно —
        // иначе слайд на тысячи px за 0.24с = «стрик». Видимые смещённые соседи
        // (дельты ~64px) плавно едут — это и даёт эффект «список расступился».
        if (Math.abs(delta) > 1 && Math.abs(delta) <= 600) {
          // setProperty(..., "important") ОБЯЗАТЕЛЕН: в чате есть глобальные
          // transition:none !important ресеты, которые перебили бы inline-
          // transition без important → transform прыгал бы мгновенно (без анимации).
          // Токен делает очистку interrupt-safe при быстрых подряд reorder'ах.
          const token = String((Number(r.dataset.flipToken) || 0) + 1);
          r.dataset.flipToken = token;
          r.style.setProperty("transition", "none", "important");
          r.style.setProperty("transform", `translateY(${delta}px)`, "important");
          requestAnimationFrame(() => {
            r.style.setProperty("transition", "transform 0.24s cubic-bezier(0.32, 0.72, 0, 1)", "important");
            r.style.setProperty("transform", "translateY(0)", "important");
            window.setTimeout(() => {
              if (r.dataset.flipToken === token) {
                r.style.removeProperty("transition");
                r.style.removeProperty("transform");
              }
            }, 280);
          });
        }
      }
    }
    flipPrevTops.current = newTops;
  }, [sidebarTopicsTree, draggingTopicId]);

  // Объект активной группы (для header в panel-view)
  const activeGroup = useMemo(
    () => activeGroupId == null ? null : topics.find(t => t.id === activeGroupId) ?? null,
    [topics, activeGroupId],
  );
  // Можно ли свернуть боковую панель в текущем view. Раньше — только внутри
  // Drive/Tasks-группы. Добавили kind='reports' (Контроль сделок) — там
  // тоже хочется простор для встроенной страницы отчётов.
  const canCollapseSidebar = useMemo(
    () => Boolean((activeGroup && activeTopicId) || (activeTopicId && (
      topics.find(t => t.id === activeTopicId)?.kind === "reports"
      || topics.find(t => t.id === activeTopicId)?.kind === "focal"
      || topics.find(t => t.id === activeTopicId)?.kind === "visits_schedule"
      || topics.find(t => t.id === activeTopicId)?.kind === "visits_funnel"))),
    [activeGroup, activeTopicId, topics],
  );
  // Bot DM detection — для slash-popup и hamburger-кнопки в composer
  const isBotDm = !!(activeTopic?.kind === "dm" && activeTopic?.dm_partner?.user_id === BOT_SALARY_USER_ID);
  // Slash-команды: query=null если не активен, иначе текущий ввод "/..."
  const [slashQuery, setSlashQuery] = useState<string | null>(null);
  const [slashIdx, setSlashIdx] = useState(0);
  // Generic slash для любого чата (не бот-DM): «Файл с диска» / «Поставить задачу»
  const [actionSlashQuery, setActionSlashQuery] = useState<string | null>(null);
  const [actionSlashIdx, setActionSlashIdx] = useState(0);
  const [drivePickerOpen, setDrivePickerOpen] = useState(false);
  const [editorPickerOpen, setEditorPickerOpen] = useState(false);
  const [taskCreatorOpen, setTaskCreatorOpen] = useState(false);
  const [noteToProjectOpen, setNoteToProjectOpen] = useState(false);
  // Save-to-drive: массив аттачей (один или несколько) для сохранения в общую папку
  const [saveToDrive, setSaveToDrive] = useState<Array<{ attachmentId: number; filename?: string }> | null>(null);

  // Snapshot messages.length на момент open чата — baseline для pulse.
  // Срабатывает один раз когда messages впервые приходит для нового topic.
  useEffect(() => {
    if (!activeTopicId) return;
    const len = messagesQ.data?.length ?? 0;
    if (len === 0) return;
    if (baselineMsgLenRef.current?.topicId !== activeTopicId) {
      baselineMsgLenRef.current = { topicId: activeTopicId, len };
      setPulseTick(v => v + 1);
    }
  }, [activeTopicId, messagesQ.data?.length]);
  const dmPartnerId = activeTopic?.kind === "dm" ? activeTopic.dm_partner?.user_id : null;
  const dmPartnerQ = useQuery({
    queryKey: ["dm-partner-public", dmPartnerId],
    queryFn: () => fetchUserPublic(dmPartnerId!),
    enabled: !!dmPartnerId && isMessengerRoute,
    // staleTime=0 + refetchOnWindowFocus: при возврате к вкладке last_seen_at
    // дёргается сразу, не ждём 60s. refetchInterval=60s — fallback под SSE.
    staleTime: 0,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });

  const _messagesRaw = messagesQ.data || [];
  // Если "Скрывать удалённые" включено — фильтруем (backend всё равно отдаёт
  // удалённые для admin/author, но фронт может их полностью скрыть).
  // Фильтруем удалённые (если showDeleted=false) И полностью пустые (текст
  // пустой И аттачей нет): такие "призрачные" сообщения иногда проскакивают
  // из-за гонок в send/edit логике. Оптимистики (id<0) не фильтруем.
  //
  // useMemo: при поллинге react-query делает structural sharing — у
  // неизменившихся сообщений сохраняются объекты-ссылки. Если _messagesRaw
  // не поменялся (или поменялись только некоторые поля), этот useMemo
  // вернёт стабильную ссылку, и ниже useMemo'ы (topicMedia и т.д.) тоже
  // не будут пересчитываться.
  const allMessages = useMemo(() => (
    (showDeleted ? _messagesRaw : _messagesRaw.filter(m => !m.deleted_at))
      .filter(m => {
        if (m.id < 0) return true;
        if (m.deleted_at) return true;
        const hasText = !!(m.text && m.text.trim());
        const hasAttachments = !!(m.attachments && m.attachments.length > 0);
        return hasText || hasAttachments;
      })
  ), [_messagesRaw, showDeleted]);
  // Воронки Amo, встречающиеся в загруженных сообщениях топика ([pipe:Имя])
  const pipeOptions = useMemo(() => {
    const set = new Set<string>();
    for (const m of allMessages) {
      const txt = (m as { text?: string }).text || "";
      let idx = 0;
      while ((idx = txt.indexOf("[pipe:", idx)) !== -1) {
        const end = txt.indexOf("]", idx);
        if (end === -1) break;
        set.add(txt.slice(idx + 6, end));
        idx = end + 1;
      }
    }
    return Array.from(set).sort();
  }, [allMessages]);

  // Windowing убрана — Virtuoso handles all messages efficiently через виртуализацию
  const messages = useMemo(
    () => (pipeFilter
      ? allMessages.filter((m) => (((m as { text?: string }).text) || "").includes("[pipe:" + pipeFilter + "]"))
      : allMessages),
    [allMessages, pipeFilter]
  );

  // Если 401 или reload размонтировал ChatPage во время POST, восстанавливаем
  // локальный snapshot. Если сообщение всё-таки было принято сервером, его
  // client_request_id уже есть в истории — тогда composer не возвращаем.
  useEffect(() => {
    if (!user?.id || !activeTopicId) return;
    const recovery = readPendingSend(user.id, activeTopicId);
    if (!recovery) return;
    const matchingMessage = allMessages.find(
      message => message.meta?.client_request_id === recovery.id,
    );
    // Отрицательный id принадлежит текущему optimistic-сообщению. Это штатная
    // отправка, а не восстановление после сбоя: snapshot пока оставляем, но
    // предупреждение не показываем. На success snapshot очистит mutation.
    if (matchingMessage && matchingMessage.id < 0) return;
    if (matchingMessage) {
      clearPendingSend(recovery.id);
      setText(current => current === recovery.text ? "" : current);
      setReplyTo(current => current?.id === recovery.replyTo?.id ? null : current);
      const deliveredAttachmentIds = new Set(recovery.attachments.map(file => file.id));
      setPendingFiles(current => current.filter(file => !deliveredAttachmentIds.has(file.id)));
      setSendError(null);
      return;
    }
    setText(current => current || recovery.text);
    setReplyTo(current => current ?? recovery.replyTo);
    setPendingFiles(current => {
      const ids = new Set(current.map(file => file.id));
      const recovered = recovery.attachments.filter(file => !ids.has(file.id));
      return recovered.length > 0 ? [...current, ...recovered] : current;
    });
    setSendError("Предыдущая отправка не завершилась. Текст и вложения восстановлены.");
  }, [activeTopicId, allMessages, user?.id]);

  // Смена топика — сброс фильтра воронок
  useEffect(() => { setPipeFilter(null); }, [activeTopicId]);

  // Post-mount scroll-to-bottom correction. Когда юзер открывает топик,
  // Virtuoso mount'ится с initialTopMostItemIndex по ESTIMATED heights.
  // Картинки/voice/аттач загружаются → реальные heights растут → scrollTop
  // отстаёт от scrollHeight → лента «не в самом конце». Один scrollToIndex
  // через 500ms догоняет к bottom. Skip если есть divider (отдельный useEffect)
  // или pin-jump (jumpRemountKey self-managed).
  // ВАЖНО: useEffect должен быть ПОСЛЕ `const messages` — иначе TDZ при минификации.
  // STICK-TO-BOTTOM (TG-style): лента сразу видна, scroll автоматически
  // удерживается у bottom после каждого image load. Юзер видит плавное
  // добавление контента вместо "толчков" вверх/вниз.
  // Период активности: 1.5с после смены топика (после этого юзер может
  // листать историю — не мешаем).
  // Virtuoso initialTopMostItemIndex + overflow-anchor:auto + alignToBottom
  // сами справляются с positioning. Дополнительный post-mount scrollToIndex
  // был визуальным "1 толчком" — удалён.

  // (Auto-scroll на вход в чат — делает старый wasZero block + Virtuoso initialTopMostItemIndex.)
  // Зеркалим в ref для touch-end колбэка (он мемоизирован — не должен
  // пересоздаваться при каждой смене messages).
  messagesRef.current = messages;

  // ── Пересылка как в TG (18.09.2026): превью, оптимистичная вставка, отправка ──
  const buildForward = useCallback((messageIds: number[], attachmentIds: number[]): { info: ForwardPreviewInfo; sources: ChatMessage[] } => {
    const idSet = new Set(messageIds);
    const attSet = new Set(attachmentIds);
    const sources: ChatMessage[] = [];
    for (const m of messagesRef.current) {
      if (idSet.has(m.id)) { sources.push(m); continue; }
      const picked = attSet.size > 0 ? (m.attachments || []).filter(a => attSet.has(a.id)) : [];
      if (picked.length > 0) sources.push({ ...m, text: "", attachments: picked });
    }
    const count = sources.length || (messageIds.length + attachmentIds.length);
    const authors = Array.from(new Set(
      sources.map(m => m.forwarded_from_author_name || m.author_name).filter((x): x is string => !!x),
    ));
    const author = authors.length === 0 ? "Сообщение"
      : authors.length === 1 ? authors[0] : `${authors[0]} и ещё ${authors.length - 1}`;
    const atts = sources.flatMap(m => m.attachments || [])
      .filter(a => !(a.mime || "").toLowerCase().startsWith("audio/"))
      .slice(0, 10)
      .map(a => ({ id: a.id, mime: a.mime, filename: a.filename, size_bytes: a.size_bytes }));
    const snippetOf = (m: ChatMessage): string => {
      const txt = (m.text || "").replace(/\s+/g, " ").trim().slice(0, 160);
      if (txt) return txt;
      const list = m.attachments || [];
      if (list.length === 0) return "";
      const mime = (list[0].mime || "").toLowerCase();
      return mime.startsWith("audio/") ? "Голосовое сообщение"
        : list.length > 1 ? `${list.length} ${pluralRu(list.length, ["вложение", "вложения", "вложений"])}`
        : mime.startsWith("image/") ? "Фото"
        : mime.startsWith("video/") ? "Видео"
        : list[0].filename;
    };
    // По записи на каждое сообщение — владелец: «несколько сообщений пересылаю —
    // несколько предпросмотров».
    const items = sources.map(m => ({
      author: m.forwarded_from_author_name || m.author_name || "Сообщение",
      text: snippetOf(m),
      attachments: (m.attachments || [])
        .filter(a => !(a.mime || "").toLowerCase().startsWith("audio/"))
        .slice(0, 3)
        .map(a => ({ id: a.id, mime: a.mime, filename: a.filename, size_bytes: a.size_bytes })),
    }));
    const snippet = sources.length === 1
      ? snippetOf(sources[0])
      : `${count} ${pluralRu(count, ["сообщение", "сообщения", "сообщений"])}`;
    return { info: { count, author, text: snippet, attachments: atts, items }, sources };
  }, []);

  const runForward = useCallback(async (fwd: ForwardDraft) => {
    const tid = fwd.targetTopicId;
    const nowIso = new Date().toISOString().replace("Z", "");
    const base = -Date.now();
    const temps = fwd.sources.map((src, i) => ({
      id: base - i - Math.floor(Math.random() * 1000) * 1000,
      topic_id: tid,
      author_user_id: user?.id ?? null,
      author_name: user?.display_name || user?.email || "Я",
      text: src.text,
      created_at: nowIso,
      reply_to: null,
      attachments: src.attachments ?? [],
      forwarded_from_message_id: src.forwarded_from_message_id ?? src.id,
      forwarded_from_author_name: src.forwarded_from_author_name ?? src.author_name,
      meta: null,
    } as unknown as ChatMessage));
    const tempIds = new Set(temps.map(t => t.id));
    if (temps.length > 0) {
      qc.setQueryData<ChatMessage[]>(["chat/messages", tid], (old) => (old ? [...old, ...temps] : old));
    }
    try {
      const real = await forwardChatMessages(tid, fwd.messageIds, fwd.attachmentIds);
      // SSE мог уже заменить часть оптимистиков реальными (applyMessageInsert меняет
      // первый id<0) — убираем оставшиеся временные и добавляем недостающие реальные.
      qc.setQueryData<ChatMessage[]>(["chat/messages", tid], (old) => {
        if (!old) return old;
        const kept = old.filter(m => !tempIds.has(m.id));
        const have = new Set(kept.map(m => m.id));
        return [...kept, ...real.filter(m => !have.has(m.id))];
      });
    } catch (err) {
      qc.setQueryData<ChatMessage[]>(["chat/messages", tid], (old) => (old ? old.filter(m => !tempIds.has(m.id)) : old));
      setForwardDraft(cur => cur ?? fwd);   // вернуть плашку — можно повторить
      const detail = err instanceof Error ? err.message : "сетевая ошибка";
      setSendError(`Не удалось переслать: ${detail}. Плашка пересылки сохранена — можно повторить.`);
    }
  }, [qc, user]);
  runForwardRef.current = runForward;

  const openForwardInTopic = useCallback((targetId: number, messageIds: number[], attachmentIds: number[]) => {
    // Превью и исходники собираем ДО смены чата: messagesRef — лента-источник.
    const built = buildForward(messageIds, attachmentIds);
    setForwardDraft({ targetTopicId: targetId, messageIds, attachmentIds, info: built.info, sources: built.sources });
    setForwardPickerOpen(false);
    setForwardPicker(null);
    setSelectionMode(false);
    setSelectedIds(new Set());
    setSelectedAttachmentIds(new Set());
    if (targetId !== activeTopicIdRef.current) setActiveTopicId(targetId);
    window.setTimeout(() => textareaRef.current?.focus({ preventScroll: true }), 150);
  }, [buildForward]);

  // ID выбранных сообщений, которые удалены — для кнопки «Восстановить»
  // в selection-баре (показывается только если есть удалённые в выборке).
  const selectedDeletedIds = useMemo(
    () => messages.filter(m => selectedIds.has(m.id) && m.deleted_at).map(m => m.id),
    [messages, selectedIds],
  );

  // Diff: если последний msg id > предыдущего seen → новые id'шники получают
  // fresh-метку на 320ms. Сама смена freshIds Set делает только 1 ре-рендер
  // ChatPage; React.memo на ChatMsg не пропустит у тех, чей isFresh не
  // изменился (большинство сообщений).
  useEffect(() => {
    if (!activeTopicId || messages.length === 0) return;
    const lastId = messages[messages.length - 1].id;
    const seen = lastSeenIdByTopicRef.current.get(activeTopicId) ?? 0;
    if (seen === 0) {
      // первый раз видим этот топик — НЕ анимируем (это initial load)
      lastSeenIdByTopicRef.current.set(activeTopicId, lastId);
      return;
    }
    if (lastId > seen) {
      const fresh = new Set<number>();
      for (let i = messages.length - 1; i >= 0; i--) {
        const id = messages[i].id;
        if (id <= seen) break;
        if (id > 0) fresh.add(id); // не анимируем optimistic (id<0)
      }
      lastSeenIdByTopicRef.current.set(activeTopicId, lastId);
      if (fresh.size > 0) {
        setFreshIds(fresh);
        const t = window.setTimeout(() => setFreshIds(new Set()), 320);
        return () => window.clearTimeout(t);
      }
    }
  }, [messages, activeTopicId]);

  // === Global long-press → contextmenu dispatch + lift clone ===
  // На iOS contextmenu НЕ фаерится автоматически на long-press. Реализуем сами:
  // 300ms таймер на touchstart, lift target в portal-клон, dispatch contextmenu.
  useEffect(() => {
    let timer: number | null = null;
    let startX = 0, startY = 0;
    let target: HTMLElement | null = null;
    const cancel = () => {
      if (timer !== null) { window.clearTimeout(timer); timer = null; }
      target = null;
    };
    // touchstart sets target; touchmove > 8px cancels; on cleanup also remove lift

    const onStart = (e: TouchEvent) => {
      if (!e.touches[0]) return;
      const t = e.target as HTMLElement;
      // Игнорируем chat-bubble — там свой long-press handler
      if (t.closest(".chat-bubble")) return;
      // Игнорируем input/textarea/button (нативные элементы)
      if (t.closest("input, textarea, .chat-form, .chat-composer")) return;
      target = t;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      timer = window.setTimeout(() => {
        if (!target) return;
        if ("vibrate" in navigator) navigator.vibrate?.(15);
        // Lift: клонируем целевой элемент в body
        const liftTarget = (target.closest(".chat-topic, .chat-folder-rail-item, .profile-media-item, .profile-file-row, .profile-voice-row, .profile-link-row, .chat-folders-bar-inline button") as HTMLElement | null);
        if (liftTarget) _liftEl(liftTarget);
        // Folder button — bypass React onContextMenu (он не фаерится надёжно через
        // dispatchEvent на visibility:hidden parent на iOS PWA). Используем CustomEvent.
        const folderBtn = (target.closest(".chat-folders-bar-inline button[data-folder-id]") as HTMLElement | null);
        if (folderBtn) {
          const fid = Number(folderBtn.getAttribute("data-folder-id"));
          if (Number.isFinite(fid) && fid > 0) {
            document.dispatchEvent(new CustomEvent("kb-folder-ctx", {
              detail: { folderId: fid, x: startX, y: startY },
            }));
          }
        } else {
          // Прочие long-press targets — через дефолтный contextmenu
          const ev = new MouseEvent("contextmenu", {
            bubbles: true,
            cancelable: true,
            clientX: startX,
            clientY: startY,
          });
          target.dispatchEvent(ev);
        }
        // Suppress synthetic click iOS shoots ~300ms after touchend.
        // Without this: FoldersBar close-listener fires → menu closes immediately;
        // FolderTab button onClick fires → switches folder.
        const suppressClick = (ce: MouseEvent) => {
          ce.preventDefault();
          ce.stopImmediatePropagation();
          document.removeEventListener("click", suppressClick, true);
        };
        document.addEventListener("click", suppressClick, true);
        window.setTimeout(() => {
          document.removeEventListener("click", suppressClick, true);
        }, 600);
        target = null;
        timer = null;
      }, 300);
    };
    const onMove = (e: TouchEvent) => {
      if (!target || !e.touches[0]) return;
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      if (Math.hypot(dx, dy) > 8) cancel();
    };
    const onEnd = () => cancel();

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    document.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
      cancel();
      _removeLift();
    };
  }, []);

  // iOS PWA fix: глобальный non-passive touchmove listener.
  // Вызываем preventDefault во время горизонтального свайпа bubble — это
  // unlock'ит iOS paint pipeline (без этого inline transform на bubble
  // остаётся в DOM но визуально не рендерится до touchend).
  useEffect(() => {
    const onMove = (e: TouchEvent) => {
      const s = swipeStartRef.current;
      if (!s || !e.touches[0]) return;
      const dx = e.touches[0].clientX - s.x;
      const dy = e.touches[0].clientY - s.y;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 8) {
        // Горизонтальный свайп — забираем у iOS native scroll
        e.preventDefault();
      }
    };
    document.addEventListener("touchmove", onMove, { passive: false });
    return () => document.removeEventListener("touchmove", onMove);
  }, []);


  // Стабильный renderer текста для ChatMsg (mentions/url/emoji). Меняется только
  // при смене mentionMap — не на каждый рендер ChatPage.
  const renderText = useCallback((s: string) =>
    renderTextWithMentions(s, {
      mentionMap,
      onMentionClick: (uid: string) => { if (isMessengerRoute) openUserProfile(uid); else setProfileLightboxId(uid); },
      onHashtagClick: (tag: string) => { setSearchQ(tag); },
    }),
  [mentionMap, isMessengerRoute, openUserProfile]);

  // Стабильный onProfileClick (раньше был inline arrow в itemContent — каждый
  // рендер ChatPage давал НОВУЮ ссылку, что ломало React.memo на ChatMsg
  // и заставляло ВСЕ сообщения ре-рендериться на каждый polling tick).
  const onChatMsgProfileClick = useCallback((uid: string) => {
    if (isMessengerRoute) openUserProfile(uid); else setProfileLightboxId(uid);
  }, [isMessengerRoute, openUserProfile]);

  // === Fresh-message animation: помечаем недавно прилетевшие сообщения,
  // чтобы CSS дал им fade-in + slideUp. Срабатывает ОДИН раз per id, не
  // повторяется когда юзер скроллит назад (Virtuoso reuse'ит DOM-узлы).
  const [freshIds, setFreshIds] = useState<Set<number>>(() => new Set());
  // topicId → последний id, который мы уже видели (чтобы не пометить старые
  // как fresh при смене топика)
  const lastSeenIdByTopicRef = useRef<Map<number, number>>(new Map());
  // === END fresh-message animation refs ===

  // Все медиа (картинки+видео) текущего топика — для unified lightbox-навигации
  // (как в TG, листать вперёд/назад через все медиа независимо от типа).
  type TopicMediaItem = {
    id: number;
    kind: "image" | "video";
    src: string;
    filename: string;
    caption: string;
    durationSec?: number | null;
  };
  const topicMedia: TopicMediaItem[] = useMemo(() => {
    const out: TopicMediaItem[] = [];
    for (const m of messages) {
      if (m.deleted_at) continue;
      for (const a of (m.attachments || [])) {
        if (isImage(a.mime, a.filename)) {
          out.push({
            id: a.id, kind: "image",
            src: chatAttachmentUrl(a.id),
            filename: a.filename,
            caption: `${m.author_name} · ${a.filename}`,
          });
        } else if (isVideo(a.mime, a.filename)) {
          out.push({
            id: a.id, kind: "video",
            src: chatAttachmentUrl(a.id),
            filename: a.filename,
            caption: `${m.author_name} · ${a.filename}`,
            durationSec: a.duration_sec,
          });
        }
      }
    }
    return out;
  }, [messages]);
  const topicMediaIds: number[] = useMemo(() => topicMedia.map(m => m.id), [topicMedia]);
  // Только image-кадры — нужны для PhotoLightbox (он не умеет видео).
  // Когда текущий медиа-элемент — картинка, передаём ему весь топик-медиа,
  // но PhotoLightbox получает image-only список с правильным idx.
  const topicImages: LightboxImage[] = useMemo(
    () => topicMedia.map(m => ({
      src: m.src,
      previewSrc: m.kind === "image" ? chatAttachmentPreviewUrl(m.id) : undefined,
      filename: m.filename,
      caption: m.caption,
      attachmentId: m.id,
    })),
    [topicMedia],
  );
  const chatLightboxIdx = chatLightboxAid !== null ? topicMediaIds.indexOf(chatLightboxAid) : -1;
  const pinned = pinnedQ.data || [];

  // TG-стиль: shown pin в banner = последний закреп СТРОГО ВЫШЕ viewport-top.
  // После прыжка к закрепу viewport на нём → банннер переключается на
  // следующий старший закреп, если есть. Если все закрепы НИЖЕ viewport
  // (юзер скроллнут в самый верх) — fallback на самый ранний.
  const pinnedRef = useRef(pinned); pinnedRef.current = pinned;
  const pinnedIdxRef = useRef(pinnedIdx); pinnedIdxRef.current = pinnedIdx;
  const recomputeShownPin = useCallback(() => {
    const pinnedList = pinnedRef.current;
    const msgs = messagesRef.current;
    if (pinnedList.length === 0 || msgs.length === 0) return;
    const pinnedWithIdx = pinnedList
      .map(p => ({ id: p.id, idx: msgs.findIndex(m => m.id === p.id) }))
      .filter(p => p.idx >= 0);
    if (pinnedWithIdx.length === 0) return;
    // Строго < startIndex — закреп уже "пролистан" наверх viewport'а
    const eligible = pinnedWithIdx.filter(p => p.idx < visibleStartIdxRef.current);
    const shown = eligible.length > 0
      ? eligible.reduce((a, b) => (a.idx > b.idx ? a : b))
      : pinnedWithIdx.reduce((a, b) => (a.idx < b.idx ? a : b));
    const newIdx = pinnedList.findIndex(p => p.id === shown.id);
    if (newIdx >= 0 && newIdx !== pinnedIdxRef.current) {
      pinnedIdxRef.current = newIdx;
      setPinnedIdx(newIdx);
    }
  }, []);
  useEffect(() => { recomputeShownPin(); }, [pinned, messages, pinnedIdx, recomputeShownPin]);
  const myUserId = user?.id ?? "";
  // canSend учитывает edit-mode: при редактировании смотрим на editingText,
  // иначе — на text + pendingFiles. Иначе кнопка "Отправить" disabled во время edit.
  // Плашка над строкой ввода (Ответ / Правка / Переслать) поднимает плавающую
  // кнопку профиля на телефоне: FAB (fixed, bottom:82px) наезжал на плашку и её ✕
  // (18.09.2026). Класс на <html> — дешёвая инвалидация только самих кнопок FAB.
  const _fwdHere = !!forwardDraft && forwardDraft.targetTopicId === activeTopicId;
  const _bannerOn = !!replyTo || editingId !== null || _fwdHere;
  // Высота плашки разная (строка / плитки / список пересылаемых) — меряем после
  // отрисовки и выбираем ступень подъёма FAB.
  useEffect(() => {
    const root = document.documentElement;
    const clear = () => root.classList.remove("kb-cbanner", "kb-cbanner-tall", "kb-cbanner-xl");
    if (!_bannerOn) { clear(); return; }
    const raf = window.requestAnimationFrame(() => {
      const h = Math.max(0, ...Array.from(document.querySelectorAll(".chat-composer-banner")).map(e => e.getBoundingClientRect().height));
      clear();
      root.classList.add(h <= 70 ? "kb-cbanner" : h <= 170 ? "kb-cbanner-tall" : "kb-cbanner-xl");
    });
    return () => { window.cancelAnimationFrame(raf); clear(); };
  }, [_bannerOn, replyTo, forwardDraft, editingId]);
  const _composeReady = editingId !== null
    ? editingText.trim().length > 0
    : (text.trim().length > 0 || pendingFiles.length > 0
       || (!!forwardDraft && forwardDraft.targetTopicId === activeTopicId));
  const canSend = _composeReady && !sendMut.isPending && !isUploading
    && uploadingFiles.length === 0 && !editMut.isPending;
  // currentPinned теперь вычисляется внутри ChatPinnedBanner (extracted в P2#10a)

  if (topicsQ.isLoading) return isMessengerRoute ? <KbSplash/> : <Spinner size="lg" label="Загрузка чата…"/>;
  if (topicsQ.isError && topicsQ.data === undefined) {
    return (
      <div className="chat-load-error chat-load-error-full" role="alert">
        <div className="chat-load-error-title">Не удалось загрузить список чатов</div>
        <div className="chat-load-error-text">{(topicsQ.error as Error).message}</div>
        <button type="button" className="btn btn-primary" onClick={() => void topicsQ.refetch()}>
          <Icon name="refresh" size={15}/> Повторить
        </button>
      </div>
    );
  }

  return (
    <div
      className={`chat-shell mobile-view-${mobileView}${isDropping ? " is-drop-active" : ""}${sidebarCollapsed && canCollapseSidebar ? " sidebar-collapsed" : ""}${swipeExiting ? " is-swipe-exit" : ""}`}
      data-panel-open={isMessengerRoute && (rightPanelOpen || profileDrawerOpen || pinnedListOpen) ? "true" : "false"}
      style={isMessengerRoute ? ({ "--mng-sidebar-w": `${sidebarWidth}px` } as React.CSSProperties) : undefined}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <FolderRail
        topics={topics}
        activeFolderId={activeFolderId}
        onChange={setActiveFolderId}
        onOpenApps={isMessengerRoute && appTopics.length > 0 ? () => setAppsModalOpen(true) : undefined}
      />
      {appsModalOpen && appsAsCards && createPortal(
        <div
          className={"chat-apps-modal-backdrop chat-apps-cards-backdrop" + (appsModalClosing ? " is-closing" : "")}
          onClick={closeAppsModal}
        >
          <div className="chat-apps-cards" role="dialog" aria-label="Приложения" onClick={(e) => e.stopPropagation()}>
            <div className="chat-apps-cards-head">
              <span className="chat-apps-cards-title">Приложения</span>
              <button type="button" className="chat-apps-cards-close" aria-label="Закрыть" onClick={closeAppsModal}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
              </button>
            </div>
            <div className="chat-apps-cards-body">
              {appSections.map((sec) => (
                <section key={sec.id} className="chat-apps-cards-sec">
                  {appSections.length > 1 && <h3 className="chat-apps-cards-sec-title">{sec.name}</h3>}
                  <div className="chat-apps-cards-grid">
                    {sec.apps.map((app) => {
                      const card = appCardOf(app);
                      const ext = topicOrgs.appUrlOf(app);
                      return (
                        <button key={app.id} type="button" className="chat-apps-card" onClick={() => openApp(app)}
                          title={ext ? "Откроется в CRM компании" : undefined}>
                          <span className="chat-apps-card-pic">
                            {card
                              ? <img src={card.pic} alt="" loading="lazy" decoding="async"/>
                              : <span className="chat-apps-card-letter" style={{ background: pickAvatarGradient(app.id) }}>{(app.name || "?").charAt(0)}</span>}
                            {ext && (
                              <span className="chat-apps-card-ext" aria-hidden="true">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17L17 7M9 7h8v8"/></svg>
                              </span>
                            )}
                          </span>
                          <span className="chat-apps-card-text">
                            <span className="chat-apps-card-name">{app.name}</span>
                            {card && <span className="chat-apps-card-hint">{card.hint}</span>}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
      {appsModalOpen && !appsAsCards && createPortal(
        <div
          className={"chat-apps-modal-backdrop" + (appsModalClosing ? " is-closing" : "")}
          onClick={closeAppsModal}
        >
          <div className="chat-apps-modal" onClick={(e) => e.stopPropagation()}>
            <div className="chat-apps-modal-list">
              {appTopics.map((app) => {
                let iconNode: React.ReactNode = null;
                if (app.avatar_filename) {
                  const url = topicAvatarUrl(app.id, app.avatar_filename);
                  if (url) iconNode = <img src={url} alt="" className="chat-apps-modal-img"/>;
                }
                if (!iconNode && app.icon) {
                  const p = parseTopicIcon(app.icon);
                  if (p.kind === "lottie") iconNode = <LottieFromUrl url={p.lottie.url} size={30} fallback={p.lottie.fallback}/>;
                  else if (p.kind === "lottie_url") iconNode = <LottieFromUrl url={p.url} size={30} fallback="🎬"/>;
                  else if (p.kind === "pack") iconNode = <img src={p.url} alt="" className="chat-apps-modal-img"/>;
                  else if (p.kind === "emoji") {
                    let bg = pickAvatarGradient(app.id);
                    if (app.kind === "docs") bg = "linear-gradient(135deg,#6e8efb 0%,#a777e3 100%)";
                    else if (app.slug === "drive-root" || app.name === "Диск") bg = "linear-gradient(135deg,#f6d365 0%,#fda085 100%)";
                    else if (app.slug === "tasks-root" || app.name === "Календарь") bg = "linear-gradient(135deg,#84fab0 0%,#8fd3f4 100%)";
                    iconNode = <span className="chat-apps-modal-emoji" style={{ background: bg }}>{p.emoji}</span>;
                  }
                }
                if (!iconNode) iconNode = <span className="chat-apps-modal-emoji" style={{ background: pickAvatarGradient(app.id) }}>{(app.name || "?").charAt(0)}</span>;
                return (
                  <button key={app.id} type="button" className="chat-apps-modal-item"
                    onClick={() => openApp(app)}>
                    <span className="chat-apps-modal-icon">{iconNode}</span>
                    <span className="chat-apps-modal-label">{app.name}</span>
                    {(() => {
                      const om = topicOrgs.markOf(app);   // чей раздел — у кого несколько организаций (04.10.2026)
                      return om ? <span className="chat-apps-modal-org" title={`Организация: ${om.name}`}>{om.name}</span> : null;
                    })()}
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
      {isMessengerRoute && (
        <div
          className="chat-sidebar-resize-handle"
          onPointerDown={onSidebarResizeDown}
          onPointerMove={onSidebarResizeMove}
          onPointerUp={onSidebarResizeUp}
          onPointerCancel={onSidebarResizeUp}
          title="Перетащить для изменения ширины"
          aria-hidden="true"
        />
      )}
      <aside ref={sidebarRef} className={`chat-sidebar${activeGroup ? " in-group" : ""}`}>
        {activeGroup ? (
          // TG-style: header в режиме группы — back-arrow + название + count
          <div className="chat-side-group-header">
            <button
              type="button"
              className="chat-side-group-back"
              onClick={() => setActiveGroupId(null)}
              aria-label="Назад к списку чатов"
            >
              <Icon name="chevron_l" size={20}/>
            </button>
            <div className="chat-side-group-title">
              <span className="chat-side-group-name">{activeGroup.name}</span>
              <span className="chat-side-group-count">
                {sidebarTopicsTree.length} {sidebarTopicsTree.length === 1 ? "чат" : sidebarTopicsTree.length < 5 ? "чата" : "чатов"}
              </span>
            </div>
            {activeTopicId && (
              <button
                type="button"
                className="chat-sidebar-collapse-btn chat-sidebar-collapse-btn-in-group"
                onClick={() => setSidebarCollapsed(true)}
                title="Свернуть боковую панель"
                aria-label="Свернуть"
              >
                <Icon name="chevron_l" size={16}/>
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="chat-side-tools">
              {/* desktop: inline input; mobile: button-mock который открывает overlay (анти-zoom iOS) */}
              <input
                className="input-sm chat-side-search chat-side-search-desktop"
                type="search"
                placeholder="Поиск по сообщениям…"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
              />
              <button
                type="button"
                className="chat-side-search-mobile"
                onClick={() => setMobileSearchOpen(true)}
              >
                <Icon name="search" size={14}/>
                <span>{searchQ || "Поиск по сообщениям…"}</span>
              </button>
              <button
                type="button"
                className="btn btn-sm chat-side-tools-add"
                title="Создать новый чат"
                onClick={() => setNewChatOpen(true)}
              >
                <Icon name="plus" size={14}/>
              </button>
              {/* Collapse sidebar — для app-style view'ов где нужен простор
                  (например Контроль сделок). Показывается только когда активен
                  reports-топик и sidebar ещё развёрнут. */}
              {canCollapseSidebar && !sidebarCollapsed && (
                <button
                  type="button"
                  className="btn btn-sm chat-side-tools-add"
                  title="Свернуть боковую панель"
                  aria-label="Свернуть"
                  onClick={() => setSidebarCollapsed(true)}
                >
                  <Icon name="chevron_l" size={14}/>
                </button>
              )}
            </div>

            <div className="chat-folders-bar-inline">
              <FoldersBar
                activeFolderId={activeFolderId}
                onChange={setActiveFolderId}
              />
            </div>
            {/* Voice-player bar в сайдбаре — ТОЛЬКО на мобиле:
                когда юзер на странице со списком чатов, main не виден,
                и без этого экземпляра плеер был бы вообще не виден. */}
            <div className="chat-sidebar-voice-bar-mobile">
              <VoicePlayerBar/>
            </div>
          </>
        )}
        {/* Список топиков — sort+filter в useMemo выше (sidebarTopicsFiltered) */}
        {sidebarRows}

        {/* Результаты поиска — отдельной секцией снизу. Только сообщения, не названия чатов. */}
        {searchQ.trim().length >= 2 && (
          <div className="chat-search-section">
            <div className="chat-search-section-head">
              Найдено в сообщениях
              <button
                type="button"
                className="chat-search-section-clear"
                onClick={() => setSearchQ("")}
                title="Очистить поиск"
              >
                <Icon name="x" size={11}/>
              </button>
            </div>
            <div className="chat-search-results">
              {searchGlobalQ.isFetching && <div className="chat-search-empty">Поиск…</div>}
              {!searchGlobalQ.isFetching && (searchGlobalQ.data ?? []).length === 0 && (
                <div className="chat-search-empty">Ничего не найдено</div>
              )}
              {(searchGlobalQ.data ?? []).map((hit) => {
                // Используем author из самого hit'а (теперь backend возвращает его поля).
                // Fallback на partner из topicsQ для DM где author = partner.
                const topic = (topicsQ.data ?? []).find(t => t.id === hit.topic_id);
                const isDm = topic?.kind === "dm";
                const partner = (topic as any)?.dm_partner;
                const avaUserId = hit.author_user_id || (isDm ? partner?.user_id : null);
                const avaFilename = hit.author_avatar_filename || (isDm ? partner?.avatar_filename : null);
                const avaUrl = avaUserId ? userAvatarUrl(avaUserId, avaFilename ?? null) : null;
                const initial = (hit.author_name || hit.topic_name || "?").trim().charAt(0).toUpperCase();
                const colors = ["#4fae4e","#3390ec","#9c7ce5","#00acc1","#e85c5c","#e99a12"];
                const seedSrc = String(avaUserId || hit.topic_id);
                const color = colors[seedSrc.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % colors.length];
                return (
                  <button
                    key={hit.message_id}
                    type="button"
                    className="chat-search-hit"
                    onClick={() => gotoSearchHit(hit)}
                  >
                    <div className="chat-search-hit-ava">
                      <span className="chat-search-hit-ava-stub" style={{ background: color }}>{initial}</span>
                      {avaUrl && (
                        <img src={avaUrl} alt="" className="chat-search-hit-ava-img"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                      )}
                    </div>
                    <div className="chat-search-hit-body">
                      <div className="chat-search-hit-head">
                        <span className="chat-search-hit-name">{hit.topic_name}</span>
                        <span className="chat-search-hit-time">{fmtSidebarDate(hit.created_at)}</span>
                      </div>
                      <div className="chat-search-hit-snippet">
                        <span className="chat-search-hit-author">{hit.author_name}:</span> {hit.snippet}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </aside>

      {sidebarCollapsed && canCollapseSidebar && (
        <button
          type="button"
          className="chat-sidebar-expand-tab"
          onClick={() => setSidebarCollapsed(false)}
          title="Развернуть боковую панель"
          aria-label="Развернуть"
        >
          <Icon name="chevron_r" size={18}/>
        </button>
      )}
      <section className="chat-main">
        {!activeTopic ? (
          <div className="chat-empty">Выберите топик слева</div>
        ) : topicOrgs.appUrlOf(activeTopic) ? (
          // Раздел организации-компании brixcrm («Крепкий жар») живёт в её CRM (04.10.2026): здесь — переход туда
          <div className="chat-empty org-app-out">
            <div className="org-app-out-title">{activeTopic.name}</div>
            <div className="org-app-out-text">
              Раздел работает в CRM «{topicOrgs.orgOf(activeTopic)?.name}» — откроется в новой вкладке.
            </div>
            <a className="btn btn-primary" href={topicOrgs.appUrlOf(activeTopic) ?? "#"} target="_blank" rel="noopener">
              Открыть в CRM
            </a>
          </div>
        ) : activeTopic.kind === "drive" ? (
          <DriveView
            topicId={activeTopic.id}
            topicName={activeTopic.name}
            initialFolderId={(() => {
              const urlTopic = search.topic ? Number(search.topic) : NaN;
              if (urlTopic !== activeTopic.id) return null;
              const f = search.folder ? Number(search.folder) : NaN;
              return Number.isFinite(f) ? f : null;
            })()}
          />
        ) : activeTopic.kind === "tasks" ? (
          <TasksView topicId={activeTopic.id} topicName={activeTopic.name} topics={topics}/>
        ) : activeTopic.kind === "docs" ? (
          (() => {
            // Редактор организации (04.10.2026): её документы; у другой организации новые — на её Диск
            const docsOrg = topicOrgs.orgOf(activeTopic);
            const drive = docsOrg && !(topicOrgs.home && docsOrg.id === topicOrgs.home.id)
              ? (topicsQ.data ?? []).find(t => t.kind === "drive" && topicOrgs.orgOf(t)?.id === docsOrg.id)
              : undefined;
            return <DocsView orgId={docsOrg?.id ?? null} driveTopicId={drive?.id ?? null}/>;
          })()
        ) : activeTopic.kind === "reports" ? (
          <ReportsView
            topic={activeTopic}
            canModerate={!!user?.is_superuser || activeTopic.created_by_user_id === myUserId}
            myUserId={myUserId ?? null}
          />
        ) : activeTopic.kind === "focal" ? (
          <FocalDealsView
            topic={activeTopic}
            canModerate={!!user?.is_superuser || activeTopic.created_by_user_id === myUserId}
            myUserId={myUserId ?? null}
          />
        ) : activeTopic.kind === "visits_schedule" ? (
          <VisitsScheduleView
            topic={activeTopic}
            canModerate={!!user?.is_superuser || activeTopic.created_by_user_id === myUserId}
            myUserId={myUserId ?? null}
          />
        ) : activeTopic.kind === "visits_funnel" ? (
          <VisitsFunnelView
            topic={activeTopic}
            canModerate={!!user?.is_superuser || activeTopic.created_by_user_id === myUserId}
            myUserId={myUserId ?? null}
          />
        ) : (
          <>
            {/* Selection mode top-bar: FORWARD N | DELETE N | CANCEL — TG style.
                Подменяет обычную шапку чата пока режим выбора активен.
                Извлечено в ChatSelectionBar (P2#10c). */}
            {selectionMode ? (
              <ChatSelectionBar
                totalSelectedCount={totalSelectedCount}
                selectedIdsSize={selectedIds.size}
                selectedDeletedIds={selectedDeletedIds}
                onForward={() => setForwardPickerOpen(true)}
                onDelete={() => {
                  if (selectedIds.size === 0) return;
                  if (!confirm(`Удалить ${selectedIds.size} ${selectedIds.size === 1 ? "сообщение" : "сообщений"}?`)) return;
                  selectedIds.forEach((id) => deleteMut.mutate(id));
                  setSelectionMode(false);
                  setSelectedIds(new Set());
                  setSelectedAttachmentIds(new Set());
                }}
                onRestore={() => {
                  selectedDeletedIds.forEach((id) => restoreMut.mutate(id));
                  setSelectionMode(false);
                  setSelectedIds(new Set());
                  setSelectedAttachmentIds(new Set());
                }}
                onCancel={() => {
                  setSelectionMode(false);
                  setSelectedIds(new Set());
                  setSelectedAttachmentIds(new Set());
                }}
              />
            ) : (
            <div className="chat-header">
              <button
                type="button"
                className="chat-header-back"
                onClick={() => { setMobileView("topics"); setActiveTopicId(null); }}
                title="К списку топиков"
                aria-label="Назад"
              >
                <Icon name="chevron_l" size={20}/>
              </button>
              <button
                type="button"
                className="chat-header-tappable"
                onClick={() => { if (isMessengerRoute) { setRightPanelOpen(false); if (activeTopic) setProfileDrawerStack([activeTopic]); setProfileDrawerOpen(true); } else setMediaModalOpen(true); }}
                title={isMessengerRoute ? "Профиль/информация" : "Открыть медиа топика"}
              >
                <span className="chat-header-icon" data-slug={activeTopic.slug}>
                  {!isMessengerRoute ? (
                    <Icon name={topicIconName(activeTopic.slug)} size={18}/>
                  ) : activeTopic.kind === "dm" && activeTopic.dm_partner ? (
                    <Avatar
                      name={activeTopic.dm_partner.display_name || "?"}
                      userId={activeTopic.dm_partner.user_id}
                      avatarFilename={activeTopic.dm_partner.avatar_filename ?? null}
                      online={isOnline(activeTopic.dm_partner.last_seen_at)}
                      size={42}
                      color={pickAvatarGradient(activeTopic.dm_partner.user_id)}
                    />
                  ) : activeTopic.avatar_filename ? (
                    <img src={topicAvatarUrl(activeTopic.id, activeTopic.avatar_filename) || ""} alt="" style={{ width: 42, height: 42, borderRadius: "50%", objectFit: "cover" }}/>
                  ) : activeTopic.icon ? (() => {
                    const parsed = parseTopicIcon(activeTopic.icon);
                    if (parsed.kind === "lottie") return <LottieFromUrl url={parsed.lottie.url} size={42} fallback={parsed.lottie.fallback}/>;
                    if (parsed.kind === "lottie_url") return <LottieFromUrl url={parsed.url} size={42} fallback="🎬"/>;
                    if (parsed.kind === "pack") return <img src={parsed.url} alt="" width={42} height={42} loading="lazy" decoding="async"
                      style={{ width: 42, height: 42, objectFit: "contain", borderRadius: "50%" }}/>;
                    if (parsed.kind === "emoji") return <span style={{ fontSize: 26, lineHeight: 1 }}>{parsed.emoji}</span>;
                    return <Icon name={topicIconName(activeTopic.slug)} size={18}/>;
                  })() : (
                    <Avatar
                      name={activeTopic.name}
                      userId={null}
                      avatarFilename={null}
                      size={42}
                      color={pickAvatarGradient(activeTopic.id)}
                    />
                  )}
                </span>
                <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <div className="chat-header-title">
                    <span className="chat-header-name">
                      {activeTopic.kind === "dm" && activeTopic.dm_partner ? (activeTopic.dm_partner.display_name || "Личный чат") : activeTopic.name}
                    </span>
                    {(() => {
                      if (activeTopic.kind === "dm") {
                        const pos = topicPartnerPosition(activeTopic.dm_partner, taxRoleLabels);
                        if (!pos) return null;
                        const cls = pos.roleKey ? `dept-tag dept-tag-role-${pos.roleKey}` : "dept-tag dept-tag-neutral";
                        return <span className={cls}>{pos.label}</span>;
                      }
                      const dept = activeTopic.department;
                      if (!dept || !taxDeptLabels[dept]) return null;
                      return <span className={`dept-tag dept-tag-${dept}`}>{taxDeptLabels[dept]}</span>;
                    })()}
                    {(() => {
                      const om = topicOrgs.markOf(activeTopic);
                      return om ? <span className="dept-tag dept-tag-org" title={`Организация: ${om.name}`}>{om.name}</span> : null;
                    })()}
                  </div>
                  <div className="chat-header-sub">
                    {(typingQ.data && typingQ.data.length > 0) ? (
                      <span className="chat-header-typing">
                        <span className="chat-typing-dots"><span/><span/><span/></span>
                        <span>{fmtTyping(typingQ.data)}</span>
                      </span>
                    ) : !isMessengerRoute
                      ? `${messages.length} ${messages.length === 1 ? "сообщение" : "сообщений"}`
                      : (activeTopic.kind === "dm" && activeTopic.dm_partner
                          /* ЕДИНЫЙ источник last_seen с зелёным кружком аватарки.
                             Раньше текст брал из dmPartnerQ (отдельный fetch с другой
                             cadence) → кружок мог быть зелёный, а текст «10 мин назад». */
                          ? formatDmLastSeen(activeTopic.dm_partner.last_seen_at ?? dmPartnerQ.data?.last_seen_at)
                          : formatGroupSubtitle(topicStatsQ.data, messages.length))}
                  </div>
                </div>
              </button>
              <div className="chat-header-actions">
                <button
                  type="button"
                  className="chat-header-btn"
                  title="Поиск в этом топике"
                  data-act="topic-search-toggle"
                  onClick={() => { setTopicSearchOpen(v => !v); if (topicSearchOpen) setTopicSearchQ(""); }}
                >
                  {isMessengerRoute ? (
                    <svg viewBox="0 0 24 24" width="20" height="20" style={{ display: "block" }}>
                      <path fill="#707579" d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                    </svg>
                  ) : (
                    <Icon name="search" size={16}/>
                  )}
                </button>
                {(() => {
                  const isCreator = activeTopic.created_by_user_id === myUserId;
                  const isSuper = !!user?.is_superuser;
                  const isPrivate = activeTopic.kind === "private_group";
                  const isDm = activeTopic.kind === "dm";
                  const canRename = isSuper || isCreator;
                  const canLeave = (isPrivate || isDm);
                  const showMenu = isSuper || isCreator || canLeave;
                  // на /messenger всегда показываем 3-dots в шапке (как в TG-моке)
                  if (!showMenu && !isMessengerRoute) return null;
                  return (
                    <div style={{ position: "relative" }}>
                      <button
                        type="button"
                        className="chat-header-btn"
                        title={isMessengerRoute ? "Медиа и настройки" : "Действия с топиком"}
                        data-act="topic-menu-toggle"
                        onClick={() => {
                          if (isMessengerRoute) { setProfileDrawerOpen(false); setRightPanelOpen(v => !v); }
                          else setTopicMenuOpen(v => !v);
                        }}
                      >
                        {isMessengerRoute ? (
                          <svg viewBox="0 0 24 24" width="20" height="20" style={{ display: "block" }}>
                            <path fill="#707579" d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                          </svg>
                        ) : (
                          <Icon name="settings" size={16}/>
                        )}
                      </button>
                      {topicMenuOpen && (
                        <div className="chat-topic-menu">
                          {(isPrivate || isDm) && (
                            <button type="button" className="chat-topic-menu-item" onClick={() => {
                              setTopicMenuOpen(false);
                              setMembersModalOpen(true);
                            }}>👥 Участники</button>
                          )}
                          {canRename && (
                            <button type="button" className="chat-topic-menu-item" onClick={() => {
                              setTopicMenuOpen(false);
                              setIconPickerOpen(true);
                            }}>✏ Иконка и название</button>
                          )}
                          {!isDm && canRename && (
                            <button type="button" className="chat-topic-menu-item" onClick={() => {
                              setTopicMenuOpen(false);
                              if (isPrivate) {
                                if (confirm(`Открыть «${activeTopic.name}» для всех? Все нынешние участники будут убраны.`)) {
                                  convertTopicMut.mutate({ id: activeTopic.id, kind: "public" });
                                }
                              } else {
                                // public -> private_group: переключим, дальше юзер добавит участников через "Участники"
                                if (confirm(`Сделать «${activeTopic.name}» закрытой группой? Доступ останется только у вас, потом сможете добавить участников.`)) {
                                  convertTopicMut.mutate({ id: activeTopic.id, kind: "private_group" });
                                }
                              }
                            }}>{isPrivate ? "🌐 Сделать общим" : "🔒 Сделать закрытой"}</button>
                          )}
                          {canLeave && (
                            <button type="button" className="chat-topic-menu-item is-danger" onClick={() => {
                              setTopicMenuOpen(false);
                              const what = isDm ? "Скрыть этот личный чат? (история останется у собеседника)" : `Покинуть «${activeTopic.name}»?`;
                              if (confirm(what)) leaveTopicMut.mutate(activeTopic.id);
                            }}>🚪 {isDm ? "Скрыть чат" : "Покинуть"}</button>
                          )}
                          {isSuper && (
                            <button type="button" className="chat-topic-menu-item is-danger" onClick={() => {
                              setTopicMenuOpen(false);
                              if (confirm(`Удалить топик «${activeTopic.name}» и все сообщения в нём? Действие необратимо.`)) {
                                deleteTopicMut.mutate(activeTopic.id);
                              }
                            }}>🗑 Удалить топик</button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
            )}

            {topicSearchOpen && (
              <div className="chat-topic-search-bar">
                <div className="chat-topic-search-input-wrap">
                  <Icon name="search" size={14}/>
                  <input
                    type="search"
                    className="chat-topic-search-input"
                    placeholder="Поиск в этом топике…"
                    value={topicSearchQ}
                    onChange={(e) => setTopicSearchQ(e.target.value)}
                    autoFocus
                  />
                  {topicSearchQ && (
                    <button
                      type="button"
                      className="chat-topic-search-clear"
                      onClick={() => setTopicSearchQ("")}
                      aria-label="Очистить"
                    >
                      <Icon name="x" size={14}/>
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  className="chat-topic-search-close"
                  onClick={() => { setTopicSearchOpen(false); setTopicSearchQ(""); }}
                  aria-label="Закрыть поиск"
                >
                  <Icon name="x" size={16}/>
                </button>
              </div>
            )}
            {topicSearchOpen && topicSearchQ.trim().length >= 2 && (
              <div className="chat-search-results is-inline">
                {searchTopicQ.isFetching && <div className="chat-search-empty">Поиск…</div>}
                {!searchTopicQ.isFetching && (searchTopicQ.data ?? []).length === 0 && (
                  <div className="chat-search-empty">Ничего не найдено</div>
                )}
                {(searchTopicQ.data ?? []).map((hit) => {
                  const avaUrl = hit.author_user_id ? userAvatarUrl(hit.author_user_id, hit.author_avatar_filename ?? null) : null;
                  const initial = (hit.author_name || "?").trim().charAt(0).toUpperCase();
                  const colors = ["#4fae4e","#3390ec","#9c7ce5","#00acc1","#e85c5c","#e99a12"];
                  const color = hit.author_user_id
                    ? colors[hit.author_user_id.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % colors.length]
                    : "#9e9e9e";
                  return (
                    <button
                      key={hit.message_id}
                      type="button"
                      className="chat-search-hit"
                      onClick={() => gotoSearchHit(hit)}
                    >
                      <div className="chat-search-hit-ava">
                        {avaUrl
                          ? <img src={avaUrl} alt=""/>
                          : <span className="chat-search-hit-ava-stub" style={{ background: color }}>{initial}</span>
                        }
                      </div>
                      <div className="chat-search-hit-body">
                        <div className="chat-search-hit-head">
                          <span className="chat-search-hit-name">{hit.author_name}</span>
                          <span className="chat-search-hit-time">{fmtSidebarDate(hit.created_at)}</span>
                        </div>
                        <div className="chat-search-hit-snippet">{hit.snippet}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            <VoicePlayerBar/>
            <ChatPinnedBanner
              pinned={pinned}
              pinnedIdx={pinnedIdx}
              onCycle={setPinnedIdx}
              onJump={jumpToMessage}
              onOpenList={() => { setRightPanelOpen(false); setPinnedListOpen(true); }}
            />


            <div
              key={activeTopicId ?? "none"}
              className="chat-feed-wrap"
              style={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0, overflow: "hidden" }}
            >
              {/* Pin-jump out-of-range overlay убран — единственный спиннер
                  в мессенджере = KbSplash (синий, на initial app boot). */}
              {pipeOptions.length > 0 && (
                <div className="chat-pipe-filter">
                  <button
                    type="button"
                    className={"chat-pipe-chip" + (!pipeFilter ? " active" : "")}
                    onClick={() => setPipeFilter(null)}
                  >Все</button>
                  {pipeOptions.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={"chat-pipe-chip " + pipeCls(p) + (pipeFilter === p ? " active" : "")}
                      onClick={() => setPipeFilter(pipeFilter === p ? null : p)}
                    >{p}</button>
                  ))}
                </div>
              )}
              {loadingMore && messages.length > 0 && (
                /* Индикатор подгрузки старых сообщений при доскролле вверх. */
                <div className="chat-loadmore-spinner" aria-hidden="true" />
              )}
              {/* Шторка на время прыжка к сообщению — см. jumpCurtain/jumpToMessage */}
              {(
                <div className={`chat-jump-curtain${jumpCurtain ? " is-on" : ""}`} aria-hidden="true">
                  <span className="chat-jump-curtain-label">Переходим к сообщению…</span>
                </div>
              )}
              {messagesQ.isLoading ? (
                /* Скелетон (Вариант B — детальные сообщения): аватар + имя +
                   строки текста + таймстамп, «дыхание». Стили .chat-skeleton
                   в chat-redesign.css. */
                <div className="chat-skeleton" aria-hidden="true">
                  {[
                    { mine: false, name: true, lines: [165, 125] },
                    { mine: true, name: false, lines: [135] },
                    { mine: false, name: true, lines: [175, 150, 95] },
                    { mine: true, name: false, lines: [160, 105] },
                    { mine: false, name: true, lines: [140] },
                  ].map((r, i) => (
                    <div key={i} className={`chat-skel-row${r.mine ? " mine" : ""}`}>
                      {!r.mine && <div className="chat-skel-avatar" />}
                      <div className="chat-skel-col">
                        {r.name && <div className="chat-skel-name" />}
                        <div className="chat-skel-bubble">
                          {r.lines.map((w, j) => (
                            <div key={j} className="chat-skel-line" style={{ width: w }} />
                          ))}
                        </div>
                        <div className="chat-skel-time" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : messagesQ.isError && messagesQ.data === undefined ? (
                <div className="chat-load-error" role="alert">
                  <div className="chat-load-error-title">Не удалось загрузить сообщения</div>
                  <div className="chat-load-error-text">{(messagesQ.error as Error).message}</div>
                  <button type="button" className="btn btn-primary" onClick={() => void messagesQ.refetch()}>
                    <Icon name="refresh" size={15}/> Повторить
                  </button>
                </div>
              ) : messages.length === 0 ? (
                <div className="chat-empty-feed">Пока нет сообщений. Напишите первое или перетащите файл.</div>
              ) : null}

{/* TG-style sticky date pill — рендерится через createPortal в document.body
   (escapes chat-shell grid layout, всех overflow:hidden и stacking-контекстов).
   Координаты вычисляются JS-ом в useEffect выше через getBoundingClientRect()
   на chat-feed-wrap. См. feedback_modal_layer_separation.md. */}
              <Virtuoso
                // Key включает length>0 — Virtuoso ремаунтится КОГДА данные
                // приходят (не на пустом списке). Это гарантирует что
                // initialTopMostItemIndex применится с полным data, и Virtuoso
                // встанет внизу. Раньше Virtuoso монтировался с пустым data,
                // потом приходили items — initial index уже не работал → чат
                // "скакал вверх" к index=0.
                // Key включает jumpRemountKey — при out-of-range jump (pinned-banner
                // на старое сообщение) мы заменяем slice и ремаунтим Virtuoso,
                // чтобы initialTopMostItemIndex применился к target а не к end.
                key={
                  activeTopicId != null
                    && messages.length > 0
                    && unreadSnapshotLastReadId !== undefined
                    ? `t-${activeTopicId}-${jumpRemountKey ?? "loaded"}`
                    : "none"
                }
                ref={virtuosoRef}
                data={messages}
                // firstItemIndex держит scroll-якорь при prepend старых
                // сообщений (см. loadMoreOlder). Без него Virtuoso «телепортирует»
                // юзера внутри ленты после fetchBefore и startReached перестаёт
                // триггериться → backwards scroll упирается в стену на 1-й странице.
                firstItemIndex={firstItemIndex}
                // "auto" (мгновенно), НЕ "smooth": анимированный followOutput
                // дёргает экран на каждом новом сообщении и дерётся с
                // императивным jumpBottom при отправке. auto = мгновенный
                // pin вниз, без jitter (рекомендация мейнтейнера Virtuoso).
                // followOutput пинает к bottom при изменении data. В pin-jump-режиме
                // (jumpRemountKey != null) ВСЕГДА false — иначе после ремаунта
                // на center Virtuoso временно считает isAtBottom=true (scrollTop
                // не выставлен сразу) и срывает юзера вниз («сначала вверх,
                // потом срывается вниз»).
                followOutput={(isAtBottom) => {
                  // Блок на первые 1.2с после mount — иначе SSE/refetch fires
                  // followOutput → scroll → visible толчок. После — нормально.
                  if (Date.now() < paginatorReadyAtRef.current) return false;
                  if (jumpRemountKey != null) return false;
                  return (forceFollow || isAtBottom) ? "auto" : false;
                }}
                // initialTopMostItemIndex с firstItemIndex — Virtuoso ждёт
                // ВИРТУАЛЬНЫЙ индекс. По умолчанию: реальный «последний» =
                // messages.length-1 → виртуальный = firstItemIndex + last.
                // При jumpRemountKey (out-of-range pinned/reply jump) ставим
                // target idx из ref'а, align: "center". jumpRemountTargetIdxRef
                // обновляется ПЕРЕД setJumpRemountKey, доступен при mount.
                initialTopMostItemIndex={(() => {
                  // 1) Pin-jump → center на target
                  if (jumpRemountKey != null && jumpRemountTargetIdxRef.current != null) {
                    return { index: firstItemIndex + jumpRemountTargetIdxRef.current, align: "center" as const };
                  }
                  // 2) Всегда — внизу (на последнем сообщении). Юзер
                  //    хочет видеть свежие сразу, без скролла вниз.
                  return { index: firstItemIndex + Math.max(0, messages.length - 1), align: "end" as const };
                })()}
                // Scroll-up paginator: при достижении самого верха ленты
                // подгружаем 80 более старых сообщений через before_id.
                // Защита от двойных вызовов внутри loadMoreOlder.
                startReached={() => {
                  // Блокируем первые 1.2с после смены топика — Virtuoso
                  // ошибочно triggers это при initial mount → лента толкается вверх.
                  if (Date.now() < paginatorReadyAtRef.current) return;
                  loadMoreOlder();
                }}
                alignToBottom={true}
                atBottomStateChange={(atBottom) => {
                  // Virtuoso вызывает этот callback на КАЖДОЕ scroll событие
                  // (включая микро-jitter). setState без дедупа → re-render
                  // всего ChatPage даже когда atBottom не изменилось.
                  if (atBottomRef.current === atBottom) return;
                  atBottomRef.current = atBottom;
                  setShowScrollDown(!atBottom);
                  if (atBottom && activeTopicId) {
                    baselineMsgLenRef.current = { topicId: activeTopicId, len: messagesQ.data?.length ?? 0 };
                    setPulseTick(v => v + 1);
                  }
                }}
                rangeChanged={({ startIndex }) => {
                  // TG-стиль pinned banner: shown pin = последний закреп
                  // чьё idx СТРОГО < startIndex (т.е. закреп ВЫШЕ viewport).
                  // Без setState — см. visibleStartIdxRef.
                  if (visibleStartIdxRef.current === startIndex) return;
                  visibleStartIdxRef.current = startIndex;
                  recomputeShownPin();
                }}
                scrollerRef={(el) => { (feedRef as React.MutableRefObject<HTMLDivElement | null>).current = el as HTMLDivElement | null; }}
                // skipAnimationFrameInResizeObserver — рекомендация мейнтейнера
                // Virtuoso для image-jitter (Discussion #1083). Отключает rAF
                // обёртку в ResizeObserver (введена в 4.7.6), которая давала
                // лишние jitter при scroll к items с медиа.
                skipAnimationFrameInResizeObserver
                // overflow-anchor: auto — BROWSER NATIVE scroll-anchoring.
                // Когда content grows/shrinks (image load, reply preview, etc.)
                // браузер сам компенсирует scrollTop так, что anchor item
                // остаётся в той же экранной позиции. Юзер видит "content
                // плавно появляется", а не "лента дёргается". Это и есть как
                // работает TG/WhatsApp/Slack — native browser feature.
                style={{ flex: 1, minHeight: 0, overscrollBehavior: "none" as const, overflowAnchor: "auto" as const }}
                // Ключ — client_request_id (есть и у оптимистика, и у ответа сервера):
                // при подтверждении отправки элемент НЕ перемонтируется, <img> эмодзи
                // и вложения не перерисовываются заново (16.09.2026). Иначе — id.
                computeItemKey={(_idx, m) => m.meta?.client_request_id || m.id}
                itemsRendered={(items) => {
                  // Кешируем rendered items с offset/size — нужно для быстрого поиска
                  // "первого видимого" по scrollTop без forced layout.
                  renderedItemsRef.current = items.map(it => ({
                    index: it.index,
                    offset: (it as any).offset ?? 0,
                    size: (it as any).size ?? 0,
                    data: (it as any).data as ChatMessage,
                  }));
                }}
                onScroll={() => {
                  // Throttle через rAF. Читаем scrollTop напрямую (без layout-flush),
                  // ищем первый видимый item в renderedItemsRef, обновляем pill ИМПЕРАТИВНО.
                  if (stickyRafRef.current != null) return;
                  stickyRafRef.current = window.requestAnimationFrame(() => {
                    stickyRafRef.current = null;
                    const scroller = feedRef.current;
                    const pill = stickyPillRef.current;
                    if (!scroller) return;
                    // Если открыт lightbox изображений — pill виден через
                    // прозрачные области fullscreen overlay. Скрываем.
                    if (chatLightboxAid !== null) {
                      if (pill) pill.style.visibility = "hidden";
                      return;
                    }
                    const scrollTop = scroller.scrollTop;
                    const items = renderedItemsRef.current;
                    if (items.length === 0) return;
                    if (!pill) return;
                    // Первый видимый: offset + size > scrollTop (item ещё не уехал за верх)
                    let first: typeof items[number] | null = null;
                    for (const it of items) {
                      if (it.offset + it.size > scrollTop) { first = it; break; }
                    }
                    if (!first || !first.data?.created_at) {
                      pill.style.visibility = "hidden";
                      stickyLastLabelRef.current = null;
                      return;
                    }
                    const d = parseBackendIso(first.data.created_at) || new Date(NaN);
                    if (Number.isNaN(d.getTime())) {
                      pill.style.visibility = "hidden";
                      stickyLastLabelRef.current = null;
                      return;
                    }
                    // Скрываем pill если top-видимое сообщение — первое в дне
                    // (его inline date-sep и так виден прямо над ним).
                    // ВАЖНО: first.index — ВИРТУАЛЬНЫЙ (с firstItemIndex'ом),
                    // реальный позицию в messages[] = first.index - firstItemIndex.
                    const realFirstIdx = first.index - firstItemIndex;
                    const prev = realFirstIdx > 0 ? messages[realFirstIdx - 1] : null;
                    const isFirstOfDay = (() => {
                      if (!prev?.created_at) return true;
                      const pd = parseBackendIso(prev.created_at);
                      return !pd || Number.isNaN(pd.getTime()) || pd.toDateString() !== d.toDateString();
                    })();
                    if (isFirstOfDay) {
                      pill.style.visibility = "hidden";
                      stickyLastLabelRef.current = null;
                      return;
                    }
                    const label = fmtDateSep(d);
                    if (label !== stickyLastLabelRef.current) {
                      stickyLastLabelRef.current = label;
                      pill.textContent = label;
                    }
                    pill.style.visibility = "visible";
                    // Авто-скрытие 1.2с после остановки скролла
                    if (stickyHideTimerRef.current) window.clearTimeout(stickyHideTimerRef.current);
                    stickyHideTimerRef.current = window.setTimeout(() => {
                      if (stickyPillRef.current) stickyPillRef.current.style.visibility = "hidden";
                      stickyLastLabelRef.current = null;
                    }, 1200);
                  });
                }}
                // 130 = медиана реальной высоты сообщения (замер 12.09.2026: p50 116–150,
                // avg 128–177). При 70 неизмеренная история оценивалась вдвое короче,
                // пропорциональный телепорт в jumpToMessage промахивался на экраны.
                defaultItemHeight={130}
                components={VIRTUOSO_FEED_COMPONENTS}
                increaseViewportBy={{ top: 2400, bottom: 1200 }}
                overscan={{ main: 1200, reverse: 600 }}
                atBottomThreshold={300}
                itemContent={(idx, m) => {
                  // ВАЖНО: с firstItemIndex Virtuoso передаёт ВИРТУАЛЬНЫЙ idx
                  // (~1_000_000), а реальный индекс в messages[] = idx - firstItemIndex.
                  // Без вычета — messages[idx - 1] = undefined → date-separator
                  // рендерится на КАЖДОМ сообщении (баг «много ВЧЕРА подряд»).
                  const realIdx = idx - firstItemIndex;
                  const prev = realIdx > 0 ? messages[realIdx - 1] : null;
                  const dateSep = (() => {
                    if (!m.created_at) return null;
                    const d = parseBackendIso(m.created_at);
                    if (!d || Number.isNaN(d.getTime())) return null;
                    const dayKey = d.toDateString();
                    if (prev?.created_at) {
                      const pd = parseBackendIso(prev.created_at);
                      if (pd && !Number.isNaN(pd.getTime()) && pd.toDateString() === dayKey) return null;
                    }
                    return fmtDateSep(d);
                  })();
                  const mine = m.author_user_id === myUserId;
                  const pending = m.id < 0;
                  const deleted = !!m.deleted_at;
                  const editing = editingId === m.id;
                  const isJumpTarget = jumpTargetId === m.id;
                  const msgDayLabel = m.created_at
                    ? (() => { const d = parseBackendIso(m.created_at); return !d || Number.isNaN(d.getTime()) ? "" : fmtDateSep(d); })()
                    : "";
                  const pressed = false;   // .is-pressed ставится императивно (onBubbleTouchStart)
                  const isSelected = selectionMode && selectedIds.has(m.id);
                  const showUnreadDivider = firstUnreadId != null && m.id === firstUnreadId;
                  return (
                    // padding-bottom вместо margin-bottom — margins НЕ учитываются
                    // в ResizeObserver.contentRect → Virtuoso мерит меньше реала
                    // → drift offsets → jitter при scroll к items с медиа.
                    <div style={{ paddingBottom: 8 }}>
                    {showUnreadDivider && (
                      <div className="chat-new-messages-divider" role="separator" aria-label="Непрочитанные сообщения">
                        <span>Непрочитанные сообщения</span>
                      </div>
                    )}
                    <ChatMsg
                      m={m}
                      dateSep={dateSep}
                      msgDayLabel={msgDayLabel}
                      mine={mine}
                      pending={pending}
                      deleted={deleted}
                      editing={editing}
                      isJumpTarget={isJumpTarget}
                      avatarSize={avatarSize}
                      pressed={pressed}
                      selectionMode={selectionMode}
                      isSelected={isSelected}
                      onToggleSelect={onToggleSelect}
                      selectedAttachmentIds={selectedAttachmentIds}
                      onToggleAttachmentSelect={onToggleAttachmentSelect}
                      mentionMap={mentionMap}
                      renderText={renderText}
                      onProfileClick={onChatMsgProfileClick}
                      isFresh={freshIds.has(m.id)}
                      onLightboxOpen={setChatLightboxAid}
                      onFilePreview={setFilePreview}
                      onContextMenu={openContextMenu}
                      onReply={startReply}
                      onReactToggle={reactToggleByClick}
                      onReplyJump={jumpToReply}
                      onTouchStart={onBubbleTouchStart}
                      onTouchEnd={onBubbleTouchEnd}
                      onTouchMove={onBubbleTouchMove}
                      onBotButton={onBotButtonClick}
                    />
                    </div>
                  );
                }}
              />
              {/* TG-стиль scroll-to-bottom кнопка — всегда в DOM, скрытие через opacity
                  чтобы не дёргать feed layout (особенно при play голосового) */}
              <button
                type="button"
                // pulse только когда unread вырос ПОСЛЕ открытия чата (baseline
                // snapshot) и last_message не от меня. void pulseTick — это
                // re-render trigger при изменении baseline.
                className={(() => {
                  void pulseTick;
                  const msgs = messagesQ.data;
                  const curLen = msgs?.length ?? 0;
                  const baseLen = baselineMsgLenRef.current?.topicId === activeTopicId
                    ? baselineMsgLenRef.current.len : 0;
                  const lastMsg = msgs && msgs.length ? msgs[msgs.length - 1] : null;
                  const lastFromMe = lastMsg?.author_user_id === myUserId;
                  // КРИТИЧНО: pulse НЕ показываем если юзер at-bottom — сообщение
                  // уже visible, кружок-индикатор создавал visible flicker на каждое
                  // новое сообщение между insert и atBottomStateChange.
                  const pulse = baseLen > 0 && curLen > baseLen && !lastFromMe && !atBottomRef.current;
                  return `chat-scroll-down${(showScrollDown || pulse) ? " is-visible" : ""}${pulse ? " is-new" : ""}`;
                })()}
                onClick={() => {
                  // Снимаем baseline = current → pulse исчезает.
                  if (activeTopicId) {
                    baselineMsgLenRef.current = { topicId: activeTopicId, len: messagesQ.data?.length ?? 0 };
                  }
                  setPulseTick(v => v + 1);
                  // Если в pin-jump режиме (видим slice вокруг старого
                  // сообщения) — клик по «вниз» возвращает к tail:
                  // сброс jumpRemountKey + invalidate → useQuery refetch'ит
                  // последние 200 → Virtuoso ремаунтится на end.
                  if (jumpRemountKey != null && activeTopicId) {
                    jumpRemountTargetIdxRef.current = null;
                    setJumpRemountKey(null);
                    qc.invalidateQueries({ queryKey: ["chat/messages", activeTopicId] });
                    return;
                  }
                  feedRef.current?.scrollTo({
                    top: feedRef.current.scrollHeight,
                    behavior: "smooth",
                  });
                }}
                title={jumpRemountKey != null ? "Вернуться к последним сообщениям" : "К последним сообщениям"}
                aria-hidden={!showScrollDown}
              >
                <Icon name="chevron_d" size={20}/>
              </button>
            </div>

            {/* Bottom-rendered typing удалён — теперь индикатор живёт внутри chat-header-sub */}

            {/* Composer: reply banner + pending files + textarea */}
            <div className="chat-composer">
              {(scheduledQ.data?.length ?? 0) > 0 && (
                <div
                  className="chat-composer-banner"
                  style={{ cursor: "pointer" }}
                  onClick={() => setSchedListOpen(true)}
                  title="Показать список отложенных сообщений"
                >
                  <div className="chat-composer-banner-bar" style={{ background: "#f59e0b" }}/>
                  <div className="chat-composer-banner-body">
                    <div className="chat-composer-banner-title" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                      </svg>
                      <span>Отложенных сообщений: {scheduledQ.data?.length ?? 0}</span>
                    </div>
                  </div>
                </div>
              )}
              {forwardDraft && forwardDraft.targetTopicId === activeTopicId && (
                <div className="chat-composer-banner is-forward">
                  <div className="chat-composer-banner-bar"/>
                  <div className="chat-composer-banner-body">
                    {forwardDraft.info.items.length > 1 && (
                      <>
                        <div className="chat-composer-banner-title">
                          {`Переслать ${forwardDraft.info.count} ${pluralRu(forwardDraft.info.count, ["сообщение", "сообщения", "сообщений"])}`}
                        </div>
                        <div className="chat-composer-banner-fwdlist">
                          {forwardDraft.info.items.slice(0, 6).map((it, i) => (
                            <div key={i} className="chat-composer-banner-fwdrow">
                              {it.attachments.map(att => {
                                const img = isImage(att.mime, att.filename);
                                const vid = isVideo(att.mime, att.filename);
                                return (
                                  <span key={att.id} className="chat-composer-banner-fwdrow-thumb" title={att.filename}>
                                    {img && <img src={chatAttachmentPreviewUrl(att.id)} alt="" draggable={false}/>}
                                    {vid && <img src={chatVideoThumbnailUrl(att.id)} alt="" draggable={false}/>}
                                    {!img && !vid && <Icon name="file" size={16}/>}
                                  </span>
                                );
                              })}
                              <span className="chat-composer-banner-fwdrow-text">
                                <span className="chat-composer-banner-author">{it.author}:</span>{" "}
                                <span className="chat-composer-banner-text">{it.text || "—"}</span>
                              </span>
                            </div>
                          ))}
                          {forwardDraft.info.items.length > 6 && (
                            <div className="chat-composer-banner-fwdmore">и ещё {forwardDraft.info.items.length - 6}</div>
                          )}
                        </div>
                      </>
                    )}
                    {forwardDraft.info.items.length <= 1 && forwardDraft.info.attachments.length > 0 && (
                      <div className="chat-composer-banner-previews">
                        {forwardDraft.info.attachments.map(att => {
                          const img = isImage(att.mime, att.filename);
                          const vid = isVideo(att.mime, att.filename);
                          return (
                            <span
                              key={att.id}
                              className={`chat-composer-banner-preview${img || vid ? " is-media" : " is-file"}`}
                              title={att.filename}
                            >
                              {img && <img src={chatAttachmentPreviewUrl(att.id)} alt="" draggable={false}/>}
                              {vid && <img src={chatVideoThumbnailUrl(att.id)} alt="" draggable={false}/>}
                              {vid && <span className="chat-composer-banner-preview-play"><Icon name="play" size={18}/></span>}
                              {!img && !vid && (
                                <>
                                  <Icon name="file" size={22}/>
                                  <span className="chat-composer-banner-preview-name">{att.filename}</span>
                                  {att.size_bytes != null && <span className="chat-composer-banner-preview-size">{fmtSize(att.size_bytes)}</span>}
                                </>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {forwardDraft.info.items.length <= 1 && (
                      <>
                        <div className="chat-composer-banner-title">Переслать сообщение</div>
                        <div className="chat-composer-banner-line">
                          <span className="chat-composer-banner-author">{forwardDraft.info.author}:</span>
                          <span className="chat-composer-banner-text">{forwardDraft.info.text || "—"}</span>
                        </div>
                      </>
                    )}
                  </div>
                  <button type="button" className="chat-composer-banner-close" onClick={(e) => { e.stopPropagation(); setForwardDraft(null); }} title="Отменить пересылку">
                    <Icon name="x" size={14}/>
                  </button>
                </div>
              )}
              {replyTo && (
                <div className="chat-composer-banner">
                  <div className="chat-composer-banner-bar"/>
                  <button
                    type="button"
                    className="chat-composer-banner-body chat-composer-banner-body-btn"
                    onClick={() => jumpToMessage(replyTo.id)}
                    title="Перейти к сообщению"
                  >
                    {replyTo.attachments && replyTo.attachments.length > 0 && (
                      <div className="chat-composer-banner-previews">
                        {replyTo.attachments.map(att => {
                          const img = isImage(att.mime, att.filename);
                          const vid = isVideo(att.mime, att.filename);
                          return (
                            <span
                              key={att.id}
                              className={`chat-composer-banner-preview${img || vid ? " is-media" : " is-file"}`}
                              title={att.filename}
                            >
                              {img && <img src={chatAttachmentPreviewUrl(att.id)} alt="" draggable={false}/>}
                              {vid && <img src={chatVideoThumbnailUrl(att.id)} alt="" draggable={false}/>}
                              {vid && <span className="chat-composer-banner-preview-play"><Icon name="play" size={18}/></span>}
                              {!img && !vid && (
                                <>
                                  <Icon name="file" size={22}/>
                                  <span className="chat-composer-banner-preview-name">{att.filename}</span>
                                  {att.size_bytes != null && <span className="chat-composer-banner-preview-size">{fmtSize(att.size_bytes)}</span>}
                                </>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <div className="chat-composer-banner-title">
                      {replyTo.quote ? "Цитата" : "Ответ"}
                    </div>
                    <div className="chat-composer-banner-line">
                      <span className="chat-composer-banner-author">{replyTo.author_name}:</span>
                      <span className="chat-composer-banner-text">
                        {replyTo.quote
                          ? <span className="chat-quoted">«{replyTo.quote}»</span>
                          : (replyTo.preview || "—")}
                      </span>
                    </div>
                  </button>
                  <button type="button" className="chat-composer-banner-close" onClick={(e) => { e.stopPropagation(); setReplyTo(null); }} title="Отменить ответ">
                    <Icon name="x" size={14}/>
                  </button>
                </div>
              )}

              {editingId && (
                <div className="chat-composer-banner" style={{ borderLeft: "3px solid var(--brick)" }}>
                  <div className="chat-composer-banner-bar"/>
                  <div className="chat-composer-banner-body">
                    <div className="chat-composer-banner-title" style={{ color: "var(--brick)" }}>
                      <Icon name="edit" size={12}/> Редактирование
                    </div>
                    <div className="chat-composer-banner-line">
                      <span className="chat-composer-banner-text">{(messages.find(m => m.id === editingId)?.text || "").slice(0, 80)}</span>
                    </div>
                  </div>
                  <button type="button" className="chat-composer-banner-close" onClick={cancelEdit} title="Отменить редактирование">
                    <Icon name="x" size={14}/>
                  </button>
                </div>
              )}

              {!composerModalOpen && (uploadingFiles.length > 0 || pendingFiles.length > 0) && (
                <div className="chat-pending-files">
                  {/* Uploading: thumbnail + progress bar + cancel — TG-стиль */}
                  {uploadingFiles.map(u => {
                    const pct = u.total > 0 ? Math.min(100, (u.loaded / u.total) * 100) : 0;
                    return (
                      <div key={u.tempId} className="chat-upload-card">
                        <div className="chat-upload-thumb">
                          {u.isImage && u.thumbUrl && <img src={u.thumbUrl} alt={u.file.name}/>}
                          {u.isVideo && u.thumbUrl && <video src={u.thumbUrl} muted preload="none"/>}
                          {!u.isImage && !u.isVideo && <Icon name="file" size={28}/>}
                          <div className="chat-upload-progress-overlay">
                            <svg viewBox="0 0 36 36" className="chat-upload-ring">
                              <circle cx="18" cy="18" r="15" strokeWidth="2.5" fill="none" stroke="rgba(255,255,255,0.3)"/>
                              <circle
                                cx="18" cy="18" r="15" strokeWidth="2.5" fill="none"
                                stroke="#fff" strokeDasharray={`${(pct / 100) * 94.25} 94.25`}
                                strokeLinecap="round" transform="rotate(-90 18 18)"
                              />
                            </svg>
                            <button
                              type="button"
                              className="chat-upload-cancel"
                              onClick={() => cancelUpload(u.tempId)}
                              title="Отменить"
                            >
                              <Icon name="x" size={14}/>
                            </button>
                          </div>
                        </div>
                        <div className="chat-upload-meta">
                          <div className="chat-upload-name" title={u.file.name}>{u.file.name}</div>
                          <div className="chat-upload-prog-bar">
                            <div className="chat-upload-prog-bar-fill" style={{ width: `${pct}%` }}/>
                          </div>
                          <div className="chat-upload-prog-text">
                            {fmtSize(u.loaded)} / {fmtSize(u.total)} · {pct.toFixed(0)}%
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {/* Ready (загружено) — TG-style thumbnail tiles */}
                  {pendingFiles.map(f => {
                    const img = isImage(f.mime, f.filename);
                    const vid = isVideo(f.mime, f.filename);
                    return (
                      <div key={f.id} className={`chat-pending-tile${img || vid ? " is-media" : " is-file"}`} title={f.filename}>
                        {img && (
                          <img src={chatAttachmentPreviewUrl(f.id)} alt="" className="chat-pending-tile-img" draggable={false}/>
                        )}
                        {vid && (
                          <>
                            <img src={chatVideoThumbnailUrl(f.id)} alt="" className="chat-pending-tile-img" draggable={false}/>
                            <span className="chat-pending-tile-play"><Icon name="play" size={20}/></span>
                          </>
                        )}
                        {!img && !vid && (
                          <div className="chat-pending-tile-file">
                            <Icon name="file" size={28}/>
                            <span className="chat-pending-tile-filename">{f.filename}</span>
                            <span className="chat-pending-tile-filesize">{fmtSize(f.size_bytes)}</span>
                          </div>
                        )}
                        <button
                          type="button"
                          className="chat-pending-tile-remove"
                          onClick={() => removePendingFile(f.id)}
                          title="Убрать"
                        >
                          <Icon name="x" size={12}/>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {sendError && (
                <div
                  role="alert"
                  style={{
                    margin: "0 12px 8px",
                    padding: "8px 10px",
                    borderRadius: 8,
                    color: "#9f2d20",
                    background: "rgba(220, 70, 50, 0.12)",
                    fontSize: 13,
                  }}
                >
                  {sendError}
                </div>
              )}

              <form
                onSubmit={onSubmit}
                className={`chat-form${hasTextareaSelection ? " has-text-selection" : ""}`}
                style={{ position: "relative" }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => {
                    if (e.target.files) void handleFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
                {recState !== "recording" && recState !== "paused" && isBotDm && (
                  <button
                    type="button"
                    className="chat-form-bot-menu"
                    title="Команды бота"
                    onClick={() => {
                      // Открываем popup со ВСЕМИ командами через query="/"
                      setSlashQuery(slashQuery !== null ? null : "/");
                      setSlashIdx(0);
                      textareaRef.current?.focus();
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="3" y1="6"  x2="21" y2="6"/>
                      <line x1="3" y1="12" x2="21" y2="12"/>
                      <line x1="3" y1="18" x2="21" y2="18"/>
                    </svg>
                  </button>
                )}
                {recState !== "recording" && recState !== "paused" && (
                <button
                  type="button"
                  className="chat-form-attach"
                  title="Прикрепить файл"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading || sendMut.isPending}
                >
{isMessengerRoute ? (
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
                  </svg>
                ) : <Icon name="paperclip" size={20}/>}
                </button>
                )}
                {composerPickerOpen && (
                  <div className="chat-emoji-picker is-composer is-tg">
                    <ChatEmojiPicker
                      theme={themeEffective}
                      onSelect={(native) => {
                        insertEmoji(native);
                      }}
                    />
                  </div>
                )}
                {mentionQuery !== null && mentionMatches.length > 0 && (
                  <div className="chat-mention-pop" role="listbox">
                    {mentionMatches.map((u, i) => (
                      <button
                        key={u.id}
                        type="button"
                        ref={i === mentionIdx ? mentionActiveRef : null}
                        className={`chat-mention-item${i === mentionIdx ? " is-active" : ""}`}
                        onMouseDown={(e) => { e.preventDefault(); applyMention(u); }}
                        onMouseEnter={() => setMentionIdx(i)}
                      >
                        <Avatar
                          name={u.display_name || "?"}
                          userId={u.id}
                          avatarFilename={u.avatar_filename ?? null}
                          size={28}
                          color="#AB2B2B"
                        />
                        <div className="chat-mention-text">
                          <span className="chat-mention-name">{u.display_name || "(без имени)"}</span>
                          {u.position && <span className="chat-mention-pos">{u.position}</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {recState !== "recording" && recState !== "paused" && (
                <div
                  className={`chat-form-input-wrap${
                    mentionQuery !== null && mentionMatches.length > 0 ? " is-mentions-open" : ""
                  }`}
                  style={{ position: "relative" }}
                >
                  {isBotDm && (
                    <BotSlashMenu
                      query={slashQuery}
                      selectedIdx={slashIdx}
                      onPick={(cmd) => {
                        if (activeTopicId) {
                          sendMut.mutate({ topicId: activeTopicId, text: cmd, replyToId: null, attachmentIds: [] });
                          setText("");
                        }
                      }}
                      onClose={() => setSlashQuery(null)}
                    />
                  )}
                  {!isBotDm && !drivePickerOpen && !editorPickerOpen && !taskCreatorOpen && !noteToProjectOpen && (
                    <SlashActionMenu
                      query={actionSlashQuery}
                      selectedIdx={actionSlashIdx}
                      onPick={(a: SlashAction) => {
                        if (a === "drive") setDrivePickerOpen(true);
                        // «editor» — отдельный пикер /api/docs/list (уже фильтрует
                        // по доступу + исключает чужие DM).
                        else if (a === "editor") setEditorPickerOpen(true);
                        else if (a === "task") setTaskCreatorOpen(true);
                        else if (a === "project_note") setNoteToProjectOpen(true);
                        setText("");
                        setActionSlashQuery(null);
                      }}
                      onClose={() => setActionSlashQuery(null)}
                    />
                  )}
                  {!isBotDm && drivePickerOpen && (
                    <DriveAttachmentPicker
                      inline
                      topics={topics}
                      onClose={() => setDrivePickerOpen(false)}
                      onPick={async (f: DriveFile) => {
                        // КЛОНИРУЕМ chat_attachment перед прикреплением: оригинал
                        // может быть загружен другим юзером и/или уже привязан
                        // к чужому сообщению — тогда backend send-message
                        // отклоняет его («Один или несколько файлов недоступны»).
                        try {
                          const fresh = await cloneChatAttachment(f.attachment_id);
                          setPendingFiles(prev => [...prev, fresh]);
                        } catch (e) {
                          alert(`Не удалось прикрепить: ${(e as Error).message ?? e}`);
                        } finally {
                          setDrivePickerOpen(false);
                        }
                      }}
                    />
                  )}
                  {!isBotDm && editorPickerOpen && (
                    <EditorFilePicker
                      onClose={() => setEditorPickerOpen(false)}
                      onPick={async (f) => {
                        try {
                          const fresh = await cloneChatAttachment(f.attachment_id);
                          setPendingFiles(prev => [...prev, fresh]);
                        } catch (e) {
                          alert(`Не удалось прикрепить: ${(e as Error).message ?? e}`);
                        } finally {
                          setEditorPickerOpen(false);
                        }
                      }}
                    />
                  )}
                  {!isBotDm && taskCreatorOpen && (
                    <TaskCreatorModal
                      inline
                      topics={topics}
                      sourceTopic={activeTopic ?? null}
                      onClose={() => setTaskCreatorOpen(false)}
                      onCreated={(task, assignees) => {
                        setTaskCreatorOpen(false);
                        if (activeTopicId) {
                          const mentions = assignees.length > 0
                            ? assignees.map(a => `@${(a.display_name ?? "").split(" ")[0]}`).join(", ")
                            : "";
                          const deadlinePart = task.deadline_at
                            ? ` Дедлайн: ${new Date(task.deadline_at).toLocaleString("ru-RU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}.`
                            : "";
                          const targetName = topics.find(t => t.id === task.topic_id)?.name ?? "Календарь";
                          const msg = `📋 Новая задача${mentions ? ` для ${mentions}` : ""}: «${task.title}».${deadlinePart} В «${targetName}»`;
                          sendMut.mutate({
                            topicId: activeTopicId, text: msg, replyToId: null, attachmentIds: [],
                          });
                        }
                      }}
                    />
                  )}
                  {!isBotDm && noteToProjectOpen && (
                    <NoteToProjectModal
                      onClose={() => setNoteToProjectOpen(false)}
                      onSentToChat={async (chatText: string) => {
                        if (!activeTopicId) return;
                        sendMut.mutate({
                          topicId: activeTopicId, text: chatText,
                          replyToId: null, attachmentIds: [],
                        });
                      }}
                    />
                  )}
                <RichComposer
                  ref={textareaRef}
                  className="chat-form-input"
                  onSelectionChange={setHasTextareaSelection}
                  value={editingId !== null ? editingText : text}
                  onBlur={() => setHasTextareaSelection(false)}
                  onChange={(value, selStart, selEnd) => {
                    if (editingId !== null) setEditingText(value);
                    else setText(value);
                    detectMentionAtCursor(value, selStart);
                    pingTyping();
                    setHasTextareaSelection(selStart !== selEnd);
                    // Bot slash-команды: открываем popup если ввод начинается с "/"
                    // и нет пробелов (после пробела это уже не команда).
                    if (isBotDm && editingId === null) {
                      const v = value;
                      if (v.startsWith("/") && !v.includes(" ")) {
                        setSlashQuery(v);
                        setSlashIdx(0);
                      } else {
                        setSlashQuery(null);
                      }
                    }
                    // Generic slash-actions: «/» в начале любого чата (не бот-DM)
                    // открывает меню с действиями (Файл с диска / Поставить задачу).
                    if (!isBotDm && editingId === null) {
                      const v = value;
                      if (v.startsWith("/") && !v.includes(" ")) {
                        setActionSlashQuery(v);
                        setActionSlashIdx(0);
                      } else {
                        setActionSlashQuery(null);
                      }
                    }
                  }}
                  onKeyDown={(e) => {
                    // Generic action-slash активен → стрелки/Enter навигируют
                    if (actionSlashQuery !== null) {
                      const items = filterSlashActions(actionSlashQuery);
                      if (items.length > 0) {
                        if (e.key === "ArrowDown") {
                          e.preventDefault();
                          setActionSlashIdx(i => (i + 1) % items.length);
                          return;
                        }
                        if (e.key === "ArrowUp") {
                          e.preventDefault();
                          setActionSlashIdx(i => (i - 1 + items.length) % items.length);
                          return;
                        }
                        if (e.key === "Enter" || e.key === "Tab") {
                          e.preventDefault();
                          const a = items[actionSlashIdx]?.action;
                          if (a) {
                            if (a === "drive") setDrivePickerOpen(true);
                            else if (a === "editor") setEditorPickerOpen(true);
                            else if (a === "task") setTaskCreatorOpen(true);
                            else if (a === "project_note") setNoteToProjectOpen(true);
                            setText("");
                          }
                          setActionSlashQuery(null);
                          return;
                        }
                        if (e.key === "Escape") { e.preventDefault(); setActionSlashQuery(null); return; }
                      }
                    }
                    // Bot slash-popup активен → стрелки навигируют, Enter/Tab отправляют команду
                    if (slashQuery !== null) {
                      const items = filterCommands(slashQuery);
                      if (items.length > 0) {
                        if (e.key === "ArrowDown") {
                          e.preventDefault();
                          setSlashIdx(i => (i + 1) % items.length);
                          return;
                        }
                        if (e.key === "ArrowUp") {
                          e.preventDefault();
                          setSlashIdx(i => (i - 1 + items.length) % items.length);
                          return;
                        }
                        if (e.key === "Enter" || e.key === "Tab") {
                          e.preventDefault();
                          const cmd = items[slashIdx]?.cmd;
                          if (cmd && activeTopicId) {
                            sendMut.mutate({ topicId: activeTopicId, text: cmd, replyToId: null, attachmentIds: [] });
                            setText("");
                          }
                          setSlashQuery(null);
                          return;
                        }
                        if (e.key === "Escape") { e.preventDefault(); setSlashQuery(null); return; }
                      }
                    }
                    // Mention-dropdown активен → стрелки навигируют, Enter/Tab
                    // применяют выделенного юзера (как TG/Slack/Discord). Esc
                    // закрывает dropdown без отправки. Если пользователь хочет
                    // отправить с открытым autocomplete'ом — Esc → Enter.
                    if (mentionQuery !== null && mentionMatches.length > 0) {
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setMentionIdx(i => (i + 1) % mentionMatches.length);
                        return;
                      }
                      if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setMentionIdx(i => (i - 1 + mentionMatches.length) % mentionMatches.length);
                        return;
                      }
                      if (e.key === "Enter" || e.key === "Tab") {
                        e.preventDefault();
                        applyMention(mentionMatches[mentionIdx]);
                        return;
                      }
                      if (e.key === "Escape") { e.preventDefault(); setMentionQuery(null); return; }
                    }
                    onKeyDown(e);
                  }}
                  onKeyUp={() => {
                    const ta = textareaRef.current;
                    if (ta) detectMentionAtCursor(ta.value, ta.selectionStart);
                  }}
                  placeholder={replyTo
                    ? `Ответ ${replyTo.author_name}…`
                    : `Сообщение…`}
                  title="Enter — отправить, Shift+Enter — перенос строки. @ — упомянуть юзера"
                  autoFocus
                />
                  {!isMessengerRoute && (
                    <button
                      type="button"
                      className="chat-form-emoji-inside"
                      data-act="composer-emoji"
                      title="Эмодзи"
                      onClick={() => setComposerPickerOpen((v) => !v)}
                    >
                      <Icon name="smile" size={20}/>
                    </button>
                  )}
                </div>
                )}
                {recState !== "recording" && recState !== "paused" && isMessengerRoute && (
                  <div className="chat-form-actions-right">
                    <button
                      type="button"
                      className="chat-form-emoji-inside"
                      data-act="composer-emoji"
                      title="Эмодзи"
                      onClick={() => setComposerPickerOpen((v) => !v)}
                    >
                      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/>
                        <circle cx="8" cy="10" r="1.5" fill="currentColor" stroke="none"/>
                        <circle cx="16" cy="10" r="1.5" fill="currentColor" stroke="none"/>
                        <path d="M8 15s1.5 2 4 2 4-2 4-2"/>
                      </svg>
                    </button>
                    {recState === "idle" && !canSend && editingId === null && (
                      <button
                        type="button"
                        className="chat-form-mic"
                        onClick={startRecording}
                        title="Записать голосовое сообщение"
                      >
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="9" y="2" width="6" height="12" rx="3"/>
                          <path d="M19 11v1a7 7 0 01-14 0v-1"/>
                          <line x1="12" y1="19" x2="12" y2="22"/>
                          <line x1="8" y1="22" x2="16" y2="22"/>
                        </svg>
                      </button>
                    )}
                  </div>
                )}
                {!isMessengerRoute && recState === "idle" && !canSend && editingId === null && (
                  <button
                    type="button"
                    className="btn chat-form-send chat-form-mic"
                    onClick={startRecording}
                    title="Записать голосовое сообщение"
                  >
{isMessengerRoute ? (
                      <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="2" width="6" height="12" rx="3"/>
                        <path d="M19 11v1a7 7 0 01-14 0v-1"/>
                        <line x1="12" y1="19" x2="12" y2="22"/>
                        <line x1="8" y1="22" x2="16" y2="22"/>
                      </svg>
                    ) : <Icon name="mic" size={20}/>}
                  </button>
                )}
                {(recState === "recording" || recState === "paused") && (
                  <>
                    <span className={`chat-rec-indicator${recState === "paused" ? " is-paused" : ""}`}>
                      <span className="chat-rec-dot"/>
                      <span className="chat-rec-time">
                        {String(Math.floor(recElapsed / 60)).padStart(2, "0")}
                        :{String(recElapsed % 60).padStart(2, "0")}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="chat-form-rec-cancel-text"
                      onClick={cancelRecording}
                      title="Отменить запись"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="chat-form-rec-pause"
                      onClick={recState === "paused" ? resumeRecording : pauseRecording}
                      title={recState === "paused" ? "Продолжить запись" : "Пауза"}
                    >
                      {recState === "paused" ? (
                        <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor"><path d="M5 3l12 7-12 7V3z"/></svg>
                      ) : (
                        <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor"><rect x="5" y="3" width="4" height="14" rx="1"/><rect x="11" y="3" width="4" height="14" rx="1"/></svg>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary chat-form-send"
                      onClick={stopAndSendRecording}
                      title="Отправить голосовое"
                    >
                      <Icon name="send" size={16}/>
                    </button>
                  </>
                )}
                {recState === "uploading" && (
                  <span className="chat-rec-indicator">
                    <Icon name="clock" size={14}/> загрузка…
                  </span>
                )}
                {/* Часы убраны: отложить = right-click (desktop) / long-press (mobile)
                    на «Отправить» → всплывашка «Отложить сообщение». */}
                {recState === "idle" && (canSend || editingId !== null) && (
                  <button
                    type="submit"
                    className="btn btn-primary chat-form-send"
                    disabled={!canSend}
                    title="Отправить (зажать — отложить)"
                    onMouseDown={(e) => {
                      // Клик мышью не должен уводить фокус из textarea — иначе
                      // после отправки приходится кликать в поле заново.
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      if (sendLongPressFired.current) {
                        e.preventDefault();
                        sendLongPressFired.current = false;
                      }
                    }}
                    onContextMenu={(e) => {
                      // Right-click / context-menu на «Отправить» — тоже открывает
                      // «Отложить сообщение» (аналог long-press на мобиле).
                      e.preventDefault();
                      e.stopPropagation();
                      if (editingId !== null) return;
                      setSendActionMenuOpen(true);
                    }}
                    onTouchStart={() => {
                      sendTouchMoved.current = false;
                      if (!isMessengerRoute) return;
                      if (editingId !== null) return;
                      sendLongPressFired.current = false;
                      if (sendLongPressTimer.current) window.clearTimeout(sendLongPressTimer.current);
                      sendLongPressTimer.current = window.setTimeout(() => {
                        sendLongPressFired.current = true;
                        if ("vibrate" in navigator) navigator.vibrate?.(20);
                        setSendActionMenuOpen(true);
                      }, 500);
                    }}
                    onTouchMove={() => {
                      sendTouchMoved.current = true;
                      if (sendLongPressTimer.current) {
                        window.clearTimeout(sendLongPressTimer.current);
                        sendLongPressTimer.current = null;
                      }
                    }}
                    onTouchEnd={(e) => {
                      if (sendLongPressTimer.current) {
                        window.clearTimeout(sendLongPressTimer.current);
                        sendLongPressTimer.current = null;
                      }
                      // Обычный тап (не long-press «Отложить», не свайп): шлём прямо
                      // из touchend и гасим синтетический click — так iOS не уводит
                      // фокус из textarea, клавиатура остаётся открытой для следующего.
                      if (sendLongPressFired.current || sendTouchMoved.current) return;
                      if (!canSend) return;
                      e.preventDefault();
                      submit();
                      textareaRef.current?.focus({ preventScroll: true });
                    }}
                  >
                    {sendMut.isPending
                      ? "…"
                      : isMessengerRoute
                        ? (
                          <svg viewBox="0 0 24 24" width="18" height="18" style={{ display: "block" }}>
                            <path fill="currentColor" d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                          </svg>
                        )
                        : <>
                            <span className="chat-form-send-text">Отправить</span>
                            <span className="chat-form-send-icon">➤</span>
                          </>
                    }
                  </button>
                )}
                {sendActionMenuOpen && (
                  <>
                    <div
                      onClick={() => setSendActionMenuOpen(false)}
                      onTouchStart={() => setSendActionMenuOpen(false)}
                      style={{ position: "fixed", inset: 0, zIndex: 9998, background: "transparent" }}
                    />
                    <div
                      style={{
                        position: "fixed", right: 16, bottom: 76,
                        background: "#fff", borderRadius: 12, padding: 4,
                        boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
                        zIndex: 9999, minWidth: 220,
                      }}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSendActionMenuOpen(false);
                          setSchedPickerOpen(true);
                        }}
                        style={{
                          display: "flex", alignItems: "center", gap: 10, width: "100%",
                          padding: "12px 14px", background: "transparent", border: "none",
                          cursor: "pointer", fontFamily: "inherit", fontSize: 14,
                          color: "var(--ink, #131313)", textAlign: "left", borderRadius: 8,
                        }}
                      >
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        <span>Отложить сообщение</span>
                      </button>
                    </div>
                  </>
                )}
                {/* Format-toolbar: вынесен как sibling input-wrap, якорится к
                    .chat-form (position:relative) — занимает всю ширину строки
                    с composer'ом. CSS: .chat-form:focus-within → виден.
                    На мобиле — расширяется на 100vw. */}
                <div className="chat-format-toolbar" aria-hidden="false">
                  <button
                    type="button"
                    className="chat-format-btn"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => wrapSelectionInTextarea(textareaRef.current, "**", editingId, setText, setEditingText)}
                    aria-label="Жирный"
                    title="**Жирный**"
                  ><b>B</b></button>
                  <button
                    type="button"
                    className="chat-format-btn"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => wrapSelectionInTextarea(textareaRef.current, "_", editingId, setText, setEditingText)}
                    aria-label="Курсив"
                    title="_Курсив_"
                  ><i>I</i></button>
                  <button
                    type="button"
                    className="chat-format-btn"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => wrapSelectionInTextarea(textareaRef.current, "__", editingId, setText, setEditingText)}
                    aria-label="Подчёркнутый"
                    title="__Подчёркнутый__"
                  ><u>U</u></button>
                  <button
                    type="button"
                    className="chat-format-btn"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => wrapSelectionInTextarea(textareaRef.current, "~~", editingId, setText, setEditingText)}
                    aria-label="Зачёркнутый"
                    title="~~Зачёркнутый~~"
                  ><s>S</s></button>
                  <button
                    type="button"
                    className="chat-format-btn"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => wrapSelectionInTextarea(textareaRef.current, "`", editingId, setText, setEditingText)}
                    aria-label="Код"
                    title="`код`"
                  ><code>{'</>'}</code></button>
                  <button
                    type="button"
                    className="chat-format-btn"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => wrapSelectionInTextarea(textareaRef.current, "||", editingId, setText, setEditingText)}
                    aria-label="Спойлер"
                    title="||Спойлер||"
                  >▓</button>
                </div>
              </form>
            </div>
          </>
        )}
      </section>

      {mediaModalOpen && activeTopic && (
        <TopicMediaModal
          topicId={activeTopic.id}
          topicName={activeTopic.name}
          onClose={() => setMediaModalOpen(false)}
          onJump={(messageId) => {
            setMediaModalOpen(false);
            // Сбрасываем highlight сначала — иначе если прыгаем к тому же
            // сообщению, что и в прошлый раз, setJumpTargetId не триггерит
            // re-render и анимация подсветки не запускается.
            setJumpTargetId(null);
            // 150ms — даём модалке закрыться/анимироваться, потом скроллим
            setTimeout(() => jumpToMessage(messageId), 150);
          }}
        />
      )}

      {selfProfileOpen && (
        <ProfileSelfModal onClose={() => setSelfProfileOpen(false)}/>
      )}
      {/* PinnedSidePanel рендерится inline в основном flow рядом с MessengerRightPanel */}
      <MobileProfileFab/>

      {profileLightboxId && (
        <ProfileLightbox
          userId={profileLightboxId}
          onClose={() => setProfileLightboxId(null)}
          myUserId={myUserId}
          onOpenDm={async (partnerUserId) => {
            try {
              const topic = await getOrCreateDm(partnerUserId);
              await qc.invalidateQueries({ queryKey: ["chat/topics"] });
              setActiveTopicId(topic.id);
              setProfileLightboxId(null);
            } catch (e) {
              alert(`Не удалось открыть личный чат: ${(e as Error).message}`);
            }
          }}
        />
      )}

      {filePreview && (
        <FilePreviewModal
          url={chatAttachmentUrl(filePreview.id)}
          filename={filePreview.filename}
          mime={filePreview.mime}
          attachmentId={filePreview.id}
          onSaveToDrive={(p) => setSaveToDrive([p])}
          onClose={() => setFilePreview(null)}
        />
      )}
      {saveToDrive && (
        <SaveToDriveModal
          topics={topics}
          items={saveToDrive}
          onClose={() => setSaveToDrive(null)}
        />
      )}

      {forwardPickerOpen && (
        <ForwardPickerModal
          topics={topics}
          sourceMessageIds={Array.from(selectedIds)}
          sourceAttachmentIds={Array.from(selectedAttachmentIds)}
          currentTopicId={activeTopicId ?? undefined}
          preview={buildForward(Array.from(selectedIds), Array.from(selectedAttachmentIds)).info}
          onPickSingle={(targetId) => openForwardInTopic(targetId, Array.from(selectedIds), Array.from(selectedAttachmentIds))}
          onClose={() => setForwardPickerOpen(false)}
          onSent={(targetId) => {
            setForwardPickerOpen(false);
            setSelectionMode(false);
            setSelectedIds(new Set());
            setSelectedAttachmentIds(new Set());
            qc.invalidateQueries({ queryKey: ["chat/messages", targetId] });
            qc.invalidateQueries({ queryKey: ["chat/topics"] });
            if (targetId !== activeTopicId) setActiveTopicId(targetId);
          }}
        />
      )}

      {composerModalOpen && (
        <ImageUploadModal
          uploadingFiles={uploadingFiles}
          pendingFiles={pendingFiles}
          text={text}
          onTextChange={setText}
          onCancel={cancelAllPending}
          onSend={sendFromModal}
          onAddMore={() => fileInputRef.current?.click()}
          onRemoveUploading={cancelUpload}
          onRemovePending={removePendingFile}
          isSending={sendMut.isPending}
        />
      )}

      {chatLightboxAid !== null && chatLightboxIdx >= 0 && (() => {
        const cur = topicMedia[chatLightboxIdx];
        const setIdx = (i: number) => {
          const id = topicMediaIds[i];
          if (id !== undefined) setChatLightboxAid(id);
        };
        const onPrev = () => { if (chatLightboxIdx > 0) setIdx(chatLightboxIdx - 1); };
        const onNext = () => { if (chatLightboxIdx < topicMedia.length - 1) setIdx(chatLightboxIdx + 1); };
        const hasPrev = chatLightboxIdx > 0;
        const hasNext = chatLightboxIdx < topicMedia.length - 1;
        if (cur.kind === "video") {
          return (
            <VideoLightbox
              src={cur.src}
              poster={chatVideoThumbnailUrl(cur.id)}
              filename={cur.filename}
              attachmentId={cur.id}
              onSaveToDrive={(p) => setSaveToDrive([p])}
              onClose={() => setChatLightboxAid(null)}
              onPrev={onPrev}
              onNext={onNext}
              hasPrev={hasPrev}
              hasNext={hasNext}
            />
          );
        }
        return (
          <PhotoLightbox
            images={topicImages}
            idx={chatLightboxIdx}
            setIdx={setIdx}
            onSaveToDrive={(p) => setSaveToDrive([p])}
            onClose={() => setChatLightboxAid(null)}
          />
        );
      })()}

      {/* Пикер даты/времени для отложенной отправки */}
      {schedPickerOpen && activeTopicId && (
        <ScheduleMessagePickerModal
          initial={schedEditing?.scheduled_for}
          onClose={() => { setSchedPickerOpen(false); setSchedEditing(null); }}
          onSelect={(utcIso) => {
            setSchedPickerOpen(false);
            if (schedEditing) {
              editSchedMut.mutate({ sid: schedEditing.id, scheduledFor: utcIso });
              setSchedEditing(null);
            } else {
              const trimmed = text.trim();
              if (!trimmed && pendingFiles.length === 0) {
                alert("Введите текст или прикрепите файл");
                return;
              }
              createSchedMut.mutate({
                topicId: activeTopicId,
                text: trimmed,
                replyToId: replyTo?.id ?? null,
                attachmentIds: pendingFiles.map(f => f.id),
                scheduledFor: utcIso,
              });
            }
          }}
        />
      )}

      {/* Список отложенных в текущем топике */}
      {schedListOpen && activeTopicId && (
        <ScheduledListModal
          items={scheduledQ.data || []}
          onClose={() => setSchedListOpen(false)}
          onCancel={(sid) => cancelSchedMut.mutate(sid)}
          onSendNow={(sid) => sendSchedNowMut.mutate(sid)}
          onEditTime={(item) => { setSchedEditing(item); setSchedPickerOpen(true); setSchedListOpen(false); }}
        />
      )}

      {/* Глобальный emoji-picker реакций (position: fixed по координатам кнопки —
          чтобы overflow chat-feed не обрезал на верхних сообщениях) */}
      {messengerCtxMenu && (() => {
        const m = messages.find((x) => x.id === messengerCtxMenu.messageId);
        if (!m) return null;
        return (
          <MessengerContextMenu
            message={m}
            x={messengerCtxMenu.x}
            y={messengerCtxMenu.y}
            myUserId={myUserId}
            isSuper={!!user?.is_superuser}
            quotedText={messengerCtxMenu.quote}
            onClose={() => { setMessengerCtxMenu(null); lastSelectionTextRef.current = ""; }}
            onReact={(emo) => pickReaction(m.id, emo)}
            onMoreReactions={(anchorEl) => {
              const rect = anchorEl.getBoundingClientRect();
              setMessengerCtxMenu(null);
              openReactionPicker(m.id, rect);
            }}
            onReply={() => startReply(m)}
            onQuoteReply={(quote) => startReply(m, quote)}
            onCopyText={() => copyMessageText(m.text)}
            onEdit={() => startEdit(m)}
            onPin={() => togglePin(m)}
            onForward={() => {
              setForwardPicker({ messageIds: [m.id], sourceTopicId: m.topic_id });
            }}
            onSaveToDrive={(items) => setSaveToDrive(items)}
            attachmentId={messengerCtxMenu.attachmentId}
            onDownloadAttachment={(att) => { void downloadChatAttachment({ attachmentId: att.attachmentId, filename: att.filename }); }}
            onDelete={() => confirmDelete(m.id)}
            onRestore={() => restoreMut.mutate(m.id)}
            onSelect={() => {
              setSelectionMode(true);
              setSelectedIds(new Set([m.id]));
            }}
            onJumpToProfile={(uid) => openUserProfile(uid)}
            onShowReadBy={(users) => setReadByModal(users)}
          />
        );
      })()}
      {mobileSearchOpen && (
        <div ref={mobileSearchRef} className={`mobile-search-overlay${mobileSearchClosing ? " is-closing" : ""}`}>
          <div className="mobile-search-head">
            <button
              type="button"
              className="mobile-search-back"
              onClick={closeMobileSearch}
              aria-label="Закрыть поиск"
            >
              <Icon name="chevron_l" size={20}/>
            </button>
            <input
              autoFocus
              className="mobile-search-input"
              type="search"
              placeholder="Поиск по сообщениям…"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
            />
            {searchQ && (
              <button
                type="button"
                className="mobile-search-clear"
                onClick={() => setSearchQ("")}
                aria-label="Очистить"
              >
                <Icon name="x" size={16}/>
              </button>
            )}
          </div>
          <div className="mobile-search-body">
            {searchQ.trim().length < 2 ? (
              <div className="mobile-search-hint">Введите минимум 2 символа</div>
            ) : searchGlobalQ.isFetching ? (
              <div className="mobile-search-hint">Поиск…</div>
            ) : (searchGlobalQ.data ?? []).length === 0 ? (
              <div className="mobile-search-hint">Ничего не найдено</div>
            ) : (
              (searchGlobalQ.data ?? []).map((hit) => {
                const topic = (topicsQ.data ?? []).find(t => t.id === hit.topic_id);
                const isDm = topic?.kind === "dm";
                const partner = (topic as any)?.dm_partner;
                const avaUrl = isDm && partner ? userAvatarUrl(partner.user_id, partner.avatar_filename ?? null) : null;
                const initial = (hit.topic_name || "?").trim().charAt(0).toUpperCase();
                const colors = ["#4fae4e","#3390ec","#9c7ce5","#00acc1","#e85c5c","#e99a12"];
                const seedSrc = String(isDm && partner ? partner.user_id : hit.topic_id);
                const color = colors[seedSrc.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % colors.length];
                return (
                  <button
                    key={hit.message_id}
                    type="button"
                    className="mobile-search-hit"
                    onClick={() => { gotoSearchHit(hit); setMobileSearchOpen(false); }}
                  >
                    <div className="mobile-search-hit-ava">
                      {avaUrl
                        ? <img src={avaUrl} alt=""/>
                        : <span className="mobile-search-hit-ava-stub" style={{ background: color }}>{initial}</span>
                      }
                    </div>
                    <div className="mobile-search-hit-body">
                      <div className="mobile-search-hit-head">
                        <span className="mobile-search-hit-name">{hit.topic_name}</span>
                        <span className="mobile-search-hit-time">{fmtSidebarDate(hit.created_at)}</span>
                      </div>
                      <div className="mobile-search-hit-snippet">
                        <span className="mobile-search-hit-author">{hit.author_name}:</span> {hit.snippet}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
      {readByModal && (
        <div className="readby-modal-backdrop" onClick={() => setReadByModal(null)}>
          <div className="readby-modal" onClick={(e) => e.stopPropagation()}>
            <div className="readby-modal-head">
              <Icon name="eye" size={18}/>
              <div className="readby-modal-title">Прочитали ({readByModal.length})</div>
              <button type="button" className="readby-modal-close" onClick={() => setReadByModal(null)} aria-label="Закрыть">
                <Icon name="x" size={18}/>
              </button>
            </div>
            <div className="readby-modal-list">
              {readByModal.map((u) => {
                const url = userAvatarUrl(u.user_id, u.avatar_filename);
                const initial = (u.display_name || "?").trim().charAt(0).toUpperCase();
                const colors = ["#4fae4e","#3390ec","#9c7ce5","#00acc1","#e85c5c","#e99a12"];
                const color = colors[u.user_id.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % colors.length];
                return (
                  <button
                    key={u.user_id}
                    type="button"
                    className="readby-modal-person"
                    onClick={() => {
                      setReadByModal(null);
                      openUserProfile(u.user_id);
                    }}
                  >
                    {url
                      ? <img src={url} alt=""/>
                      : <span className="readby-modal-person-stub" style={{ background: color }}>{initial}</span>
                    }
                    <span>{u.display_name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {reactionPicker && (() => {
        const m = messages.find((x) => x.id === reactionPicker.messageId);
        if (!m) return null;
        return (
          <div
            className="chat-emoji-picker is-fixed"
            style={{ top: reactionPicker.top, left: reactionPicker.left }}
            ref={(el) => { if (el) { try { twemoji.parse(el, { folder: "svg", ext: ".svg" }); } catch {} } }}
          >
            {REACTION_EMOJI.map((emo) => {
              const myReaction = m.reactions?.find((r) => r.emoji === emo && r.mine);
              return (
                <button
                  key={emo}
                  type="button"
                  className={`chat-emoji-pick-btn${myReaction ? " is-active" : ""}`}
                  onClick={() => pickReaction(m.id, emo)}
                >
                  <Emoji native={emo} size="1.4em"/>
                </button>
              );
            })}
          </div>
        );
      })()}
      {topicCtxMenu && (() => {
        const t = (topicsQ.data ?? []).find(x => x.id === topicCtxMenu.topicId);
        if (!t) return null;
        const isSuper = !!user?.is_superuser;
        const isDm = t.kind === "dm";
        const isPrivate = t.kind === "private_group";
        const isDriveOrTasks = t.kind === "drive" || t.kind === "tasks" || t.kind === "group" || t.kind === "docs" || t.kind === "reports" || t.kind === "focal" || t.kind === "visits_schedule" || t.kind === "visits_funnel";
        const canLeave = isDm || isPrivate;
        const canDelete = isSuper;
        const MENU_W = 240, MENU_H = 240;
        const vw = window.innerWidth, vh = window.innerHeight;
        const left = Math.min(topicCtxMenu.x, vw - MENU_W - 8);
        const top = Math.min(topicCtxMenu.y, vh - MENU_H - 8);
        return (
          <div
            className="chat-context-menu"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed", top, left, zIndex: 1400,
              minWidth: MENU_W,
            }}
          >
            {/* Для drive/tasks подтопиков — ТОЛЬКО управление доступом.
                Остальные пункты (pin/unread/folder/leave/delete) убираем,
                чтобы не загромождать UX. */}
            {!isDriveOrTasks && (
              <>
                <button
                  type="button"
                  className="chat-context-item"
                  onClick={() => {
                    pinTopicMut.mutate({ id: t.id, pin: !t.is_pinned });
                    setTopicCtxMenu(null);
                  }}
                >
                  <span className="chat-context-item-icon">
                    <Icon name="pin" size={16}/>
                  </span>
                  {t.is_pinned ? "Открепить у меня" : "Закрепить у меня"}
                </button>
                {isSuper && (
                  <button
                    type="button"
                    className="chat-context-item"
                    onClick={() => {
                      const pin = !t.is_pinned;
                      const msg = pin
                        ? `Закрепить «${t.name}» у ВСЕХ сотрудников?`
                        : `Открепить «${t.name}» у ВСЕХ сотрудников?`;
                      if (confirm(msg)) {
                        pinTopicForAllMut.mutate({ id: t.id, pin });
                      }
                      setTopicCtxMenu(null);
                    }}
                    title="Только для админа"
                  >
                    <span className="chat-context-item-icon">
                      <Icon name="pin" size={16}/>
                    </span>
                    {t.is_pinned ? "Открепить у всех" : "Закрепить у всех"}
                  </button>
                )}
                <button
                  type="button"
                  className="chat-context-item"
                  onClick={() => {
                    markUnreadMut.mutate(t.id);
                    setTopicCtxMenu(null);
                  }}
                >
                  <span className="chat-context-item-icon">
                    <Icon name="eye_off" size={16}/>
                  </span>
                  Отметить непрочитанным
                </button>
              </>
            )}
            {(isPrivate || isDriveOrTasks) && (
              <button
                type="button"
                className="chat-context-item"
                onClick={() => {
                  // Switch active topic to this one then open members modal
                  if (activeTopicId !== t.id) setActiveTopicId(t.id);
                  setMembersModalOpen(true);
                  setTopicCtxMenu(null);
                }}
              >
                <span className="chat-context-item-icon">
                  <Icon name="users" size={16}/>
                </span>
                Управление доступом
              </button>
            )}
            {isDriveOrTasks ? null : (foldersQ.data ?? []).length === 0 ? null : (
              <>
                <button
                  ref={folderSubBtnRef}
                  type="button"
                  className="chat-context-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    if (!folderSubmenuOpen) {
                      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                      // submenu рисуем справа от пункта; если не влезает — слева
                      const SUB_W = 220;
                      const left = rect.right + SUB_W + 8 < window.innerWidth
                        ? rect.right + 4
                        : Math.max(8, rect.left - SUB_W - 4);
                      setFolderSubPos({ top: rect.top, left });
                    }
                    setFolderSubmenuOpen(v => !v);
                  }}
                  style={{ justifyContent: "space-between" }}
                >
                  <span className="chat-context-item-icon">
                    <Icon name="folder" size={16}/>
                  </span>
                  <span style={{ flex: 1 }}>Добавить в папку</span>
                  <span style={{ opacity: 0.5 }}>▸</span>
                </button>
                {folderSubmenuOpen && folderSubPos && (
                  <div
                    className="chat-context-menu"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.nativeEvent.stopImmediatePropagation();
                    }}
                    style={{
                      position: "fixed",
                      top: folderSubPos.top, left: folderSubPos.left,
                      zIndex: 1401,
                      minWidth: 200,
                    }}
                  >
                    {(foldersQ.data ?? []).map(f => {
                      const inFolder = f.topic_ids.includes(t.id);
                      return (
                        <button
                          key={f.id}
                          type="button"
                          className="chat-context-item"
                          onClick={() => {
                            if (inFolder) removeFromFolderMut.mutate({ folderId: f.id, topicId: t.id });
                            else addToFolderMut.mutate({ folderId: f.id, topicId: t.id });
                            setTopicCtxMenu(null);
                          }}
                          style={{ justifyContent: "space-between" }}
                        >
                          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            {(() => {
                              const parsed = parseFolderIcon(f.icon);
                              if (parsed.kind === "svg") {
                                return (
                                  <svg viewBox="0 0 24 24" width={16} height={16} fill="currentColor" aria-hidden="true">
                                    <path d={parsed.def.path}/>
                                  </svg>
                                );
                              }
                              if (parsed.kind === "emoji") {
                                return <span style={{ fontSize: 16 }}>{parsed.emoji}</span>;
                              }
                              return null;
                            })()}
                            {f.name}
                          </span>
                          {inFolder && <span style={{ color: "var(--brick)" }}>✓</span>}
                        </button>
                      );
                    })}
                    <div style={{ borderTop: "1px solid var(--line)", margin: "4px 0" }}/>
                    <button
                      type="button"
                      className="chat-context-item"
                      onClick={async () => {
                        const name = window.prompt("Название новой папки:");
                        if (!name?.trim()) return;
                        try {
                          const f = await createChatFolder({ name: name.trim() });
                          await addTopicToFolder(f.id, t.id);
                          await qc.invalidateQueries({ queryKey: ["chat/folders"] });
                        } catch (err) {
                          alert((err as Error).message);
                        }
                        setTopicCtxMenu(null);
                      }}
                    >
                      <span className="chat-context-item-icon">
                        <Icon name="plus" size={16}/>
                      </span>
                      Создать новую папку…
                    </button>
                  </div>
                )}
              </>
            )}
            {canLeave && !isDriveOrTasks && (
              <button
                type="button"
                className="chat-context-item is-danger"
                onClick={() => {
                  setTopicCtxMenu(null);
                  const msg = isDm
                    ? "Скрыть этот личный чат? (история останется у собеседника)"
                    : `Покинуть «${t.name}»?`;
                  if (confirm(msg)) leaveTopicMut.mutate(t.id);
                }}
              >
                <span className="chat-context-item-icon">
                  <Icon name={isDm ? "archive" : "external"} size={16}/>
                </span>
                {isDm ? "Скрыть чат" : "Покинуть"}
              </button>
            )}
            {canDelete && !isDm && !isDriveOrTasks && (
              <button
                type="button"
                className="chat-context-item is-danger"
                onClick={() => {
                  setTopicCtxMenu(null);
                  if (confirm(`Удалить «${t.name}» и все сообщения? Необратимо.`)) {
                    deleteTopicMut.mutate(t.id);
                  }
                }}
              >
                <span className="chat-context-item-icon">
                  <Icon name="trash" size={16}/>
                </span>
                Удалить чат
              </button>
            )}
          </div>
        );
      })()}
      {isMessengerRoute && profileDrawerTopic && (
        <ProfileDrawer
          topic={profileDrawerTopic}
          isOpen={profileDrawerOpen}
          onClose={() => {
            setProfileDrawerStack((prev) => {
              if (prev.length > 1) return prev.slice(0, -1);
              setProfileDrawerOpen(false);
              return [];
            });
          }}
          myUserId={myUserId}
          onOpenUserProfile={openUserProfile}
          onMessageUser={async (uid) => {
            try {
              const dm = await getOrCreateDm(uid);
              await qc.invalidateQueries({ queryKey: ["chat/topics"] });
              setActiveTopicId(dm.id);
              setProfileDrawerOpen(false);
              setProfileDrawerStack([]);
            } catch (e) { console.error(e); }
          }}
          onJumpToMessage={(id, topicId) => { setProfileDrawerOpen(false); setProfileDrawerStack([]); setTimeout(() => jumpToMessage(id, topicId), 100); }}
          onReplyToMessage={(id) => {
            const m = messagesQ.data?.find((x) => x.id === id);
            if (m) {
              setReplyTo({ id: m.id, author_name: m.author_name, preview: (m.text || "").slice(0, 200), deleted: !!m.deleted_at });
              setProfileDrawerOpen(false);
            }
          }}
          onDeleteMessage={(id) => deleteMut.mutate(id)}
          onForwardMessage={(id) => {
            setForwardPicker({ messageIds: [id], sourceTopicId: activeTopic?.id });
          }}
        />
      )}
      {isMessengerRoute && (
        <MessengerRightPanel
          topicId={activeTopicId}
          isOpen={rightPanelOpen && !pinnedListOpen}
          onClose={() => setRightPanelOpen(false)}
          accent={mngAccent}
          onAccentChange={setMngAccent}
          bg={mngBg}
          onBgChange={setMngBg}
        />
      )}
      {isMessengerRoute && pinnedListOpen && (
        <PinnedSidePanel
          topicId={activeTopicId}
          isOpen={pinnedListOpen}
          onClose={() => setPinnedListOpen(false)}
          onJumpToMessage={(id) => jumpToMessage(id)}
        />
      )}
      {forwardPicker && (
        <ForwardPickerModal
          topics={topics}
          sourceMessageIds={forwardPicker.messageIds}
          currentTopicId={forwardPicker.sourceTopicId}
          preview={buildForward(forwardPicker.messageIds, []).info}
          onPickSingle={(targetId) => openForwardInTopic(targetId, forwardPicker.messageIds, [])}
          onClose={() => setForwardPicker(null)}
          onSent={(targetId) => {
            setActiveTopicId(targetId);
            setForwardPicker(null);
          }}
        />
      )}
      {iconPickerOpen && activeTopic && (
        <Suspense fallback={null}>
          <TopicIconPickerModal
            initialName={activeTopic.name}
            initialIcon={activeTopic.icon}
            theme={themeEffective}
            onCancel={() => setIconPickerOpen(false)}
            onSave={(name, icon) => {
              setIconPickerOpen(false);
              editTopicMut.mutate({ id: activeTopic.id, name, icon });
            }}
          />
        </Suspense>
      )}
      {membersModalOpen && activeTopic && (() => {
        const isSuper = !!user?.is_superuser;
        const isCreator = activeTopic.created_by_user_id === myUserId;
        // canManage — также true для модераторов; используем кэш members, если он есть.
        const members = qc.getQueryData<import("@/api/dashboard").ChatTopicMember[]>(
          ["chat/topic/members", activeTopic.id]
        );
        const myRole = members?.find(m => m.user_id === myUserId)?.role;
        const isMod = myRole === "admin";
        return (
          <TopicMembersModal
            topic={activeTopic}
            myUserId={myUserId}
            canManage={isSuper || isCreator || isMod}
            canAssignModerator={isSuper || isCreator}
            onClose={() => setMembersModalOpen(false)}
          />
        );
      })()}
      {/* Drive picker + Task creator перенесены в .chat-form-input-wrap (inline mode) */}
      {newChatOpen && (
        <NewChatModal
          myUserId={myUserId}
          onClose={() => setNewChatOpen(false)}
          onCreated={(topic) => {
            setNewChatOpen(false);
            setActiveTopicId(topic.id);
          }}
        />
      )}
      {/* Sticky date pill через portal — position:fixed escapes chat-shell grid */}
      {createPortal(
        <div
          ref={stickyPillRef}
          className="chat-sticky-date chat-sticky-date--portal"
          style={{ visibility: "hidden" }}
          aria-hidden="true"
        />,
        document.body,
      )}
      {burst && createPortal(
        <div
          key={burst.key}
          className={`chat-reaction-burst${burst.assetUrl ? " is-anim" : ""}`}
          aria-hidden="true"
        >
          {burst.assetUrl ? (
            <img
              className="chat-reaction-burst-anim"
              src={burst.assetUrl}
              alt={burst.emoji}
              decoding="async"
            />
          ) : (
            <span className="chat-reaction-burst-emoji">{burst.emoji}</span>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}


function ScheduledListModal({ items, onClose, onCancel, onSendNow, onEditTime }: {
  items: ScheduledMessage[];
  onClose: () => void;
  onCancel: (sid: number) => void;
  onSendNow: (sid: number) => void;
  onEditTime: (item: ScheduledMessage) => void;
}) {
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const fmt = (iso: string) => {
    try {
      // Backend хранит UTC без tz — при парсинге добавляем 'Z'
      const d = new Date(iso.endsWith("Z") ? iso : iso + "Z");
      if (Number.isNaN(d.getTime())) return iso;
      const pad = (n: number) => n.toString().padStart(2, "0");
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return iso; }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
        zIndex: 10002, display: "grid", placeItems: "center", padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 14, padding: 20,
          minWidth: 340, maxWidth: 560, width: "100%", maxHeight: "80vh",
          overflowY: "auto", boxShadow: "0 24px 48px rgba(0,0,0,0.24)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div style={{ fontSize: 17, fontWeight: 600, color: "var(--ink)" }}>
            Отложенные сообщения
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 20, color: "var(--mute)" }}
            aria-label="Закрыть"
          >×</button>
        </div>
        {items.length === 0 && (
          <div style={{ padding: 30, textAlign: "center", color: "var(--mute)" }}>Нет отложенных</div>
        )}
        {items.map((it) => (
          <div
            key={it.id}
            style={{
              padding: "12px 0",
              borderBottom: "1px solid var(--line, #eee)",
              display: "flex", flexDirection: "column", gap: 8,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              <span style={{ fontSize: 13, fontWeight: 500, color: "var(--ink-2, #393939)" }}>
                {fmt(it.scheduled_for)}
              </span>
              {it.attachment_ids.length > 0 && (
                <span style={{ fontSize: 12, color: "var(--mute)" }}>
                  · {it.attachment_ids.length} файл(ов)
                </span>
              )}
            </div>
            <div style={{ fontSize: 14, color: "var(--ink)", lineHeight: 1.4, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {it.text || <span style={{ color: "var(--mute)", fontStyle: "italic" }}>(без текста)</span>}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => onSendNow(it.id)}
                style={{ padding: "6px 12px", background: "var(--tg-accent, #42a5f5)", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12.5, fontFamily: "inherit" }}
              >
                Отправить сейчас
              </button>
              <button
                type="button"
                onClick={() => onEditTime(it)}
                style={{ padding: "6px 12px", background: "transparent", color: "var(--ink)", border: "1px solid var(--line)", borderRadius: 6, cursor: "pointer", fontSize: 12.5, fontFamily: "inherit" }}
              >
                Изменить время
              </button>
              <button
                type="button"
                onClick={() => onCancel(it.id)}
                style={{ padding: "6px 12px", background: "transparent", color: "#d92d20", border: "1px solid #d92d20", borderRadius: 6, cursor: "pointer", fontSize: 12.5, fontFamily: "inherit", marginLeft: "auto" }}
              >
                Отменить
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


export default ChatPage;
